import type { ChangeEvent, DragEvent, KeyboardEvent, ReactNode } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { LucideIcon } from "lucide-react";
import {
  BarChart3,
  Bell,
  CalendarDays,
  Clapperboard,
  Inbox,
  Lightbulb,
  Lock,
  Megaphone,
  Menu,
  MessageCircle,
  MessageSquare,
  Palette,
  PenSquare,
  PlaySquare,
  RotateCcw,
  Save,
  Server,
  Send,
  Sparkles,
  Target,
  TrendingUp,
  UploadCloud,
  UserPlus,
  X,
  Download,
  Layers,
} from "lucide-react";
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useDroppable,
  useDraggable,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import { restrictToWindowEdges } from "@dnd-kit/modifiers";
import { CSS } from "@dnd-kit/utilities";
import useSWR, { mutate as mutateGlobal, type KeyedMutator } from "swr";

import { CapabilityGate } from "@/components/CapabilityGate";
import { CapabilityLockedCard } from "@/components/CapabilityLockedCard";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Spinner } from "@/components/ui/Spinner";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Tooltip as ChartTooltip,
  Legend,
} from "chart.js";
import { Line, Bar, Pie } from "react-chartjs-2";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement, ChartTooltip, Legend);
import { useToast } from "@/components/ToastProvider";
import { NotificationsPanel, type UnifiedNotification } from "@/components/marketing/NotificationsPanel";
import { NotificationsView, NotificationsSkeleton } from "@/components/marketing/NotificationsView";
import { CrossSystemPanel } from "@/components/marketing/CrossSystemPanel";
import { MarketingActionsMenu } from "@/components/marketing/MarketingActionsMenu";
import { InsightsPanel } from "@/components/marketing/InsightsPanel";
import { CampaignHealthPanel } from "@/components/marketing/CampaignHealthPanel";
import { StreamingInsightsPanel } from "@/components/marketing/StreamingInsightsPanel";
import { IdeaFeed } from "@/components/marketing/IdeaFeed";
import { MetaAdsPanel } from "@/components/marketing/MetaAdsPanel";
import { BudgetAllocatorPanel } from "@/components/marketing/BudgetAllocatorPanel";
import { GeoFanGrowthPanel } from "@/components/marketing/GeoFanGrowthPanel";
import { CRMAudienceSync } from "@/components/marketing/CRMAudienceSync";
import { EngagementHub } from "@/components/marketing/EngagementHub";
import { CreatorPayoutPanel } from "@/components/marketing/CreatorPayoutPanel";
import { FanJourneyFunnel } from "@/components/marketing/FanJourneyFunnel";
import { InfluencerOutreachPanel } from "@/components/marketing/InfluencerOutreachPanel";
import { RetargetingPanel } from "@/components/marketing/RetargetingPanel";
import { TrendwatchPanel } from "@/components/marketing/TrendwatchPanel";
import { AutopilotSettings } from "@/components/marketing/AutopilotSettings";
import { MerchPromoPanel } from "@/components/marketing/MerchPromoPanel";
import { CrosspostPanel } from "@/components/marketing/CrosspostPanel";
import { ViralCandidatesPanel } from "@/components/marketing/ViralCandidatesPanel";
import { AudienceForecastPanel } from "@/components/marketing/AudienceForecastPanel";
import { CommentResponder } from "@/components/marketing/CommentResponder";
import { ContentScore } from "@/components/marketing/ContentScore";
import { EmptyState } from "@/components/marketing/EmptyState";
import { MarketingCommandPalette } from "@/components/marketing/MarketingCommandPalette";
import { AudienceSelector } from "@/components/marketing/AudienceSelector";
import { ABTestPanel } from "@/components/marketing/ABTestPanel";
import { BlockButton } from "@/components/marketing/BlockButton";
import { PostComposerDrawer } from "@/components/marketing/PostComposerDrawer";
import { LockedSection, UnlockedSection } from "@/components/locked/LockedSections";
import { TemplatePicker as MixboardTemplatePicker } from "@/components/mixboard/TemplatePicker";
import { fetcher } from "@/lib/fetcher";
import { api, post } from "@/lib/api";
import { useCapabilities } from "@/hooks/useCapabilities";
import { usePermissions, type MarketingPermissions } from "@/hooks/usePermissions";
import { useStubbedPaths } from "@/hooks/useStubbedPaths";
import { cn } from "@/lib/utils";
import { schedulePost, type SchedulePostPayload } from "@/lib/scheduler";
import { ShortFormGeneratorView } from "@/components/marketing/ShortFormGeneratorView";
import {
  CTA_PRESETS,
  NOTIFICATION_SOURCE_FILTERS,
  NOTIFICATION_SOURCE_LABELS,
  NOTIFICATION_TYPE_META,
  PLATFORM_PERMISSION_MAP,
  PLATFORMS,
  SHORTFORM_EFFECTS,
  SHORTFORM_FILTERS,
  SHORTFORM_FONTS,
  SHORTFORM_TEMPLATES,
  SEVERITY_BADGE_STYLES,
  TONE_PRESETS,
  platformLabels,
  toneLabels,
  type PlatformKey,
  type TonePreset,
} from "@/lib/marketingConstants";
import { formatDateTimeInput, formatRelativeTime, getSlotDateTime, type PostingWindow } from "@/lib/marketingDateUtils";
import type {
  ClipOverlayPayload,
  MarketingAsset,
  MarketingAssetFolder,
  MarketingBrandKit,
  MarketingCampaign,
  MarketingKpis,
  MarketingNudge,
  MarketingTask,
  MarketingTaskStatus,
  InboxItem,
  TrendItem,
  NotificationItem,
  NotificationSource,
  NotificationSeverity,
  NotificationType,
  GrowthGoal,
  GrowthRecommendation,
  AutomationWorkflow,
  CreatorProfile,
  CollabHistoryItem,
  AdAccount,
  AdCampaign,
  AdPerformanceSummary,
  AudienceSegment,
  AudiencePersona,
  Experiment,
  PeerArtist,
  CompetitivePerformance,
  CompetitiveStrategy,
  Brand,
  TourConversion,
  MarketingInsightsPayload,
  CampaignHealthPayload,
  StreamingInsightsPayload,
  DailyIdeaFeed,
  MetaAdsInsightsPayload,
  AudienceSelection,
} from "@/types/marketing";

export type MarketingView =
  | "today"
  | "week"
  | "calendar"
  | "timeline"
  | "automation"
  | "shortform"
  | "templates"
  | "replay"
  | "collabs"
  | "ads"
  | "shelf"
  | "engagement"
  | "analytics"
  | "brandkit"
  | "inbox"
  | "trends"
  | "tour"
  | "audience"
  | "consistency"
  | "experiments"
  | "competitive"
  | "tour-conversion"
  | "growth"
  | "notifications"
  | "clips"
  | "assets"
  | "captions"
  | "campaigns"
  | "permissions";

type DateContext = "today" | "week" | "month";

type PlatformKey = "instagram" | "tiktok" | "facebook" | "youtube_shorts";

type ComposerAsset = {
  id: string;
  type: "clip" | "image" | "template";
  title?: string;
  thumbnailUrl?: string;
  url?: string;
  tags?: string[];
};
type MarketingOverviewWidgetsProps = {
  kpis: MarketingKpis;
  loading: boolean;
  onNavigate: (view: MarketingView) => void;
};

const OVERVIEW_METRICS: Array<{
  key: keyof MarketingKpis;
  label: string;
  description: string;
  icon: LucideIcon;
  targetView: MarketingView;
}> = [
  {
    key: "upcomingPosts",
    label: "Upcoming posts",
    description: "Scheduled content in the next week.",
    icon: CalendarDays,
    targetView: "calendar",
  },
  {
    key: "engagement24h",
    label: "Engagement (24h)",
    description: "People interacting with your posts.",
    icon: TrendingUp,
    targetView: "analytics",
  },
  {
    key: "newFollowers24h",
    label: "New followers",
    description: "Fans gained in the last day.",
    icon: UserPlus,
    targetView: "engagement",
  },
  {
    key: "pendingClips",
    label: "Pending clips",
    description: "Clips awaiting approval or editing.",
    icon: Clapperboard,
    targetView: "clips",
  },
  {
    key: "activeCampaigns",
    label: "Active campaigns",
    description: "Campaigns currently live.",
    icon: Sparkles,
    targetView: "campaigns",
  },
];

const MarketingOverviewWidgets = ({ kpis, loading, onNavigate }: MarketingOverviewWidgetsProps) => {
  const formatValue = (value: number) => {
    if (!Number.isFinite(value)) return "—";
    if (Number.isInteger(value)) {
      return value.toLocaleString();
    }
    return value.toFixed(1);
  };

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
      {OVERVIEW_METRICS.map((metric) => {
        const rawValue = kpis[metric.key];
        const displayValue = loading ? "…" : formatValue(rawValue);
        const handleActivate = () => onNavigate(metric.targetView);
        const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            handleActivate();
          }
        };
        return (
          <Card
            key={metric.key}
            role="button"
            tabIndex={0}
            onClick={handleActivate}
            onKeyDown={handleKeyDown}
            className="group relative cursor-pointer overflow-hidden rounded-2xl border border-border/60 bg-background/40 p-4 text-left transition hover:border-border/40 hover:bg-background/50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
          >
            <CardHeader className="px-0 pb-0 pt-0">
              <div className="flex items-start justify-between gap-2">
                <CardTitle className="text-sm text-foreground/70">{metric.label}</CardTitle>
                <metric.icon className="h-5 w-5 text-muted-foreground group-hover:text-foreground" />
              </div>
              <CardDescription className="text-xs text-muted-foreground">{metric.description}</CardDescription>
            </CardHeader>
            <CardContent className="px-0 pb-0 pt-4">
              <p className="text-3xl font-semibold text-foreground">{displayValue}</p>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">View {metric.label.toLowerCase()}</p>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

type ContentShelfItem = {
  id: string;
  type: "high" | "evergreen" | "idea" | "clip" | "quote";
  title: string;
  description?: string;
  thumbnailUrl?: string;
  metrics?: {
    impressions?: number;
    engagementRate?: number;
    saves?: number;
  };
  lastUsedAt?: string;
  platforms?: PlatformKey[];
  insight?: string;
  asset?: ComposerAsset;
  caption?: string;
};

type CreateAdPayload = {
  objective: AdCampaign["objective"];
  budget: number;
  assetId?: string;
  postId?: string;
  platform: AdCampaign["platform"];
  audience: {
    radiusMiles?: number;
    venue?: string;
    lookalike?: boolean;
    interests: string[];
  };
};

type TourDate = {
  id: string;
  city: string;
  venue: string;
  date: string;
  status?: "upcoming" | "completed";
  ticketsSold?: number;
  capacity?: number;
};

type TourCityInsights = {
  venueSummary: string;
  previousPerformance: string;
  trendingFormats: string[];
  audienceBehaviors: string[];
  localHashtags: string[];
  promoCadence: Array<{ label: string; description: string }>;
  bestTimes: string[];
};

type ConsistencyReport = {
  visualScore: number;
  toneScore: number;
  platformAlignment: number;
  issues: string[];
  suggestions: string[];
};

type ExperimentDraftVariant = {
  caption?: string;
  assetId?: string;
  scheduledFor: string;
};

type CompetitiveState = {
  trackedIds: string[];
};

type AnalyticsTimeseriesPoint = {
  date: string;
  engagement: number;
  followers: number;
};

type AudienceAnalytics = {
  demographics: {
    genders: Record<string, number>;
    ages: Record<string, number>;
    regions: Record<string, number>;
  };
  bestTimes: string[];
};

type PlatformAnalytics = {
  distribution: Record<string, number>;
  comparison: Array<{ platform: string; engagement: number; reach: number }>;
};

type PerformanceAnalytics = {
  scatter: Array<{ title: string; reach: number; engagement: number }>;
  conversions: Array<{ channel: string; clicks: number; ctr: number }>;
};

export interface MarketingAutomationRules {
  postingCadence: {
    mode: "preset" | "custom";
    frequency?: number;
    customFrequency?: number;
    weekendPosting: boolean;
  };
  postingWindows: PostingWindow[];
  defaultPlatforms: PlatformKey[];
  crossposting: Record<"igToFb" | "tiktokToReels" | "shortsToTiktok", boolean>;
  eventCadence: Array<{
    id: string;
    dayOffset: number;
    platforms: PlatformKey[];
    tone: TonePreset;
  }>;
  autoEditor: {
    clipsPerBatch: number;
    autoCaptions: boolean;
    autoThumbnails: boolean;
  };
  tonePresets: Record<PlatformKey, TonePreset>;
}

const NAV_ITEMS: { id: MarketingView; label: string; badgeCount?: number; capability?: string }[] = [
  { id: "today", label: "Today" },
  { id: "week", label: "This Week" },
  { id: "calendar", label: "Calendar" },
  { id: "timeline", label: "Timeline", capability: "marketing_timeline" },
  { id: "automation", label: "Automation", capability: "marketing_automation" },
  { id: "shortform", label: "Short-Form Builder" },
  { id: "templates", label: "Template Builder" },
  { id: "replay", label: "Replay Engine" },
  { id: "collabs", label: "Collab Manager" },
  { id: "ads", label: "Ads Manager" },
  { id: "tour", label: "Tour Promo Engine" },
  { id: "audience", label: "Audience Personas" },
  { id: "consistency", label: "Consistency Report" },
  { id: "experiments", label: "Experiments" },
  { id: "competitive", label: "Competitive Intel" },
  { id: "tour-conversion", label: "Tour Conversions" },
  { id: "shelf", label: "Content Shelf" },
  { id: "engagement", label: "Engagement" },
  { id: "analytics", label: "Analytics" },
  { id: "inbox", label: "Content Inbox" },
  { id: "trends", label: "Trend Miner" },
  { id: "growth", label: "Growth Planner" },
  { id: "notifications", label: "Notifications", badgeCount: 12 },
  { id: "clips", label: "Clips" },
  { id: "assets", label: "Assets" },
  { id: "brandkit", label: "Brand Kit" },
  { id: "captions", label: "Captions" },
  { id: "campaigns", label: "Campaigns" },
  { id: "permissions", label: "Permissions" },
];

const STUB_SECTION_LABELS: Array<{ prefix: string; label: string }> = [
  { prefix: "/marketing/overview", label: "Overview KPIs" },
  { prefix: "/marketing/automations", label: "Automation workflows" },
  { prefix: "/marketing/brand", label: "Brand Kit" },
  { prefix: "/marketing/inbox", label: "Content Inbox" },
  { prefix: "/marketing/shelf", label: "Content Shelf" },
  { prefix: "/marketing/collabs", label: "Collab Manager" },
  { prefix: "/marketing/ads", label: "Ads Manager" },
  { prefix: "/marketing/audience", label: "Audience insights" },
  { prefix: "/marketing/tour", label: "Tour promo" },
  { prefix: "/marketing/experiments", label: "Experiments" },
  { prefix: "/marketing/competitive", label: "Competitive insights" },
  { prefix: "/marketing/analytics", label: "Analytics" },
  { prefix: "/marketing/trends", label: "Trend Miner" },
  { prefix: "/marketing/growth", label: "Growth Planner" },
  { prefix: "/marketing/replay", label: "Replay Engine" },
  { prefix: "/marketing/engagement", label: "Engagement Hub" },
  { prefix: "/marketing/campaigns/health", label: "Campaign Health" },
  { prefix: "/marketing/ideas", label: "Idea Feed" },
  { prefix: "/marketing/assets", label: "Asset Library" },
  { prefix: "/marketing/posts", label: "Composer" },
  { prefix: "/marketing/commands", label: "Command Palette" },
  { prefix: "/marketing/consistency", label: "Consistency Analyzer" },
];

const DATE_CONTEXT_OPTIONS: { value: DateContext; label: string }[] = [
  { value: "today", label: "Today" },
  { value: "week", label: "This Week" },
  { value: "month", label: "This Month" },
];

type ComposerState = {
  open: boolean;
  initialTask?: MarketingTask | null;
  initialAsset?: ComposerAsset | null;
  initialDatetime?: string | null;
};

type EngagementItem = {
  id: string;
  platform: "instagram" | "tiktok" | "facebook" | "youtube";
  type: "comment" | "dm" | "mention" | "reply";
  username: string;
  avatarUrl?: string;
  message: string;
  createdAt: string;
  postPreviewUrl?: string;
  postId?: string;
};

type ReplaySourcePost = {
  id: string;
  title: string;
  platform: string;
  postedAt: string;
  thumbnailUrl?: string;
  engagementScore: number;
  reach?: number;
  saves?: number;
  comments?: number;
  caption?: string;
};

type ReplayGeneration = {
  previewUrl?: string;
  caption?: string;
  recommendedTime?: string;
  templateId?: string;
  tone?: string;
  cta?: string;
};

type EngagementSignal = {
  id: string;
  message: string;
  createdAt: string;
  severity?: "low" | "medium" | "high";
  metric?: string;
  change?: string;
  recommendedAction?: string;
};

type EngagementTab = "all" | "comments" | "dms" | "mentions" | "signals";

const POSTING_WINDOWS: { value: PostingWindow; label: string; time: string }[] = [
  { value: "morning", label: "Morning", time: "8a – 12p" },
  { value: "afternoon", label: "Afternoon", time: "12p – 4p" },
  { value: "evening", label: "Evening", time: "4p – 8p" },
  { value: "late", label: "Late", time: "8p – 11p" },
];

const CROSSPOSTING_RULES = [
  {
    key: "igToFb" as const,
    label: "Auto-crosspost IG → FB",
    description: "Helpful for mirroring hero posts to Facebook pages.",
  },
  {
    key: "tiktokToReels" as const,
    label: "Auto-crosspost TikTok → Reels",
    description: "Auto-reshares viral clips to Instagram Reels.",
  },
  {
    key: "shortsToTiktok" as const,
    label: "Auto-post Shorts → TikTok",
    description: "Syndicate Shorts drops to TikTok for momentum.",
  },
];

const DEFAULT_AUTOMATION_RULES: MarketingAutomationRules = {
  postingCadence: {
    mode: "preset",
    frequency: 2,
    weekendPosting: true,
  },
  postingWindows: ["morning", "afternoon", "evening"],
  defaultPlatforms: ["instagram", "tiktok"],
  crossposting: {
    igToFb: true,
    tiktokToReels: true,
    shortsToTiktok: false,
  },
  eventCadence: [
    { id: "t-21", dayOffset: 21, platforms: ["instagram"], tone: "storytelling" },
    { id: "t-14", dayOffset: 14, platforms: ["tiktok"], tone: "hype" },
    { id: "t-7", dayOffset: 7, platforms: ["facebook"], tone: "direct_response" },
    { id: "t-1", dayOffset: 1, platforms: ["youtube_shorts"], tone: "behind_the_scenes" },
  ],
  autoEditor: {
    clipsPerBatch: 3,
    autoCaptions: true,
    autoThumbnails: true,
  },
  tonePresets: {
    instagram: "storytelling",
    tiktok: "hype",
    facebook: "direct_response",
    youtube_shorts: "behind_the_scenes",
  },
};

const cloneRules = (rules: MarketingAutomationRules): MarketingAutomationRules =>
  JSON.parse(JSON.stringify(rules));

const DEFAULT_PERMISSIONS: MarketingPermissions = {
  canSchedule: false,
  canPublishNow: false,
  canEditAssets: false,
  canManageCampaigns: false,
  canUseAutomation: false,
  platform: {
    instagram: false,
    tiktok: false,
    facebook: false,
    youtube: false,
  },
};

const DEFAULT_BRAND_KIT: MarketingBrandKit = {
  logos: [],
  colors: [],
  typography: {
    headingFont: "Space Grotesk",
    headingWeight: "600",
    bodyFont: "Instrument Sans",
    bodyWeight: "400",
  },
};

const DEFAULT_ASSET_FOLDERS: MarketingAssetFolder[] = [
  { id: "logos", name: "Logos" },
  { id: "posters", name: "Posters" },
  { id: "templates", name: "Templates" },
  { id: "tour_graphics", name: "Tour Graphics" },
  { id: "clip_thumbnails", name: "Clips Thumbnails" },
  { id: "fan_photos", name: "Fan Photos" },
];

type BrandReusableElement = {
  id: string;
  name: string;
  usage: string;
  lastUsed?: string;
};

const DEFAULT_REUSABLE_ELEMENTS: BrandReusableElement[] = [
  { id: "cta-lockup", name: "CTA Lockup", usage: "Tickets + drops", lastUsed: "2024-02-28T00:00:00Z" },
  { id: "lower-third", name: "Lower Third Overlay", usage: "Livestreams", lastUsed: "2024-02-24T00:00:00Z" },
  { id: "tour-bug", name: "Tour Bug", usage: "Geo-targeted tour promos", lastUsed: "2024-02-20T00:00:00Z" },
];

interface MarketingConflict {
  taskId: string;
  platform: string;
  conflictType: "double_slot" | "rate_limit" | "window_violation";
  suggestedFixes: { datetime: string }[];
}

export default function MarketingPage() {
  const [activeView, setActiveView] = useState<MarketingView>("today");
  const [dateContext, setDateContext] = useState<DateContext>("today");
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [notificationsDrawerOpen, setNotificationsDrawerOpen] = useState(false);
  const [composerState, setComposerState] = useState<ComposerState>({ open: false });
  const [dmDrawerItem, setDmDrawerItem] = useState<EngagementItem | null>(null);
  const [replayDrawerState, setReplayDrawerState] = useState<{ open: boolean; source?: ReplaySourcePost | null }>({
    open: false,
    source: null,
  });
  const [replyAssistantItem, setReplyAssistantItem] = useState<EngagementItem | null>(null);
  const [lifecycleTask, setLifecycleTask] = useState<MarketingTask | null>(null);
  const [adDrawerState, setAdDrawerState] = useState<{ open: boolean; seedTask?: MarketingTask | null }>({ open: false });
  const [selectedTourDateId, setSelectedTourDateId] = useState<string | null>(null);
  const [selectedTourConversionId, setSelectedTourConversionId] = useState<string | null>(null);
  const [consistencyReport, setConsistencyReport] = useState<ConsistencyReport | null>(null);
  const [consistencyLoading, setConsistencyLoading] = useState(false);
  const [experimentDrawerOpen, setExperimentDrawerOpen] = useState(false);
  const [selectedExperimentId, setSelectedExperimentId] = useState<string | null>(null);
  const [selectedBrandId, setSelectedBrandId] = useState<string | null>(null);
  const [competitiveState, setCompetitiveState] = useState<CompetitiveState>({ trackedIds: [] });
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const { capabilities, openUpgradeModal } = useCapabilities();
  const lockedItems: ReactNode[] = [];
  const lockedRegistry = new Set<string>();
  const registerLockedCapability = (capability: string) => {
    if (lockedRegistry.has(capability)) return;
    lockedRegistry.add(capability);
    lockedItems.push(<CapabilityLockedCard key={`locked-${capability}`} capability={capability} />);
  };
  const hasMarketing = capabilities ?? {};
  if (!hasMarketing.marketing_insights) {
    registerLockedCapability("marketing_insights");
  }
  if (!hasMarketing.marketing_automation) {
    registerLockedCapability("marketing_automation");
  }
  if (!hasMarketing.marketing_timeline) {
    registerLockedCapability("marketing_timeline");
  }
  if (!hasMarketing.marketing_notifications) {
    registerLockedCapability("marketing_notifications");
  }
  if (!hasMarketing.marketing_access) {
    registerLockedCapability("marketing_access");
  }
  if (!hasMarketing.marketing_ads) {
    registerLockedCapability("marketing_ads");
  }
  const stubbedPaths = useStubbedPaths();
  const { permissions: fetchedPermissions } = usePermissions();
  const { push } = useToast();
  const navigate = useNavigate();
  const requestUpgrade = useCallback(
    (message: string) => {
      openUpgradeModal("marketing_permissions", message);
    },
    [openUpgradeModal],
  );

  const marketingPermissions = fetchedPermissions ?? DEFAULT_PERMISSIONS;
  const hasMarketingAccess = Boolean(capabilities?.marketing_access);
  const tasksKey = hasMarketingAccess ? "/marketing/tasks" : null;
  const {
    data: tasks,
    error: tasksError,
    isLoading: tasksLoading,
    mutate: refreshTasks,
  } = useSWR<MarketingTask[]>(tasksKey, fetcher);

  const notificationsFeedEnabled = hasMarketingAccess && Boolean(capabilities?.marketing_notifications);
  const {
    data: unifiedNotifications,
    error: notificationsError,
    isLoading: notificationsLoading,
    mutate: refreshNotifications,
  } = useSWR<UnifiedNotification[]>(
    notificationsFeedEnabled ? "/notifications/feed?limit=50&unread_only=false" : null,
    notificationsFetcher,
    { refreshInterval: 30000 },
  );

  const { data: overviewKpis, isLoading: kpisLoading } = useSWR<MarketingKpis>(
    hasMarketingAccess ? "/marketing/overview/kpis" : null,
    fetcher,
  );

  const {
    data: nudges,
    mutate: mutateNudges,
  } = useSWR<MarketingNudge[]>(hasMarketingAccess ? "/marketing/analytics/nudges" : null, fetcher);

  const { data: automationRules } = useSWR<MarketingAutomationRules>(
    hasMarketingAccess ? "/marketing/automation/rules" : null,
    fetcher,
  );
  const {
    data: automationWorkflows,
    isLoading: automationWorkflowsLoading,
    mutate: mutateAutomationWorkflows,
  } = useSWR<AutomationWorkflow[]>(
    hasMarketingAccess && capabilities?.marketing_automation ? "/marketing/automations" : null,
    fetcher,
  );
  const { data: brandKit, mutate: mutateBrandKit } = useSWR<MarketingBrandKit | undefined>(
    hasMarketingAccess ? "/marketing/brand/kit" : null,
    fetcher,
  );
  const { data: inboxItems, mutate: mutateInbox } = useSWR<InboxItem[]>(
    hasMarketingAccess ? "/marketing/inbox/items" : null,
    fetcher,
  );
  const { data: shelfItems, mutate: mutateShelf } = useSWR<ContentShelfItem[]>(
    hasMarketingAccess ? "/marketing/shelf" : null,
    fetcher,
  );
  const { data: collabCreators, mutate: mutateCollabCreators } = useSWR<CreatorProfile[]>(
    hasMarketingAccess ? "/marketing/collabs/potential" : null,
    fetcher,
  );
  const { data: collabHistory, mutate: mutateCollabHistory } = useSWR<CollabHistoryItem[]>(
    hasMarketingAccess ? "/marketing/collabs/history" : null,
    fetcher,
  );
  const { data: adAccounts } = useSWR<AdAccount[]>(
    hasMarketingAccess ? "/marketing/ads/accounts" : null,
    fetcher,
  );
  const { data: adCampaigns, mutate: mutateAdCampaigns } = useSWR<AdCampaign[]>(
    hasMarketingAccess ? "/marketing/ads/campaigns" : null,
    fetcher,
  );
  const { data: adPerformance, mutate: mutateAdPerformance } = useSWR<AdPerformanceSummary>(
    hasMarketingAccess ? "/marketing/ads/performance" : null,
    fetcher,
  );
  const { data: audienceSegments, mutate: mutateAudienceSegments } = useSWR<AudienceSegment[]>(
    hasMarketingAccess ? "/marketing/audience/segments" : null,
    fetcher,
  );
  const { data: audiencePersonas, mutate: mutateAudiencePersonas } = useSWR<AudiencePersona[]>(
    hasMarketingAccess ? "/marketing/audience/personas" : null,
    fetcher,
  );
  const { data: tourDates } = useSWR<TourDate[]>(hasMarketingAccess ? "/marketing/tour/dates" : null, fetcher);
  const { data: tourInsights, mutate: mutateTourInsights } = useSWR<TourCityInsights>(
    selectedTourDateId ? `/marketing/tour/${selectedTourDateId}/city-insights` : null,
    fetcher,
  );
  const { data: experiments, mutate: mutateExperiments } = useSWR<Experiment[]>(
    hasMarketingAccess ? "/marketing/experiments" : null,
    fetcher,
  );
  const { data: experimentDetail, mutate: mutateExperimentDetail } = useSWR<Experiment>(
    selectedExperimentId ? `/marketing/experiments/${selectedExperimentId}` : null,
    fetcher,
  );
  const { data: competitiveArtists, mutate: mutateCompetitiveArtists } = useSWR<PeerArtist[]>(
    hasMarketingAccess ? "/marketing/competitive/artists" : null,
    fetcher,
  );
  const { data: competitivePerformance } = useSWR<CompetitivePerformance>(
    hasMarketingAccess ? "/marketing/competitive/performance" : null,
    fetcher,
  );
  const { data: competitiveRecentPosts } = useSWR<Record<string, string[]>>(
    hasMarketingAccess ? "/marketing/competitive/recent-posts" : null,
    fetcher,
  );
  const { data: competitiveStrategy } = useSWR<CompetitiveStrategy[]>(
    hasMarketingAccess ? "/marketing/competitive/strategy" : null,
    fetcher,
  );
  const { data: tourConversions } = useSWR<TourConversion[]>(
    hasMarketingAccess ? "/marketing/tour/conversions" : null,
    fetcher,
  );
  const { data: tourConversionDetail } = useSWR<TourConversion>(
    selectedTourConversionId ? `/marketing/tour/conversions/${selectedTourConversionId}` : null,
    fetcher,
  );
  const { data: analyticsTimeseries } = useSWR<AnalyticsTimeseriesPoint[]>(
    hasMarketingAccess ? "/marketing/analytics/timeseries" : null,
    fetcher,
  );
  const { data: analyticsAudience } = useSWR<AudienceAnalytics>(
    hasMarketingAccess ? "/marketing/analytics/audience" : null,
    fetcher,
  );
  const { data: analyticsPlatforms } = useSWR<PlatformAnalytics>(
    hasMarketingAccess ? "/marketing/analytics/platforms" : null,
    fetcher,
  );
  const { data: analyticsCohorts } = useSWR<Array<{ cohort: string; posts: number; avgEngagement: number; conversion: number }>>(
    hasMarketingAccess ? "/marketing/analytics/cohorts" : null,
    fetcher,
  );
  const { data: analyticsPerformance } = useSWR<PerformanceAnalytics>(
    hasMarketingAccess ? "/marketing/analytics/performance" : null,
    fetcher,
  );
  const { data: trendSounds } = useSWR<TrendItem[]>(hasMarketingAccess ? "/marketing/trends/sounds" : null, fetcher);
  const { data: trendFormats } = useSWR<TrendItem[]>(hasMarketingAccess ? "/marketing/trends/formats" : null, fetcher);
  const { data: trendMemes } = useSWR<TrendItem[]>(hasMarketingAccess ? "/marketing/trends/memes" : null, fetcher);
  const { data: trendGenre } = useSWR<TrendItem[]>(hasMarketingAccess ? "/marketing/trends/genre" : null, fetcher);
  const { data: growthGoals, mutate: mutateGrowthGoals } = useSWR<GrowthGoal[]>(
    hasMarketingAccess ? "/marketing/growth/goals" : null,
    fetcher,
  );
  const { data: growthRecommendations, mutate: mutateGrowthRecommendations } = useSWR<GrowthRecommendation[]>(
    hasMarketingAccess ? "/marketing/growth/recommendations" : null,
    fetcher,
  );
  const { data: replaySources, isLoading: replaySourcesLoading, mutate: mutateReplaySources } = useSWR<ReplaySourcePost[]>(
    hasMarketingAccess ? "/marketing/replay/source-posts" : null,
    fetcher,
  );
  const { data: engagementFeed } = useSWR<EngagementItem[]>(hasMarketingAccess ? "/marketing/engagement/feed" : null, fetcher);
  const { data: engagementDms } = useSWR<EngagementItem[]>(hasMarketingAccess ? "/marketing/engagement/dms" : null, fetcher);
  const { data: engagementComments } = useSWR<EngagementItem[]>(hasMarketingAccess ? "/marketing/engagement/comments" : null, fetcher);
  const { data: engagementSignals } = useSWR<EngagementSignal[]>(
    hasMarketingAccess ? "/marketing/engagement/signals" : null,
    fetcher,
  );
  const { data: marketingInsights, isLoading: insightsLoading } = useSWR<MarketingInsightsPayload>(
    hasMarketingAccess ? "/marketing/analytics/insights" : null,
    fetcher,
  );
  const { data: campaignHealth, isLoading: campaignHealthLoading } = useSWR<CampaignHealthPayload>(
    hasMarketingAccess ? "/marketing/campaigns/health" : null,
    fetcher,
  );
  const { data: streamingAnalytics, isLoading: streamingLoading } = useSWR<StreamingInsightsPayload>(
    hasMarketingAccess ? "/marketing/analytics/streams" : null,
    fetcher,
  );
  const { data: dailyIdeas, isLoading: ideasLoading } = useSWR<DailyIdeaFeed>(
    hasMarketingAccess ? "/marketing/ideas/daily" : null,
    fetcher,
  );
  const { data: metaAdsInsights, isLoading: metaAdsLoading, mutate: mutateMetaAdsInsights } = useSWR<MetaAdsInsightsPayload>(
    hasMarketingAccess ? "/marketing/ads/meta/insights" : null,
    fetcher,
  );
  const { data: budgetRecommendations, isLoading: budgetLoading } = useSWR<Record<string, number>>(
    hasMarketingAccess ? "/marketing/ads/budget/recommendations" : null,
    fetcher,
  );
  const marketingStubbedSections = useMemo(() => {
    const labels = new Set(
      stubbedPaths
        .filter((path) => path.startsWith("/marketing/"))
        .map((path) => {
          const mapping = STUB_SECTION_LABELS.find((entry) => path.startsWith(entry.prefix));
          return mapping?.label ?? path.replace("/marketing/", "marketing/");
        }),
    );
    return Array.from(labels).sort();
  }, [stubbedPaths]);
  const marketingLoadIssue = hasMarketingAccess && (tasksError || notificationsError);

  const safeTasks = tasks ?? [];
  const safeUnifiedNotifications = unifiedNotifications ?? [];
  const safeNotifications = useMemo(() => safeUnifiedNotifications.map((item) => normalizeNotification(item)), [safeUnifiedNotifications]);
  const notificationsCount = notificationsFeedEnabled
    ? safeUnifiedNotifications.filter((item) => !item.read).length
    : safeNotifications.length;
  const safeNudges = nudges ?? [];
  const safeKpis: MarketingKpis = overviewKpis ?? {
    upcomingPosts: 0,
    engagement24h: 0,
    newFollowers24h: 0,
    pendingClips: 0,
    activeCampaigns: 0,
  };
  const safeAutomationWorkflows = automationWorkflows ?? [];
  const safeEngagement = {
    feed: engagementFeed ?? [],
    dms: engagementDms ?? [],
    comments: engagementComments ?? [],
    signals: engagementSignals ?? [],
  };
  const safeReplaySources = replaySources ?? [];
  const safeBrandKit = brandKit ?? DEFAULT_BRAND_KIT;
  const safeTimeseries: AnalyticsTimeseriesPoint[] = analyticsTimeseries ?? [];
  const safeStreamingInsights = streamingAnalytics ?? null;
  const streamingInsightsLoading = streamingLoading && !streamingAnalytics;
  const safeIdeas = dailyIdeas ?? null;
  const ideasLoadingState = ideasLoading && !dailyIdeas;
  const safeMetaAdsInsights = metaAdsInsights ?? null;
  const metaAdsLoadingState = metaAdsLoading && !metaAdsInsights;
  const safeBudgetRecommendations = budgetRecommendations ?? null;
  const safeAutomationRules = automationRules ?? DEFAULT_AUTOMATION_RULES;
  const defaultMetaPlatforms = automationRules?.defaultPlatforms?.length
    ? automationRules.defaultPlatforms
    : ["instagram"];
  const metaBudgetLookup = useMemo(() => {
    const map: Record<string, number> = {};
    (safeMetaAdsInsights?.insights ?? []).forEach((insight) => {
      map[insight.post_id] = insight.recommended_budget;
    });
    return map;
  }, [safeMetaAdsInsights]);
  const safeCampaignHealth = campaignHealth ?? null;
  const campaignHealthLoadingState = campaignHealthLoading && !campaignHealth;
  const safeAudience: AudienceAnalytics =
    analyticsAudience ?? {
      demographics: { genders: {}, ages: {}, regions: {} },
      bestTimes: [],
    };
  const safePlatforms: PlatformAnalytics =
    analyticsPlatforms ?? {
      distribution: {},
      comparison: [],
    };
  const safeCohorts: Array<{ cohort: string; posts: number; avgEngagement: number; conversion: number }> = analyticsCohorts ?? [];
  const safePerformance: PerformanceAnalytics =
    analyticsPerformance ?? {
      scatter: [],
      conversions: [],
    };
  const safeInboxItems = inboxItems ?? [];
  const safeShelfItems = shelfItems ?? [];
  const safeTrends = {
    sounds: trendSounds ?? [],
    formats: trendFormats ?? [],
    memes: trendMemes ?? [],
    genre: trendGenre ?? [],
  };
  const safeGrowthGoals = growthGoals ?? [];
  const safeGrowthRecommendations = growthRecommendations ?? [];
  const safeCollabCreators = collabCreators ?? [];
  const safeCollabHistory = collabHistory ?? [];
  const safeAdAccounts = adAccounts ?? [];
  const safeAdCampaigns = adCampaigns ?? [];
  const safeAdPerformance: AdPerformanceSummary =
    adPerformance ?? { roasSeries: [], cpm: 0, cpc: 0, ctr: 0, topPosts: [] };
  const safeAudienceSegments = audienceSegments ?? [];
  const safeAudiencePersonas = audiencePersonas ?? [];
  const safeTourDates = tourDates ?? [];
  const safeExperiments = experiments ?? [];
  const safeCompetitiveArtists = competitiveArtists ?? [];
  const safeCompetitivePerformance =
    competitivePerformance ?? { dates: [], growthSeries: {}, engagementSeries: {}, frequencySeries: {} };
  const safeCompetitivePosts = competitiveRecentPosts ?? {};
  const safeCompetitiveStrategy = competitiveStrategy ?? [];
  const safeTourConversions = tourConversions ?? [];
  const orchestrationContext = useMemo(
    () => ({ brandId: selectedBrandId, view: activeView, dateContext }),
    [selectedBrandId, activeView, dateContext],
  );
  const executorCommands = useMemo(
    () => [
      { id: "build_week_content", label: "Build a week of content", hint: "Scheduler + Auto-Editor" },
      { id: "generate_promo_pack", label: "Generate promo pack", hint: "Mixboard + Publicist" },
      { id: "prepare_tour_announcement", label: "Prepare tour announcement", hint: "HITL" },
      { id: "boost_underperforming_posts", label: "Boost all underperforming posts", hint: "Ads" },
      { id: "generate_tomorrow_playlist", label: "Generate tomorrow's content playlist", hint: "Scheduler" },
    ],
    [],
  );

  const triggerOrchestrationAction = useCallback(
    async (actionId: string) => {
      try {
        await post("/orchestration/trigger", { action: actionId, context: orchestrationContext });
        push({ title: "Action queued", message: "Automation request sent." });
      } catch (error) {
        push({
          title: "Unable to trigger",
          message: error instanceof Error ? error.message : "Try again soon.",
          variant: "destructive",
        });
      }
    },
    [orchestrationContext, push],
  );

  const executeMarketingCommand = useCallback(
    async (commandId: string, label: string) => {
      try {
        const response = await post("/marketing/commands/execute", { command: commandId, context: orchestrationContext });
        const status = response?.result?.status ?? "queued";
        push({ title: label, message: `Command ${status}.` });
      } catch (error) {
        push({
          title: `Unable to run ${label}`,
          message: error instanceof Error ? error.message : "Try again later.",
          variant: "destructive",
        });
      }
    },
    [orchestrationContext, push],
  );

  const handleMetaBoost = useCallback(
    async (postId: string, budgetAmount: number) => {
      try {
        await post("/marketing/ads/meta/boost", { post_id: postId, budget: budgetAmount });
        push({ title: "Boost queued", message: `Boosting ${postId} with $${budgetAmount}.` });
        await mutateMetaAdsInsights();
      } catch (error) {
        push({
          title: "Unable to boost",
          message: error instanceof Error ? error.message : "Try again later.",
          variant: "destructive",
        });
      }
    },
    [mutateMetaAdsInsights, push],
  );

  const handleMetaCreateDarkPost = useCallback(
    async ({ caption: adCaption, mediaUrl, budget: adBudget }: { caption: string; mediaUrl?: string | null; budget?: number }) => {
      try {
        await post("/marketing/ads/meta/create", {
          caption: adCaption,
          media_url: mediaUrl,
          targeting: { platforms: defaultMetaPlatforms },
          budget: adBudget ?? 50,
        });
        push({ title: "Dark post queued", message: "Meta ads manager will finalize shortly." });
        await mutateMetaAdsInsights();
      } catch (error) {
        push({
          title: "Unable to create dark post",
          message: error instanceof Error ? error.message : "Try again later.",
          variant: "destructive",
        });
      }
    },
    [mutateMetaAdsInsights, push, defaultMetaPlatforms],
  );

  const handleEngagementBoost = useCallback(
    (item: EngagementItem) => {
      if (!item.postId) return;
      const budgetAmount = metaBudgetLookup[item.postId] ?? 50;
      void handleMetaBoost(item.postId, budgetAmount);
    },
    [metaBudgetLookup, handleMetaBoost],
  );

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setCommandPaletteOpen((prev) => !prev);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  const openComposerDrawer = (options?: Partial<ComposerState>) => {
    setComposerState({
      open: true,
      initialTask: options?.initialTask ?? null,
      initialAsset: options?.initialAsset ?? null,
      initialDatetime: options?.initialDatetime ?? null,
    });
  };

  const handleOpenComposer = (options?: Partial<ComposerState>) => {
    if (!marketingPermissions.canSchedule) {
      requestUpgrade("Scheduling posts requires an upgraded plan.");
      return;
    }
    openComposerDrawer(options);
  };

  const commandActions = useMemo(() => {
    const executorItems = executorCommands.map((entry) => ({
      id: entry.id,
      label: entry.label,
      hint: entry.hint,
      onSelect: () => void executeMarketingCommand(entry.id, entry.label),
    }));
    return [
      { id: "create_post", label: "Create a post", onSelect: () => handleOpenComposer() },
      { id: "schedule_clip", label: "Schedule clip", onSelect: () => handleOpenComposer() },
      ...executorItems,
      {
        id: "mixboard_asset",
        label: "Open Mixboard for asset",
        onSelect: () => navigate("/mixboard"),
      },
      {
        id: "clip_batch",
        label: "Request Auto-Editor clip batch",
        onSelect: () => void triggerOrchestrationAction("auto_editor.request_clip_batch"),
      },
      {
        id: "notifications",
        label: "Show all notifications",
        onSelect: () => {
          setActiveView("notifications");
          setNotificationsDrawerOpen(true);
        },
      },
      {
        id: "platform_status",
        label: "View platform status",
        onSelect: () => {
          setActiveView("today");
          push({ title: "Platform status", message: "See the right sidebar for live connector health." });
        },
      },
      {
        id: "marketing_calendar",
        label: "Open marketing calendar",
        onSelect: () => navigate("/marketing/calendar"),
      },
    ];
  }, [
    executorCommands,
    executeMarketingCommand,
    handleOpenComposer,
    navigate,
    push,
    triggerOrchestrationAction,
    setActiveView,
  ]);
  const handleLifecycleFromTaskId = useCallback(
    (taskId: string) => {
      const target = safeTasks.find((task) => task.id === taskId);
      if (target) {
        handleOpenLifecycle(target);
        return;
      }
      push({
        title: "Post unavailable",
        message: "We couldn't find lifecycle data for that post yet.",
        variant: "destructive",
      });
    },
    [safeTasks, push],
  );

  const handleApplyNudge = async (nudge: MarketingNudge) => {
    if (!nudge.recommendedAction) return;
    try {
      await post(`/marketing/analytics/nudges/${nudge.id}/apply`, {});
      push({ title: "Suggestion applied", message: "Recommendation added to the plan." });
      await Promise.all([
        mutateGlobal("/marketing/tasks"),
        mutateGlobal("/marketing/calendar"),
        mutateNudges(),
      ]);
    } catch (error) {
      push({
        title: "Unable to apply suggestion",
        message: error instanceof Error ? error.message : "Try again later.",
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    if (!selectedTourDateId && safeTourDates.length > 0) {
      setSelectedTourDateId(safeTourDates[0].id);
    }
  }, [selectedTourDateId, safeTourDates]);

  useEffect(() => {
    if (competitiveState.trackedIds.length === 0 && safeCompetitiveArtists.length) {
      setCompetitiveState({ trackedIds: safeCompetitiveArtists.slice(0, 3).map((artist) => artist.id) });
    }
  }, [competitiveState.trackedIds.length, safeCompetitiveArtists]);

  useEffect(() => {
    if (!selectedTourConversionId && safeTourConversions.length) {
      setSelectedTourConversionId(safeTourConversions[0].dateId);
    }
  }, [selectedTourConversionId, safeTourConversions]);

  const handleCloseComposer = () => {
    setComposerState({ open: false });
  };

  const handleOpenReplayDrawer = (source: ReplaySourcePost) => {
    setReplayDrawerState({ open: true, source });
  };

  const handleCloseReplayDrawer = () => {
    setReplayDrawerState({ open: false, source: null });
  };

  const handleOpenLifecycle = (task: MarketingTask) => {
    setLifecycleTask(task);
  };

  const handleCloseLifecycle = () => {
    setLifecycleTask(null);
  };

  const openComposerFromShelf = (item: ContentShelfItem) => {
    handleOpenComposer({
      initialAsset: item.asset
        ? {
            ...item.asset,
            thumbnailUrl: item.asset.thumbnailUrl ?? item.thumbnailUrl,
          }
        : item.thumbnailUrl
          ? {
              id: item.id,
              type: "image",
              title: item.title,
              thumbnailUrl: item.thumbnailUrl,
              url: item.thumbnailUrl,
            }
          : undefined,
      initialDatetime: new Date().toISOString(),
    });
  };

  const handleShelfReplay = async (item: ContentShelfItem) => {
    try {
      await post(`/marketing/shelf/replay/${item.id}`, {});
      push({ title: "Loaded into composer", message: `${item.title} is ready to re-run.` });
      openComposerFromShelf(item);
      await Promise.all([
        mutateGlobal("/marketing/tasks"),
        mutateGlobal("/marketing/timeline"),
        mutateGlobal("/marketing/calendar"),
        mutateShelf(),
      ]);
    } catch (error) {
      push({
        title: "Unable to replay item",
        message: error instanceof Error ? error.message : "Please try again soon.",
        variant: "destructive",
      });
    }
  };

  const handleConnectAds = (platform: AdAccount["platform"]) => {
    push({
      title: `Connecting ${platform === "facebook" ? "Facebook" : platform === "instagram" ? "Instagram" : "TikTok"} Ads`,
      message: "OAuth flow will open once ads auth is wired.",
    });
  };

  const handleCreateAd = async (payload: CreateAdPayload) => {
    try {
      await post("/marketing/ads/create", payload);
      push({ title: "Ad created", message: "Campaign added to ads manager." });
      await Promise.all([mutateAdCampaigns(), mutateAdPerformance()]);
      setAdDrawerState({ open: false, seedTask: null });
    } catch (error) {
      push({
        title: "Unable to create ad",
        message: error instanceof Error ? error.message : "Please try again later.",
        variant: "destructive",
      });
    }
  };

  const handleBoostEventAd = async (task: MarketingTask) => {
    try {
      await post("/marketing/ads/boost", { taskId: task.id });
      push({ title: "Boost scheduled", message: `Tour stop boosted for ${task.title}.` });
      await mutateAdCampaigns();
    } catch (error) {
      push({
        title: "Unable to boost event",
        message: error instanceof Error ? error.message : "Try again later.",
        variant: "destructive",
      });
    }
  };

  const handleUpdatePersona = async (personaId: string, payload: { note?: string }) => {
    try {
      await post(`/marketing/audience/update-persona/${personaId}`, payload);
      push({ title: "Persona updated", message: "Segment insights refreshed." });
      await Promise.all([mutateAudienceSegments(), mutateAudiencePersonas()]);
    } catch (error) {
      push({
        title: "Unable to update persona",
        message: error instanceof Error ? error.message : "Please try again later.",
        variant: "destructive",
      });
    }
  };

  const handleGenerateTourPromo = async (tourId: string, action: "generate" | "schedule") => {
    try {
      await post(`/marketing/tour/${tourId}/generate-promo`, { action });
      push({
        title: action === "generate" ? "Assets generating" : "Scheduling promo",
        message: action === "generate" ? "Building creative kit for this city." : "Queued content across channels.",
      });
      await mutateTourInsights();
    } catch (error) {
      push({
        title: "Unable to process tour promo",
        message: error instanceof Error ? error.message : "Try again shortly.",
        variant: "destructive",
      });
    }
  };

  const handleAnalyzeConsistency = async () => {
    setConsistencyLoading(true);
    try {
      const report = await post("/marketing/consistency/analyze", {});
      setConsistencyReport(report as ConsistencyReport);
      push({ title: "Consistency analyzed", message: "Report updated with latest findings." });
    } catch (error) {
      push({
        title: "Unable to analyze consistency",
        message: error instanceof Error ? error.message : "Try again later.",
        variant: "destructive",
      });
    } finally {
      setConsistencyLoading(false);
    }
  };

  const handleFixConsistency = async (option?: string) => {
    setConsistencyLoading(true);
    try {
      await post("/marketing/consistency/fix", { option });
      push({ title: "Consistency fix queued", message: "Batch adjustments scheduled." });
      await handleAnalyzeConsistency();
    } catch (error) {
      push({
        title: "Unable to apply fix",
        message: error instanceof Error ? error.message : "Try again later.",
        variant: "destructive",
      });
      setConsistencyLoading(false);
    }
  };

  const handleCreateExperiment = async (payload: {
    name: string;
    metric: Experiment["metric"];
    variants: ExperimentDraftVariant[];
  }) => {
    try {
      await post("/marketing/experiments/create", payload);
      push({ title: "Experiment launched", message: "Variants scheduled and tracking." });
      setExperimentDrawerOpen(false);
      await mutateExperiments();
    } catch (error) {
      push({
        title: "Unable to create experiment",
        message: error instanceof Error ? error.message : "Try again later.",
        variant: "destructive",
      });
    }
  };

  const handleApplyExperimentWinner = async (experimentId: string) => {
    try {
      await post(`/marketing/experiments/${experimentId}/apply_winner`, {});
      push({ title: "Winner applied", message: "Future posts will use the winning variant." });
      await Promise.all([mutateExperiments(), mutateExperimentDetail()]);
    } catch (error) {
      push({
        title: "Unable to apply winner",
        message: error instanceof Error ? error.message : "Try again later.",
        variant: "destructive",
      });
    }
  };

  const handleToggleTrackedArtist = async (artistId: string) => {
    setCompetitiveState((prev) => {
      const exists = prev.trackedIds.includes(artistId);
      return {
        trackedIds: exists ? prev.trackedIds.filter((id) => id !== artistId) : [...prev.trackedIds, artistId],
      };
    });
  };

  const handleSendCollabOutreach = async (creatorId: string, tone: string, message: string) => {
    try {
      await post("/marketing/collabs/outreach", { creatorId, tone, message });
      push({ title: "Outreach sent", message: "We’ll track replies and update the timeline." });
      await mutateCollabHistory();
    } catch (error) {
      push({
        title: "Unable to send outreach",
        message: error instanceof Error ? error.message : "Please try again later.",
        variant: "destructive",
      });
    }
  };

  const handleLinkCollab = async (historyId: string) => {
    try {
      await post(`/marketing/collabs/link/${historyId}`, {});
      push({ title: "Collaboration linked", message: "Growth metrics updated." });
      await mutateCollabHistory();
    } catch (error) {
      push({
        title: "Unable to link collab",
        message: error instanceof Error ? error.message : "Please try again later.",
        variant: "destructive",
      });
    }
  };

  if (hasMarketingAccess && tasksLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background text-foreground">
        <Spinner className="h-12 w-12 border-primary/60" />
      </div>
    );
  }

  const renderView = () => {
    switch (activeView) {
      case "today":
        return (
          <div className="space-y-6">
            <MarketingOverviewWidgets
              kpis={safeKpis}
              loading={kpisLoading && !overviewKpis}
              onNavigate={(view) => setActiveView(view)}
            />
            <CapabilityGate capability="marketing_insights" variant="card" fallback={null}>
              <div className="space-y-4">
                <InsightsPanel data={marketingInsights} loading={insightsLoading && !marketingInsights} />
                <CampaignHealthPanel data={safeCampaignHealth} loading={campaignHealthLoadingState} />
                <IdeaFeed data={safeIdeas ?? undefined} loading={ideasLoadingState} />
                <GeoFanGrowthPanel />
                <CRMAudienceSync />
                <EngagementHub />
                <FanJourneyFunnel />
                <TrendwatchPanel />
                <CapabilityGate capability="marketing_automation" variant="card" fallback={null}>
                  <AutopilotSettings />
                </CapabilityGate>
                <AudienceForecastPanel />
                <ViralCandidatesPanel />
              </div>
            </CapabilityGate>
            <TodayView
              tasks={safeTasks}
              nudges={safeNudges}
              onApplyNudge={handleApplyNudge}
              onCompose={(options) => handleOpenComposer(options)}
              canSchedule={marketingPermissions.canSchedule}
              onScheduleBlocked={() => requestUpgrade("Scheduling posts requires an upgraded plan.")}
            />
          </div>
        );
      case "week":
        return <WeekView tasks={safeTasks} nudges={safeNudges} onApplyNudge={handleApplyNudge} />;
      case "calendar":
        return (
          <CalendarView
            tasks={safeTasks}
            onOpenComposer={handleOpenComposer}
            canSchedule={marketingPermissions.canSchedule}
            onScheduleBlocked={() => requestUpgrade("Scheduling posts requires an upgraded plan.")}
            automationWindows={automationRules?.postingWindows ?? ["morning", "afternoon", "evening"]}
          />
        );
      case "timeline":
        return (
          <CapabilityGate
            capability="marketing_timeline"
            variant="card"
            fallback={null}
          >
            <TimelineView
              tasks={safeTasks}
              notifications={safeNotifications}
              notificationsLoading={notificationsLoading}
              onOpenComposer={handleOpenComposer}
            />
          </CapabilityGate>
        );
      case "automation":
        if (!marketingPermissions.canUseAutomation) {
          return <PermissionNotice message="Automation rules require additional permissions." onUpgrade={requestUpgrade} />;
        }
        return (
          <CapabilityGate
            capability="marketing_automation"
            variant="card"
            fallback={null}
          >
            <div className="space-y-6">
              <AutomationRulesView rules={safeAutomationRules} />
              <AutomationWorkflowView
                workflows={safeAutomationWorkflows}
                loading={Boolean(automationWorkflowsLoading && !automationWorkflows)}
                mutateWorkflows={mutateAutomationWorkflows}
              />
            </div>
          </CapabilityGate>
        );
      case "shortform":
        if (!marketingPermissions.canEditAssets) {
          return <PermissionNotice message="AI asset generation requires creative permissions." onUpgrade={requestUpgrade} />;
        }
        return <ShortFormGeneratorView onSendToComposer={handleOpenComposer} />;
      case "templates":
        if (!marketingPermissions.canEditAssets) {
          return <PermissionNotice message="Template Builder is limited to creative roles." onUpgrade={requestUpgrade} />;
        }
        return <TemplateBuilderView onSendToComposer={handleOpenComposer} />;
      case "replay":
        return (
          <ReplayEngineView
            posts={safeReplaySources}
            loading={replaySourcesLoading && !replaySources}
            onReplayClick={handleOpenReplayDrawer}
          />
        );
      case "collabs":
        return (
          <div className="space-y-6">
            <CollabManagerView
              creators={safeCollabCreators}
              history={safeCollabHistory}
              loading={!collabCreators && hasMarketingAccess}
              onSendOutreach={handleSendCollabOutreach}
              onLinkHistory={handleLinkCollab}
            />
            <CreatorPayoutPanel />
            <InfluencerOutreachPanel />
          </div>
        );
      case "ads":
        return (
          <AdManagerView
            accounts={safeAdAccounts}
            campaigns={safeAdCampaigns}
            performance={safeAdPerformance}
            tasks={safeTasks}
            onConnectAccount={handleConnectAds}
            onOpenCreate={(seedTask) => setAdDrawerState({ open: true, seedTask })}
            onBoostEvent={handleBoostEventAd}
          />
        );
      case "tour":
        return (
          <TourPromoEngineView
            dates={safeTourDates}
            selectedId={selectedTourDateId}
            insights={tourInsights ?? null}
            loading={!tourDates && hasMarketingAccess}
            onSelectDate={(id) => setSelectedTourDateId(id)}
            onGenerateAssets={(id) => void handleGenerateTourPromo(id, "generate")}
            onScheduleAll={(id) => void handleGenerateTourPromo(id, "schedule")}
          />
        );
      case "tour-conversion":
        return (
          <TourConversionView
            conversions={safeTourConversions}
            selectedId={selectedTourConversionId}
            detail={tourConversionDetail ?? null}
            onSelect={(id) => setSelectedTourConversionId(id)}
          />
        );
      case "shelf":
        return (
          <ContentShelfView
            items={safeShelfItems}
            loading={!shelfItems && hasMarketingAccess}
            onUseAgain={handleShelfReplay}
            onOpenComposer={handleOpenComposer}
          />
        );
      case "engagement":
        return (
          <EngagementView
            items={[...safeEngagement.feed, ...safeEngagement.comments, ...safeEngagement.dms]}
            signals={safeEngagement.signals}
            onRespond={(item) => setDmDrawerItem(item)}
            onCompose={handleOpenComposer}
            onAutoReply={(item) => setReplyAssistantItem(item)}
            onBoost={handleEngagementBoost}
          />
        );
      case "notifications":
        return (
          <NotificationsView
            notifications={safeNotifications}
            loading={notificationsLoading}
            onOpenComposer={handleOpenComposer}
          />
        );
      case "clips":
        return <ClipsView tasks={safeTasks} refreshTasks={refreshTasks} onOpenComposer={handleOpenComposer} />;
      case "assets":
        return (
          <AssetsView
            tasks={safeTasks}
            onOpenComposer={handleOpenComposer}
            canEditAssets={marketingPermissions.canEditAssets}
            onRequestUpgrade={requestUpgrade}
          />
        );
      case "analytics":
        return (
          <AnalyticsView
            timeseries={safeTimeseries}
            audience={safeAudience}
            platforms={safePlatforms}
            cohorts={safeCohorts}
            performance={safePerformance}
            streamingInsights={safeStreamingInsights}
            streamingLoading={streamingInsightsLoading}
          />
        );
      case "audience":
        return (
          <AudienceSegmentationView
            segments={safeAudienceSegments}
            personas={safeAudiencePersonas}
            onUpdatePersona={handleUpdatePersona}
          />
        );
      case "consistency":
        return (
          <ConsistencyView
            report={consistencyReport}
            loading={consistencyLoading}
            onAnalyze={handleAnalyzeConsistency}
            onFix={handleFixConsistency}
          />
        );
      case "experiments":
        return (
          <ExperimentsView
            experiments={safeExperiments}
            onStart={() => setExperimentDrawerOpen(true)}
            onSelect={(id) => setSelectedExperimentId(id)}
            selectedExperiment={experimentDetail ?? null}
            onApplyWinner={handleApplyExperimentWinner}
          />
        );
      case "competitive":
        return (
          <CompetitiveView
            artists={safeCompetitiveArtists}
            trackedIds={competitiveState.trackedIds}
            onToggleArtist={handleToggleTrackedArtist}
            performance={safeCompetitivePerformance}
            posts={safeCompetitivePosts}
            strategy={safeCompetitiveStrategy}
          />
        );
      case "inbox":
        return (
          <ContentInboxView
            items={safeInboxItems}
            mutateInbox={mutateInbox}
            onComposer={handleOpenComposer}
          />
        );
      case "trends":
        return (
          <TrendMinerView
            trends={safeTrends}
            onUseInShortform={() => setActiveView("shortform")}
            onUseInComposer={(trend) =>
              handleOpenComposer({
                initialDatetime: new Date().toISOString(),
                initialTask: undefined,
                initialAsset: trend.previewUrl
                  ? { id: trend.id, type: trend.type === "sound" ? "clip" : "template", title: trend.label, url: trend.previewUrl }
                  : undefined,
              })
            }
          />
        );
      case "brandkit":
        return (
          <BrandKitView
            brandKit={safeBrandKit}
            mutateBrandKit={mutateBrandKit}
            canEditAssets={marketingPermissions.canEditAssets}
            onRequestUpgrade={requestUpgrade}
          />
        );
      case "growth":
        return (
          <GrowthPlannerView
            kpis={safeKpis}
            goals={safeGrowthGoals}
            recommendations={safeGrowthRecommendations}
            mutateGoals={mutateGrowthGoals}
            mutateRecommendations={mutateGrowthRecommendations}
          />
        );
      case "captions":
        return <CaptionsView />;
      case "campaigns":
        if (!marketingPermissions.canManageCampaigns) {
          return <PermissionNotice message="Campaign controls require manager permissions." onUpgrade={requestUpgrade} />;
        }
        return (
          <CampaignsView
            nudges={safeNudges}
            tasks={safeTasks}
            onApplyNudge={handleApplyNudge}
            onOpenComposer={handleOpenComposer}
            permissions={marketingPermissions}
            onRequestUpgrade={requestUpgrade}
          />
        );
      case "permissions":
        return <PermissionsView permissions={marketingPermissions} onUpgrade={requestUpgrade} />;
      default:
        return null;
    }
  };

  const navItemsWithCounts = NAV_ITEMS.map((item) =>
    item.id === "notifications"
      ? { ...item, badgeCount: notificationsLoading ? undefined : safeNotifications.length }
      : item,
  );

  const renderNavItems = (onNavigate?: () => void) => (
    <nav className="flex-1 space-y-1 px-2 pb-6">
      {navItemsWithCounts.map((item) => {
        const isActive = activeView === item.id;
        const requiredCapability = item.capability;
        const hasCapability = requiredCapability ? Boolean(capabilities?.[requiredCapability]) : true;
        const isLocked = Boolean(requiredCapability && !hasCapability);
        return (
          <Button
            key={item.id}
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              if (isLocked) {
                openUpgradeModal(requiredCapability ?? item.label);
                return;
              }
              setActiveView(item.id);
              if (item.id === "notifications") {
                setNotificationsDrawerOpen(false);
              }
              onNavigate?.();
            }}
            className={cn(
              "flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm font-medium text-foreground/70 transition hover:bg-muted/70 hover:text-foreground",
              isActive && "bg-muted text-foreground shadow-inner",
            )}
          >
            <span className="flex items-center gap-2">
              {item.label}
              {isLocked ? <Lock className="h-3.5 w-3.5 text-primary" /> : null}
            </span>
            {item.badgeCount ? (
              <Badge
                variant="secondary"
                className={cn(
                  "border-primary/40 bg-primary/10 text-primary",
                  isActive && "bg-primary/20 text-foreground",
                )}
              >
                {item.badgeCount}
              </Badge>
            ) : null}
          </Button>
        );
      })}
    </nav>
  );

  return (
    <CapabilityGate capability="marketing_access" variant="card" fallback={null}>
      <div className="aa-page-shell flex min-h-screen text-foreground">
        <aside className="aa-glass-card hidden w-64 flex-col border-r border-border/70 lg:flex">
          <div className="px-4 pb-4 pt-6">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Marketing Ops</p>
            <p className="text-sm text-muted-foreground">Switch between orchestration surfaces.</p>
          </div>
        {renderNavItems()}
      </aside>

      <div className="flex flex-1 flex-col">
        <header className="aa-glass-card border-b border-border/70">
          <div className="flex flex-col gap-4 px-4 py-4 lg:px-8 lg:py-6">
            <div className="flex flex-wrap items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden"
                onClick={() => setMobileNavOpen(true)}
                aria-label="Open navigation"
              >
                <Menu className="h-5 w-5" />
              </Button>
              <div className="flex-1">
                <h1 className="aa-page-title text-3xl font-semibold leading-tight">
                  <span className="aa-gradient-text">Marketing Control Center</span>
                </h1>
                <p className="text-sm text-muted-foreground">
                  Coordinate omnichannel campaigns, creative approvals, and broadcast cadences from a single view.
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-end gap-2">
              <ToggleGroup
                type="single"
                className="aa-glass-card rounded-xl p-1 text-xs uppercase tracking-wide text-muted-foreground"
                value={dateContext}
                onValueChange={(value) => {
                  if (value) {
                    setDateContext(value as DateContext);
                  }
                }}
                aria-label="Date context"
              >
                {DATE_CONTEXT_OPTIONS.map((option) => (
                  <ToggleGroupItem
                    key={option.value}
                    value={option.value}
                    className="px-4 py-1 text-xs data-[state=on]:border-primary data-[state=on]:text-foreground"
                    aria-label={option.label}
                  >
                    {option.label}
                  </ToggleGroupItem>
                ))}
              </ToggleGroup>

              <Button
                variant="secondary"
                className={cn(
                  "bg-emerald-500/20 text-emerald-200 hover:bg-emerald-500/30",
                  !marketingPermissions.canSchedule && "opacity-50",
                )}
                onClick={() => {
                  if (!marketingPermissions.canSchedule) {
                    requestUpgrade("Scheduling posts requires an upgraded plan.");
                    return;
                  }
                  handleOpenComposer();
                }}
              >
                New Post
              </Button>
              <Button variant="outline" className="border-border/80 text-foreground hover:bg-muted/70">
                Upload Footage
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="relative text-foreground/70 hover:text-foreground"
                aria-label="Notifications"
                onClick={() => setNotificationsDrawerOpen((prev) => !prev)}
              >
                <Bell className="h-5 w-5" />
                <span className="absolute -right-1 -top-1 inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-primary/90 px-1 text-xs font-semibold text-white">
                  {notificationsLoading ? "…" : notificationsCount}
                </span>
              </Button>
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto bg-background/60 px-4 py-6 lg:px-10 lg:py-10">
          {marketingLoadIssue ? (
            <div className="mx-auto mb-4 w-full max-w-6xl rounded-2xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-100">
              Some marketing feeds failed to load. Showing cached or stubbed data where possible.
            </div>
          ) : null}
          {marketingStubbedSections.length > 0 ? (
            <div className="mx-auto mb-6 w-full max-w-6xl rounded-2xl border border-dashed border-primary/50 bg-primary/10 px-4 py-3 text-sm text-foreground">
              <p className="text-xs font-semibold uppercase tracking-wide text-primary">Stubbed data</p>
              <p className="mt-1 text-foreground/80">
                {`Awaiting backend wiring for: ${marketingStubbedSections.join(", ")}`}
              </p>
            </div>
          ) : null}
          <UnlockedSection className="mx-auto flex w-full max-w-6xl flex-col gap-6 lg:flex-row">
            <div className="flex-1 space-y-6">{renderView()}</div>
            <div className="lg:w-80 space-y-4">
              <CapabilityGate
                capability="marketing_notifications"
                variant="card"
                fallback={null}
              >
                <NotificationsPanel
                  items={safeUnifiedNotifications}
                  loading={notificationsLoading}
                  onRefresh={() => void refreshNotifications()}
                />
              </CapabilityGate>
              <CrossSystemPanel />
              <CapabilityGate
                capability="marketing_automation"
                variant="card"
                fallback={null}
              >
                <MarketingActionsMenu context={{ brandId: selectedBrandId, view: activeView, dateContext }} />
              </CapabilityGate>
              <CapabilityGate capability="marketing_ads" variant="card" fallback={null}>
                <MetaAdsPanel
                  insights={safeMetaAdsInsights ?? undefined}
                  loading={metaAdsLoadingState}
                  onBoost={handleMetaBoost}
                  onCreateDarkPost={handleMetaCreateDarkPost}
                />
              </CapabilityGate>
              <CapabilityGate capability="marketing_ads" variant="card" fallback={null}>
                <BudgetAllocatorPanel
                  recommendations={safeBudgetRecommendations}
                  loading={budgetLoading && !budgetRecommendations}
                />
              </CapabilityGate>
              <CapabilityGate capability="marketing_ads" variant="card" fallback={null}>
                <RetargetingPanel />
              </CapabilityGate>
              <InfluencerOutreachPanel />
              <CapabilityGate capability="marketing_ads" variant="card" fallback={null}>
                <MerchPromoPanel />
              </CapabilityGate>
              <CrosspostPanel />
            </div>
          </UnlockedSection>
          <LockedSection items={lockedItems} className="space-y-6" />
        </main>
      </div>

      {mobileNavOpen ? (
        <div className="fixed inset-0 z-40 flex lg:hidden">
          <div className="flex-1 bg-black/70" onClick={() => setMobileNavOpen(false)} />
          <div className="flex h-full w-72 flex-col border-l border-border/70 bg-background/95 shadow-xl transition">
            <div className="flex items-center justify-between border-b border-border/70 px-4 py-3">
              <p className="text-sm font-semibold text-foreground">Navigation</p>
              <Button
                variant="ghost"
                size="icon"
                aria-label="Close navigation"
                onClick={() => setMobileNavOpen(false)}
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
            <div className="px-4 pb-4 pt-4">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Views</p>
            </div>
            {/* TODO: Replace with shadcn Sheet when we finalize mobile nav motion. */}
            {renderNavItems(() => setMobileNavOpen(false))}
          </div>
        </div>
      ) : null}
        <NotificationsDrawer
          open={notificationsDrawerOpen}
          onOpenChange={setNotificationsDrawerOpen}
          notifications={safeNotifications}
          loading={notificationsLoading}
          onOpenLifecycle={handleLifecycleFromTaskId}
        />
        <PostComposerDrawer
          open={composerState.open}
          onClose={handleCloseComposer}
          initialTask={composerState.initialTask ?? undefined}
          initialAsset={composerState.initialAsset ?? undefined}
          initialDatetime={composerState.initialDatetime ?? undefined}
          defaultPlatforms={
            composerState.initialTask?.platforms ?? automationRules?.defaultPlatforms ?? ["instagram"]
          }
          automationWindows={automationRules?.postingWindows ?? ["morning", "afternoon", "evening"]}
          permissions={marketingPermissions}
          onRequestUpgrade={requestUpgrade}
          onOpenLifecycle={handleOpenLifecycle}
          abTestPosts={safeTasks.map((task) => ({ id: task.id, title: task.title }))}
          onBoostWithMeta={({ caption: payloadCaption, mediaUrl }) =>
            handleMetaCreateDarkPost({ caption: payloadCaption, mediaUrl, budget: 75 })
          }
        />
        <DMResponseDrawer item={dmDrawerItem} onClose={() => setDmDrawerItem(null)} />
        <ReplyAssistantDrawer
          item={replyAssistantItem}
          onClose={() => setReplyAssistantItem(null)}
          onRespond={() => void mutateGlobal("/marketing/engagement/comments")}
        />
        <ReplayGeneratorDrawer
          open={replayDrawerState.open}
          source={replayDrawerState.source ?? null}
          onClose={handleCloseReplayDrawer}
          onSendToComposer={handleOpenComposer}
          refreshSources={mutateReplaySources}
          permissions={marketingPermissions}
          onRequestUpgrade={requestUpgrade}
        />
        <PostLifecycleDrawer task={lifecycleTask} open={Boolean(lifecycleTask)} onClose={handleCloseLifecycle} />
        <CreateAdDrawer
          open={adDrawerState.open}
          seedTask={adDrawerState.seedTask}
          onClose={() => setAdDrawerState({ open: false, seedTask: null })}
          onSubmit={handleCreateAd}
        />
        <ExperimentCreatorDrawer
          open={experimentDrawerOpen}
          onClose={() => setExperimentDrawerOpen(false)}
          onCreate={handleCreateExperiment}
        />
        <MarketingCommandPalette
          open={commandPaletteOpen}
          onOpenChange={setCommandPaletteOpen}
          actions={commandActions}
        />
      </div>
    </CapabilityGate>
  );
}

function PlaceholderPanel({ title, description }: { title: string; description?: string }) {
  return (
    <Card className="border-border/80 bg-background/30">
      <div className="space-y-2 p-10 text-center">
        <p className="text-lg font-semibold text-foreground">{title}</p>
        {description ? <p className="text-sm text-muted-foreground">{description}</p> : null}
      </div>
    </Card>
  );
}

function PermissionNotice({ message, onUpgrade }: { message: string; onUpgrade: (reason: string) => void }) {
  return (
    <Card className="border-border/80 bg-background/40 p-10 text-center text-foreground/70">
      <p className="text-lg font-semibold text-foreground">Permission required</p>
      <p className="mt-2 text-sm text-muted-foreground">{message}</p>
      <div className="mt-4 flex justify-center">
        <BlockButton label="Request access" onClick={() => onUpgrade(message)} />
      </div>
    </Card>
  );
}

const parseDateValue = (value?: string | null): Date | null => {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const getTaskTimestamp = (task: MarketingTask): Date | null =>
  parseDateValue(task.scheduledFor ?? task.dueAt ?? task.createdAt ?? null);

const formatDateLabel = (date: Date, options?: Intl.DateTimeFormatOptions) =>
  date.toLocaleDateString(undefined, options ?? { month: "short", day: "numeric" });

const formatTimeLabel = (date: Date) =>
  date.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });

const isSameDay = (baseline: Date, comparison: Date | null) =>
  Boolean(
    comparison &&
      baseline.getFullYear() === comparison.getFullYear() &&
      baseline.getMonth() === comparison.getMonth() &&
      baseline.getDate() === comparison.getDate(),
  );

type TodayViewProps = {
  tasks: MarketingTask[];
  nudges: MarketingNudge[];
  onApplyNudge: (nudgeId: string) => void | Promise<void>;
  onCompose: (options?: Partial<ComposerState>) => void;
  canSchedule: boolean;
  onScheduleBlocked: () => void;
};

function TodayView({ tasks, nudges, onApplyNudge, onCompose, canSchedule, onScheduleBlocked }: TodayViewProps) {
  const prioritized = useMemo(() => {
    return [...tasks]
      .filter((task) => task.status !== "done")
      .sort((a, b) => {
        const left = getTaskTimestamp(a)?.getTime() ?? Number.POSITIVE_INFINITY;
        const right = getTaskTimestamp(b)?.getTime() ?? Number.POSITIVE_INFINITY;
        return left - right;
      })
      .slice(0, 5);
  }, [tasks]);

  const readyToPublish = useMemo(
    () => tasks.filter((task) => task.status === "ready" || task.status === "scheduled").slice(0, 3),
    [tasks],
  );

  const handleOpenTask = (task: MarketingTask) => {
    if (!canSchedule) {
      onScheduleBlocked();
      return;
    }
    onCompose({
      initialTask: task,
      initialDatetime: task.scheduledFor ?? task.dueAt ?? new Date().toISOString(),
    });
  };

  return (
    <div className="grid gap-6 lg:grid-cols-3">
      <Card className="lg:col-span-2 border-border/70 bg-background/70">
        <CardHeader>
          <CardTitle className="text-foreground">Today&apos;s queue</CardTitle>
          <CardDescription className="text-muted-foreground">
            Top priority content and promo tasks that need attention.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {prioritized.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border/70 p-6 text-center text-sm text-muted-foreground">
              Nothing on the board. Spin up a post or queue an automation.
            </div>
          ) : (
            prioritized.map((task) => {
              const timestamp = getTaskTimestamp(task);
              return (
                <div
                  key={task.id}
                  className="flex flex-col gap-3 rounded-2xl border border-border/70 bg-background/30 p-4 md:flex-row md:items-center"
                >
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-foreground">{task.title}</p>
                    {task.description ? <p className="text-xs text-muted-foreground">{task.description}</p> : null}
                    <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                      <Badge variant="outline" className="border-border text-foreground/80">
                        {task.status}
                      </Badge>
                      {task.platforms?.length ? <span>{task.platforms.join(", ")}</span> : null}
                      {timestamp ? <span>Due {formatDateLabel(timestamp, { weekday: "short", hour: "numeric" })}</span> : null}
                    </div>
                  </div>
                  <Button
                    variant="secondary"
                    className="w-full bg-emerald-500/20 text-emerald-100 hover:bg-emerald-500/30 md:w-auto"
                    onClick={() => handleOpenTask(task)}
                  >
                    {task.status === "scheduled" ? "Review slot" : "Schedule"}
                  </Button>
                </div>
              );
            })
          )}
        </CardContent>
      </Card>
      <div className="space-y-4">
        <Card className="border-border/70 bg-background/70">
          <CardHeader>
            <CardTitle className="text-foreground text-base">AI nudges</CardTitle>
            <CardDescription className="text-muted-foreground">Auto-detected opportunities.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {nudges.length === 0 ? (
              <p className="text-sm text-muted-foreground">No nudges active.</p>
            ) : (
              nudges.slice(0, 4).map((nudge) => (
                <div key={nudge.id} className="rounded-xl border border-border/60 bg-background/30 p-3">
                  <div className="flex items-center justify-between text-xs uppercase tracking-wide text-muted-foreground">
                    <span>{nudge.type}</span>
                    <span>{nudge.severity}</span>
                  </div>
                  <p className="mt-2 text-sm text-foreground">{nudge.message}</p>
                  {nudge.recommendedAction ? (
                    <p className="text-xs text-muted-foreground">{nudge.recommendedAction}</p>
                  ) : null}
                  <Button
                    variant="ghost"
                    className="mt-2 h-8 px-2 text-xs text-foreground hover:text-foreground"
                    onClick={() => void onApplyNudge(nudge.id)}
                  >
                    Apply suggestion
                  </Button>
                </div>
              ))
            )}
          </CardContent>
        </Card>
        <Card className="border-border/70 bg-background/70">
          <CardHeader>
            <CardTitle className="text-base text-foreground">Ready to publish</CardTitle>
            <CardDescription className="text-muted-foreground">Posts the agent marked ready.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {readyToPublish.length === 0 ? (
              <p className="text-sm text-muted-foreground">Once clips finish processing they’ll appear here.</p>
            ) : (
              readyToPublish.map((task) => (
                <div key={task.id} className="rounded-lg border border-border/70 p-3">
                  <p className="text-sm font-medium text-foreground">{task.title}</p>
                  <div className="mt-1 flex items-center justify-between text-xs text-muted-foreground">
                    <span>{task.platforms?.join(", ") ?? "All platforms"}</span>
                    {task.scheduledFor ? <span>{new Date(task.scheduledFor).toLocaleString()}</span> : null}
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

type WeekViewProps = {
  tasks: MarketingTask[];
  nudges: MarketingNudge[];
  onApplyNudge: (nudgeId: string) => void | Promise<void>;
};

function WeekView({ tasks, nudges, onApplyNudge }: WeekViewProps) {
  const start = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  }, []);

  const days = useMemo(
    () =>
      Array.from({ length: 7 }).map((_, index) => {
        const date = new Date(start);
        date.setDate(start.getDate() + index);
        return date;
      }),
    [start],
  );

  const groupedTasks = useMemo(() => {
    const grouping: Record<string, MarketingTask[]> = {};
    days.forEach((day) => {
      grouping[day.toISOString().split("T")[0]] = [];
    });
    tasks.forEach((task) => {
      const timestamp = getTaskTimestamp(task);
      if (!timestamp) {
        return;
      }
      const key = timestamp.toISOString().split("T")[0];
      if (!grouping[key]) {
        grouping[key] = [];
      }
      grouping[key].push(task);
    });
    return grouping;
  }, [days, tasks]);

  return (
    <div className="space-y-6">
      <div className="grid gap-4 lg:grid-cols-4">
        {days.map((day) => {
          const key = day.toISOString().split("T")[0];
          const items = groupedTasks[key] ?? [];
          return (
            <Card key={key} className="border-border/70 bg-background/60">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-foreground">{formatDateLabel(day, { weekday: "long" })}</CardTitle>
                <CardDescription className="text-muted-foreground">{formatDateLabel(day)}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {items.length === 0 ? (
                  <p className="text-xs text-muted-foreground">No scheduled content.</p>
                ) : (
                  items.slice(0, 4).map((task) => {
                    const timestamp = getTaskTimestamp(task);
                    return (
                      <div key={task.id} className="rounded-lg border border-border/70 p-3">
                        <p className="text-sm font-medium text-foreground">{task.title}</p>
                        <div className="mt-1 flex items-center justify-between text-xs text-muted-foreground">
                          <span>{task.platforms?.join(", ") ?? "All"}</span>
                          {timestamp ? <span>{formatTimeLabel(timestamp)}</span> : null}
                        </div>
                      </div>
                    );
                  })
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
      <Card className="border-border/70 bg-background/70">
        <CardHeader>
          <CardTitle className="text-base text-foreground">Automation nudges</CardTitle>
          <CardDescription className="text-muted-foreground">Suggestions to smooth the upcoming week.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-3 md:grid-cols-2">
          {nudges.length === 0 ? (
            <p className="text-sm text-muted-foreground">No nudges at the moment.</p>
          ) : (
            nudges.slice(0, 6).map((nudge) => (
              <div key={nudge.id} className="rounded-xl border border-border/60 bg-background/30 p-4">
                <p className="text-sm font-medium text-foreground">{nudge.message}</p>
                {nudge.recommendedAction ? (
                  <p className="text-xs text-muted-foreground">{nudge.recommendedAction}</p>
                ) : null}
                <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground">
                  <span>{nudge.type}</span>
                  <Button variant="ghost" className="h-8 px-2 text-foreground" onClick={() => void onApplyNudge(nudge.id)}>
                    Apply
                  </Button>
                </div>
              </div>
            ))
          )}
        </CardContent>
      </Card>
    </div>
  );
}

type CalendarViewProps = {
  tasks: MarketingTask[];
  onOpenComposer: (options?: Partial<ComposerState>) => void;
  canSchedule: boolean;
  onScheduleBlocked: () => void;
  automationWindows: PostingWindow[];
};

function CalendarView({ tasks, onOpenComposer, canSchedule, onScheduleBlocked, automationWindows }: CalendarViewProps) {
  const start = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  }, []);
  const days = useMemo(
    () =>
      Array.from({ length: 14 }).map((_, index) => {
        const date = new Date(start);
        date.setDate(start.getDate() + index);
        return date;
      }),
    [start],
  );

  const getTasksForDay = (day: Date) =>
    tasks.filter((task) => {
      const timestamp = getTaskTimestamp(task);
      return isSameDay(day, timestamp);
    });

  const handleSchedule = (day: Date, window?: PostingWindow) => {
    if (!canSchedule) {
      onScheduleBlocked();
      return;
    }
    const scheduled = new Date(day);
    if (window === "morning") scheduled.setHours(9, 0, 0, 0);
    if (window === "afternoon") scheduled.setHours(13, 0, 0, 0);
    if (window === "evening") scheduled.setHours(18, 0, 0, 0);
    if (window === "late") scheduled.setHours(21, 0, 0, 0);
    onOpenComposer({ initialDatetime: scheduled.toISOString() });
  };

  return (
    <div className="space-y-6">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {days.map((day) => {
          const entries = getTasksForDay(day);
          const key = day.toISOString();
          return (
            <div key={key} className="rounded-2xl border border-border/70 bg-background/50 p-4">
              <div className="flex items-center justify-between text-xs uppercase tracking-wide text-muted-foreground">
                <span>{formatDateLabel(day, { weekday: "short" })}</span>
                <span>{formatDateLabel(day)}</span>
              </div>
              <div className="mt-3 space-y-2">
                {entries.length === 0 ? (
                  <p className="text-xs text-muted-foreground">Open slot</p>
                ) : (
                  entries.slice(0, 3).map((task) => (
                    <div key={task.id} className="rounded-xl border border-border/70 bg-background/40 p-3">
                      <p className="text-sm font-medium text-foreground">{task.title}</p>
                      <p className="text-xs text-muted-foreground">{task.platforms?.join(", ") ?? "All platforms"}</p>
                    </div>
                  ))
                )}
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {automationWindows.slice(0, 2).map((window) => (
                  <Button
                    key={`${key}-${window}`}
                    variant="outline"
                    size="sm"
                    className="border-border/80 text-xs text-foreground/80 hover:bg-muted/70"
                    onClick={() => handleSchedule(day, window)}
                  >
                    Slot {window}
                  </Button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      <Card className="border-border/70 bg-background/60">
        <CardHeader>
          <CardTitle className="text-foreground">Posting windows</CardTitle>
          <CardDescription className="text-muted-foreground">
            These windows feed the automation scheduler. Click a window to schedule a slot.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-3">
          {automationWindows.map((window) => (
            <Button
              key={window}
              variant="secondary"
              className="border border-border/70 bg-background/40 text-foreground hover:bg-background/40"
              onClick={() => handleSchedule(new Date(), window)}
            >
              {window}
            </Button>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

type TimelineViewProps = {
  tasks: MarketingTask[];
  notifications: NotificationItem[];
  notificationsLoading: boolean;
  onOpenComposer: (options?: Partial<ComposerState>) => void;
};

function TimelineView({ tasks, notifications, notificationsLoading, onOpenComposer }: TimelineViewProps) {
  const events = useMemo(() => {
    const taskEvents = tasks
      .map((task) => {
        const timestamp = getTaskTimestamp(task);
        if (!timestamp) {
          return null;
        }
        return {
          id: `task-${task.id}`,
          timestamp,
          label: task.title,
          type: "task" as const,
          secondary: task.platforms?.join(", ") ?? "All platforms",
          status: task.status,
          onPrimary: () => onOpenComposer({ initialTask: task }),
        };
      })
      .filter(Boolean) as Array<{
      id: string;
      timestamp: Date;
      label: string;
      type: "task";
      secondary?: string;
      status?: MarketingTaskStatus;
      onPrimary?: () => void;
    }>;

    const notificationEvents = notifications
      .map((notification) => ({
        id: `notification-${notification.id}`,
        timestamp: parseDateValue(notification.createdAt) ?? new Date(),
        label: notification.title,
        type: "notification" as const,
        secondary: notification.message,
        severity: notification.severity,
      }))
      .filter(Boolean);

    return [...taskEvents, ...notificationEvents].sort(
      (a, b) => b.timestamp.getTime() - a.timestamp.getTime(),
    );
  }, [notifications, onOpenComposer, tasks]);

  return (
    <Card className="border-border/70 bg-background/70">
      <CardHeader>
        <CardTitle className="text-foreground">Lifecycle timeline</CardTitle>
        <CardDescription className="text-muted-foreground">
          Every task and notification plotted chronologically.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {notificationsLoading && <p className="text-xs text-muted-foreground">Syncing signals…</p>}
        {events.length === 0 ? (
          <p className="text-sm text-muted-foreground">No recent lifecycle events.</p>
        ) : (
          <ol className="space-y-4">
            {events.slice(0, 20).map((event) => (
              <li key={event.id} className="flex gap-4">
                <span className="text-xs text-muted-foreground w-24 shrink-0">
                  {event.timestamp.toLocaleString()}
                </span>
                <div className="flex-1 rounded-xl border border-border/70 bg-background/30 p-3">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold text-foreground">{event.label}</p>
                    {event.type === "task" && event.status ? (
                      <Badge variant="outline" className="border-border text-foreground/80">
                        {event.status}
                      </Badge>
                    ) : null}
                  </div>
                  {event.secondary ? <p className="text-xs text-muted-foreground">{event.secondary}</p> : null}
                  {event.type === "task" && event.onPrimary ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="mt-2 h-8 px-2 text-foreground/80"
                      onClick={event.onPrimary}
                    >
                      Open in composer
                    </Button>
                  ) : null}
                </div>
              </li>
            ))}
          </ol>
        )}
      </CardContent>
    </Card>
  );
}

type AutomationRulesViewProps = {
  rules: MarketingAutomationRules;
};

function AutomationRulesView({ rules }: AutomationRulesViewProps) {
  const cadence = rules?.postingCadence ?? DEFAULT_AUTOMATION_RULES.postingCadence;
  const windows = rules?.postingWindows ?? [];
  const crossposting = rules?.crossposting ?? DEFAULT_AUTOMATION_RULES.crossposting;
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <Card className="border-border/70 bg-background/60">
        <CardHeader>
          <CardTitle className="text-foreground">Posting cadence</CardTitle>
          <CardDescription className="text-muted-foreground">
            Future slots the agent will try to fill per day.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center justify-between rounded-xl border border-border/70 bg-background/40 p-4">
            <div>
              <p className="text-sm font-semibold text-foreground">{cadence.mode === "custom" ? "Custom cadence" : "Preset cadence"}</p>
              <p className="text-xs text-muted-foreground">
                {cadence.mode === "custom"
                  ? `${cadence.customFrequency ?? cadence.frequency ?? 0} posts/day`
                  : `${cadence.frequency ?? 0} posts/day`}
              </p>
            </div>
            <Badge variant="secondary" className="bg-emerald-500/20 text-emerald-100">
              {cadence.weekendPosting ? "Weekend on" : "Weekday only"}
            </Badge>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Posting windows</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {windows.map((window) => (
                <Badge key={window} variant="outline" className="border-border/80 text-foreground">
                  {window}
                </Badge>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
      <Card className="border-border/70 bg-background/60">
        <CardHeader>
          <CardTitle className="text-foreground">Crossposting rules</CardTitle>
          <CardDescription className="text-muted-foreground">
            Each toggle indicates whether the agent mirrors content automatically.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {CROSSPOSTING_RULES.map((rule) => (
            <div
              key={rule.key}
              className="flex items-start justify-between rounded-xl border border-border/70 bg-background/30 p-3"
            >
              <div>
                <p className="text-sm font-semibold text-foreground">{rule.label}</p>
                <p className="text-xs text-muted-foreground">{rule.description}</p>
              </div>
              <Badge
                variant={crossposting[rule.key] ? "secondary" : "outline"}
                className={cn(
                  "border-border text-xs",
                  crossposting[rule.key] ? "bg-emerald-500/20 text-emerald-100" : "text-muted-foreground",
                )}
              >
                {crossposting[rule.key] ? "Enabled" : "Off"}
              </Badge>
            </div>
          ))}
        </CardContent>
      </Card>
      <Card className="lg:col-span-2 border-border/70 bg-background/60">
        <CardHeader>
          <CardTitle className="text-foreground">Tour cadence template</CardTitle>
          <CardDescription className="text-muted-foreground">
            Automation uses this blueprint for each tour date.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3 md:hidden">
            {(rules?.eventCadence ?? []).length ? (
              (rules?.eventCadence ?? []).map((entry) => (
                <div key={entry.id} className="rounded-xl border border-border/70 bg-background/40 p-4 text-sm text-foreground/80">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Offset</p>
                  <p className="font-semibold text-foreground">{entry.dayOffset} days</p>
                  <p className="mt-2 text-xs uppercase tracking-wide text-muted-foreground">Platforms</p>
                  <p className="text-xs text-foreground/70">{entry.platforms.join(", ")}</p>
                  <p className="mt-2 text-xs uppercase tracking-wide text-muted-foreground">Tone</p>
                  <p className="text-xs text-muted-foreground">{toneLabels[entry.tone]}</p>
                </div>
              ))
            ) : (
              <p className="text-sm text-muted-foreground">No cadence rules configured.</p>
            )}
          </div>
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-left text-sm text-foreground/80">
              <thead className="text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="pb-2">Offset</th>
                  <th className="pb-2">Platforms</th>
                  <th className="pb-2">Tone</th>
                </tr>
              </thead>
              <tbody>
                {(rules?.eventCadence ?? []).map((entry) => (
                  <tr key={entry.id} className="border-t border-border/60">
                    <td className="py-2">{entry.dayOffset} days</td>
                    <td className="py-2 text-foreground/70">{entry.platforms.join(", ")}</td>
                    <td className="py-2 text-muted-foreground">{toneLabels[entry.tone]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
      <Card className="lg:col-span-2 border-border/70 bg-background/60">
        <CardHeader>
          <CardTitle className="text-foreground">Auto-editor</CardTitle>
          <CardDescription className="text-muted-foreground">
            Current batch size and enhancements for clip prep.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-4 text-sm text-foreground/80">
          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Clips per batch</p>
            <p className="text-2xl font-semibold text-foreground">{rules?.autoEditor?.clipsPerBatch ?? 0}</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Auto captions</p>
            <p className="text-base">{rules?.autoEditor?.autoCaptions ? "Enabled" : "Disabled"}</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Auto thumbnails</p>
            <p className="text-base">{rules?.autoEditor?.autoThumbnails ? "Enabled" : "Disabled"}</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

type TemplateBuilderViewProps = {
  onSendToComposer: (options?: Partial<ComposerState>) => void;
};

const TEMPLATE_IDEAS = [
  { id: "tour-drop", title: "Tour drop", summary: "City-specific CTA, bold typography", accent: "from-rose-500 to-primary/60" },
  { id: "merch", title: "Merch feature", summary: "Lifestyle photo with lower-third overlay", accent: "from-emerald-500 to-lime-300" },
  { id: "announce", title: "Release announcement", summary: "Hero art with countdown sticker", accent: "from-sky-500 to-cyan-300" },
  { id: "recap", title: "Show recap", summary: "Collage grid with stats callouts", accent: "from-fuchsia-500 to-purple-400" },
];

function TemplateBuilderView({ onSendToComposer }: TemplateBuilderViewProps) {
  return (
    <div className="space-y-6">
      <Card className="border-border/70 bg-background/60">
        <CardHeader>
          <CardTitle className="text-foreground">Template ideas</CardTitle>
          <CardDescription className="text-muted-foreground">
            Quick-start layouts that send into the composer for final tweaks.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2">
          {TEMPLATE_IDEAS.map((idea) => (
            <div key={idea.id} className="rounded-2xl border border-border/70 bg-background/30 p-4">
              <div className={`h-32 rounded-xl bg-gradient-to-br ${idea.accent} mb-4`} />
              <p className="text-sm font-semibold text-foreground">{idea.title}</p>
              <p className="text-xs text-muted-foreground">{idea.summary}</p>
              <Button
                variant="ghost"
                size="sm"
                className="mt-3 h-8 px-2 text-foreground"
                onClick={() =>
                  onSendToComposer({
                    initialAsset: {
                      id: idea.id,
                      type: "template",
                      title: idea.title,
                      url: "",
                    },
                  })
                }
              >
                Use in composer
              </Button>
            </div>
          ))}
        </CardContent>
      </Card>
      <Card className="border-border/70 bg-background/60">
        <CardHeader>
          <CardTitle className="text-foreground">Team library</CardTitle>
          <CardDescription className="text-muted-foreground">Load an existing Mixboard template.</CardDescription>
        </CardHeader>
        <CardContent>
          <MixboardTemplatePicker />
        </CardContent>
      </Card>
    </div>
  );
}

type ReplayEngineViewProps = {
  posts: ReplaySourcePost[];
  loading: boolean;
  onReplayClick: (post: ReplaySourcePost) => void;
};

function ReplayEngineView({ posts, loading, onReplayClick }: ReplayEngineViewProps) {
  return (
    <Card className="border-border/70 bg-background/60">
      <CardHeader>
        <CardTitle className="text-foreground">Replay engine</CardTitle>
        <CardDescription className="text-muted-foreground">
          Resurface high performing content and spin up a replay.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {loading && <p className="text-xs text-muted-foreground">Fetching replay sources…</p>}
        {posts.length === 0 ? (
          <p className="text-sm text-muted-foreground">No replay sources yet.</p>
        ) : (
          posts.slice(0, 6).map((post) => (
            <div key={post.id} className="flex flex-wrap items-center gap-3 rounded-2xl border border-border/70 bg-background/30 p-4">
              <div className="flex-1">
                <p className="text-sm font-semibold text-foreground">{post.title}</p>
                <p className="text-xs text-muted-foreground">
                  {post.platform} • {new Date(post.postedAt).toLocaleDateString()}
                </p>
                <p className="text-xs text-muted-foreground">Engagement score {post.engagementScore}</p>
              </div>
              <Button
                variant="secondary"
                className="bg-emerald-500/20 text-emerald-100 hover:bg-emerald-500/30"
                onClick={() => onReplayClick(post)}
              >
                Generate replay
              </Button>
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
}

type ClipsViewProps = {
  tasks: MarketingTask[];
  refreshTasks: KeyedMutator<MarketingTask[]>;
  onOpenComposer: (options?: Partial<ComposerState>) => void;
};

function ClipsView({ tasks, refreshTasks, onOpenComposer }: ClipsViewProps) {
  const clipTasks = tasks.filter((task) => task.type === "clip");
  return (
    <Card className="border-border/70 bg-background/60">
      <CardHeader className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <CardTitle className="text-foreground">Clip pipeline</CardTitle>
          <CardDescription className="text-muted-foreground">Auto-editor queue and manual clips.</CardDescription>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="border-border/80 text-foreground" onClick={() => void refreshTasks()}>
            Refresh
          </Button>
          <Button variant="secondary" className="bg-emerald-500/20 text-emerald-100" onClick={() => onOpenComposer()}>
            New clip
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {clipTasks.length === 0 ? (
          <p className="text-sm text-muted-foreground">No clip work right now.</p>
        ) : (
          clipTasks.map((task) => (
            <div key={task.id} className="rounded-2xl border border-border/70 bg-background/30 p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-foreground">{task.title}</p>
                  {task.description ? <p className="text-xs text-muted-foreground">{task.description}</p> : null}
                </div>
                <Badge variant="outline" className="border-border text-foreground/80">
                  {task.status}
                </Badge>
              </div>
              <div className="mt-3 flex flex-wrap gap-2 text-xs text-muted-foreground">
                {task.durationSeconds ? <span>{Math.round(task.durationSeconds / 60)} min</span> : null}
                {task.sourceFile ? <span>{task.sourceFile}</span> : null}
              </div>
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
}

type CampaignsViewProps = {
  tasks: MarketingTask[];
  nudges: MarketingNudge[];
  onApplyNudge: (nudgeId: string) => void | Promise<void>;
  onOpenComposer: (options?: Partial<ComposerState>) => void;
  permissions: MarketingPermissions;
  onRequestUpgrade: (message: string) => void;
};

function CampaignsView({ tasks, nudges, onApplyNudge, onOpenComposer, permissions, onRequestUpgrade }: CampaignsViewProps) {
  const grouped = useMemo(() => {
    const map = new Map<string, MarketingTask[]>();
    tasks.forEach((task) => {
      const key = task.campaignLabel ?? "Unassigned";
      const list = map.get(key) ?? [];
      list.push(task);
      map.set(key, list);
    });
    return Array.from(map.entries());
  }, [tasks]);

  const handleCreate = () => {
    if (!permissions.canManageCampaigns) {
      onRequestUpgrade("Campaign creation requires manager permissions.");
      return;
    }
    onOpenComposer({ initialTask: null });
  };

  return (
    <div className="space-y-6">
      <Card className="border-border/70 bg-background/60">
        <CardHeader className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <CardTitle className="text-foreground">Active campaigns</CardTitle>
            <CardDescription className="text-muted-foreground">
              Snapshot pulled from outstanding tasks.
            </CardDescription>
          </div>
          <Button variant="secondary" className="bg-emerald-500/20 text-emerald-100" onClick={handleCreate}>
            New campaign asset
          </Button>
        </CardHeader>
        <CardContent className="space-y-3">
          {grouped.length === 0 ? (
            <p className="text-sm text-muted-foreground">No campaign-linked tasks detected.</p>
          ) : (
            grouped.map(([label, entries]) => (
              <div key={label} className="rounded-2xl border border-border/70 bg-background/30 p-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-foreground">{label}</p>
                  <Badge variant="outline" className="border-border text-foreground/80">
                    {entries.length} tasks
                  </Badge>
                </div>
                <ul className="mt-3 space-y-2 text-sm text-foreground/70">
                  {entries.slice(0, 4).map((task) => (
                    <li key={task.id} className="flex items-center justify-between text-xs text-muted-foreground">
                      <span className="truncate">{task.title}</span>
                      <span>{task.status}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))
          )}
        </CardContent>
      </Card>
      <Card className="border-border/70 bg-background/60">
        <CardHeader>
          <CardTitle className="text-foreground">Campaign nudges</CardTitle>
          <CardDescription className="text-muted-foreground">AI flagged adjustments.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {nudges.length === 0 ? (
            <p className="text-sm text-muted-foreground">No nudges right now.</p>
          ) : (
            nudges.map((nudge) => (
              <div key={nudge.id} className="rounded-xl border border-border/70 bg-background/30 p-3">
                <p className="text-sm font-semibold text-foreground">{nudge.message}</p>
                {nudge.recommendedAction ? (
                  <p className="text-xs text-muted-foreground">{nudge.recommendedAction}</p>
                ) : null}
                <Button
                  variant="ghost"
                  size="sm"
                  className="mt-2 h-8 px-2 text-foreground"
                  onClick={() => void onApplyNudge(nudge.id)}
                >
                  Apply
                </Button>
              </div>
            ))
          )}
        </CardContent>
      </Card>
    </div>
  );
}

type PermissionsViewProps = {
  permissions: MarketingPermissions;
  onUpgrade: (reason: string) => void;
};

function PermissionsView({ permissions, onUpgrade }: PermissionsViewProps) {
  const matrix = [
    { label: "Schedule posts", enabled: permissions.canSchedule },
    { label: "Publish instantly", enabled: permissions.canPublishNow },
    { label: "Edit asset library", enabled: permissions.canEditAssets },
    { label: "Manage campaigns", enabled: permissions.canManageCampaigns },
    { label: "Configure automation", enabled: permissions.canUseAutomation },
  ];

  return (
    <Card className="border-border/70 bg-background/60">
      <CardHeader>
        <CardTitle className="text-foreground">Permissions</CardTitle>
        <CardDescription className="text-muted-foreground">Feature gates detected for this seat.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {matrix.map((entry) => (
          <div
            key={entry.label}
            className="flex items-center justify-between rounded-xl border border-border/70 bg-background/30 p-3"
          >
            <p className="text-sm text-foreground">{entry.label}</p>
            <Badge
              variant={entry.enabled ? "secondary" : "outline"}
              className={cn(
                "border-border text-xs",
                entry.enabled ? "bg-emerald-500/20 text-emerald-100" : "text-muted-foreground",
              )}
            >
              {entry.enabled ? "Enabled" : "Request"}
            </Badge>
          </div>
        ))}
        <div>
          <p className="text-xs uppercase tracking-wide text-muted-foreground">Platform connectors</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {Object.entries(permissions.platform).map(([platform, enabled]) => (
              <Badge
                key={platform}
                variant={enabled ? "secondary" : "outline"}
                className={cn(
                  "border-border text-xs",
                  enabled ? "bg-sky-500/20 text-sky-100" : "text-muted-foreground",
                )}
              >
                {platform}
              </Badge>
            ))}
          </div>
        </div>
        <Button
          variant="secondary"
          className="w-full bg-primary/15 text-foreground hover:bg-primary/25"
          onClick={() => onUpgrade("Upgrade to unlock marketing automation.")}
        >
          Request upgrade
        </Button>
      </CardContent>
    </Card>
  );
}

type WorkflowFormState = {
  id?: string;
  name: string;
  description: string;
  trigger: AutomationWorkflow["trigger"] | "";
  conditions: AutomationWorkflow["conditions"];
  actions: AutomationWorkflow["actions"];
  enabled: boolean;
};

type WorkflowOption<T> = {
  value: T;
  label: string;
  description: string;
  icon: LucideIcon;
};

const WORKFLOW_TRIGGER_OPTIONS: WorkflowOption<AutomationWorkflow["trigger"]>[] = [
  { value: "clip_processed", label: "New clip processed", description: "Whenever a fresh clip is ready coming out of the editor.", icon: Clapperboard },
  { value: "event_promo_due", label: "Event hits cadence", description: "Fire when a tour date reaches its promo window.", icon: CalendarDays },
  { value: "scheduled_post_published", label: "Post published", description: "React right after a scheduled post goes live.", icon: PenSquare },
  { value: "engagement_spike", label: "Engagement spike", description: "Jump on sudden activity bursts.", icon: TrendingUp },
  { value: "new_follower_burst", label: "Follower burst", description: "Scale outreach when growth surges.", icon: UserPlus },
  { value: "campaign_milestone", label: "Campaign milestone", description: "Mark key campaign checkpoints with automations.", icon: Target },
];

const WORKFLOW_CONDITION_OPTIONS: WorkflowOption<AutomationWorkflow["conditions"][number]>[] = [
  { value: "platform_allowed", label: "Platform allowed", description: "Confirm platform tokens + permissions are valid.", icon: Server },
  { value: "engagement_below_x", label: "Engagement below threshold", description: "Only run if engagement is dipping.", icon: RotateCcw },
  { value: "engagement_above_x", label: "Engagement above threshold", description: "Double down when posts are surging.", icon: TrendingUp },
  { value: "trend_hot", label: "Trend is hot", description: "Checks Trend Miner before moving.", icon: Lightbulb },
  { value: "time_window", label: "In automation window", description: "Respect posting windows from Automation Rules.", icon: CalendarDays },
];

const WORKFLOW_ACTION_OPTIONS: WorkflowOption<AutomationWorkflow["actions"][number]>[] = [
  { value: "generate_clip", label: "Generate clip", description: "Kick off clip generation pipeline.", icon: Clapperboard },
  { value: "generate_caption", label: "Generate caption", description: "Draft AI caption variants using tone presets.", icon: MessageSquare },
  { value: "create_draft_post", label: "Create draft post", description: "Open a ready-to-review composer draft.", icon: PlaySquare },
  { value: "schedule_post", label: "Schedule post", description: "Push directly into the scheduler engine.", icon: CalendarDays },
  { value: "send_notification", label: "Send notification", description: "Alert humans in Notifications feed.", icon: Bell },
  { value: "apply_growth_strategy", label: "Apply growth strategy", description: "Sync with Growth Planner objectives.", icon: Target },
];

const DEFAULT_WORKFLOW_FORM: WorkflowFormState = {
  name: "",
  description: "",
  trigger: "",
  conditions: [],
  actions: [],
  enabled: true,
};

type AutomationWorkflowViewProps = {
  workflows: AutomationWorkflow[];
  loading: boolean;
  mutateWorkflows: KeyedMutator<AutomationWorkflow[]>;
};

function AutomationWorkflowView({ workflows, loading, mutateWorkflows }: AutomationWorkflowViewProps) {
  const { push } = useToast();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [wizardStep, setWizardStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [formState, setFormState] = useState<WorkflowFormState>(DEFAULT_WORKFLOW_FORM);
  const stepLabels = ["Trigger", "Conditions", "Actions"];

  const triggerLabelMap = useMemo(
    () => Object.fromEntries(WORKFLOW_TRIGGER_OPTIONS.map((option) => [option.value, option.label])),
    [],
  );
  const conditionLabelMap = useMemo(
    () => Object.fromEntries(WORKFLOW_CONDITION_OPTIONS.map((option) => [option.value, option.label])),
    [],
  );
  const actionLabelMap = useMemo(
    () => Object.fromEntries(WORKFLOW_ACTION_OPTIONS.map((option) => [option.value, option.label])),
    [],
  );

  const resetForm = () => {
    setFormState({ ...DEFAULT_WORKFLOW_FORM });
    setWizardStep(0);
  };

  const openDrawer = (workflow?: AutomationWorkflow) => {
    if (workflow) {
      setFormState({
        id: workflow.id,
        name: workflow.name,
        description: workflow.description ?? "",
        trigger: workflow.trigger,
        conditions: workflow.conditions,
        actions: workflow.actions,
        enabled: workflow.enabled,
      });
    } else {
      resetForm();
    }
    setWizardStep(0);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    resetForm();
  };

  const toggleCondition = (value: AutomationWorkflow["conditions"][number]) => {
    setFormState((prev) => {
      const exists = prev.conditions.includes(value);
      return {
        ...prev,
        conditions: exists ? prev.conditions.filter((condition) => condition !== value) : [...prev.conditions, value],
      };
    });
  };

  const toggleAction = (value: AutomationWorkflow["actions"][number]) => {
    setFormState((prev) => {
      const exists = prev.actions.includes(value);
      return {
        ...prev,
        actions: exists ? prev.actions.filter((action) => action !== value) : [...prev.actions, value],
      };
    });
  };

  const handleSave = async () => {
    if (!formState.name.trim()) {
      push({ title: "Name required", message: "Give this automation a label.", variant: "destructive" });
      return;
    }
    if (!formState.trigger) {
      push({ title: "Trigger missing", message: "Select when this automation fires.", variant: "destructive" });
      return;
    }
    if (formState.actions.length === 0) {
      push({ title: "No actions", message: "Pick at least one action.", variant: "destructive" });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: formState.name,
        description: formState.description,
        trigger: formState.trigger,
        conditions: formState.conditions,
        actions: formState.actions,
        enabled: formState.enabled,
      };
      if (formState.id) {
        await api.patch(`/marketing/automations/${formState.id}`, payload);
        push({ title: "Automation updated", message: "Workflow saved." });
      } else {
        await post("/marketing/automations", payload);
        push({ title: "Automation created", message: "Workflow added to your stack." });
      }
      await mutateWorkflows();
      closeDrawer();
    } catch (error) {
      push({
        title: "Unable to save automation",
        message: error instanceof Error ? error.message : "Please try again.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (workflow: AutomationWorkflow) => {
    const confirmDelete =
      typeof window !== "undefined"
        ? window.confirm(`Delete automation "${workflow.name}"? This cannot be undone.`)
        : true;
    if (!confirmDelete) return;
    try {
      await api.delete(`/marketing/automations/${workflow.id}`);
      push({ title: "Automation deleted", message: "Workflow removed." });
      await mutateWorkflows();
    } catch (error) {
      push({
        title: "Unable to delete automation",
        message: error instanceof Error ? error.message : "Please try again later.",
        variant: "destructive",
      });
    }
  };

  const handleToggleWorkflow = async (workflow: AutomationWorkflow) => {
    try {
      await api.patch(`/marketing/automations/${workflow.id}`, { enabled: !workflow.enabled });
      push({
        title: workflow.enabled ? "Automation paused" : "Automation enabled",
        message: workflow.enabled ? "Workflow will stop running." : "Workflow now watching triggers.",
      });
      await mutateWorkflows();
    } catch (error) {
      push({
        title: "Unable to update automation",
        message: error instanceof Error ? error.message : "Try again shortly.",
        variant: "destructive",
      });
    }
  };

  const sortedWorkflows = useMemo(
    () => [...workflows].sort((a, b) => Number(b.enabled) - Number(a.enabled)),
    [workflows],
  );

  const canAdvance = wizardStep === 0 ? Boolean(formState.trigger) : true;
  const isFinalStep = wizardStep === stepLabels.length - 1;

  const renderStepContent = () => {
    switch (wizardStep) {
      case 0:
        return (
          <div className="grid gap-3 md:grid-cols-2">
            {WORKFLOW_TRIGGER_OPTIONS.map((option) => {
              const Icon = option.icon;
              const selected = formState.trigger === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setFormState((prev) => ({ ...prev, trigger: option.value }))}
                  className={cn(
                    "flex items-start gap-3 rounded-2xl border p-4 text-left transition",
                    selected ? "border-primary/70 bg-primary/15 text-foreground" : "border-border/70 bg-background/60 text-foreground/70",
                  )}
                >
                  <div className="rounded-xl border border-border/80 bg-background/50 p-2">
                    <Icon className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="font-semibold">{option.label}</p>
                    <p className="text-sm text-muted-foreground">{option.description}</p>
                  </div>
                </button>
              );
            })}
          </div>
        );
      case 1:
        return (
          <div className="space-y-3">
            {WORKFLOW_CONDITION_OPTIONS.map((option) => {
              const Icon = option.icon;
              return (
                <label
                  key={option.value}
                  className="flex items-start gap-3 rounded-2xl border border-border/70 bg-background/40 p-3"
                >
                  <Checkbox checked={formState.conditions.includes(option.value)} onCheckedChange={() => toggleCondition(option.value)} />
                  <div>
                    <div className="flex items-center gap-2 text-foreground">
                      <Icon className="h-4 w-4 text-primary" />
                      <span className="font-medium">{option.label}</span>
                    </div>
                    <p className="text-sm text-muted-foreground">{option.description}</p>
                  </div>
                </label>
              );
            })}
          </div>
        );
      case 2:
      default:
        return (
          <div className="space-y-3">
            {WORKFLOW_ACTION_OPTIONS.map((option) => {
              const Icon = option.icon;
              const active = formState.actions.includes(option.value);
              return (
                <label
                  key={option.value}
                  className={cn(
                    "flex items-start gap-3 rounded-2xl border border-border/70 p-3",
                    active ? "bg-emerald-500/10" : "bg-background/40",
                  )}
                >
                  <Checkbox checked={active} onCheckedChange={() => toggleAction(option.value)} />
                  <div>
                    <div className="flex items-center gap-2 text-foreground">
                      <Icon className="h-4 w-4 text-emerald-300" />
                      <span className="font-semibold">{option.label}</span>
                    </div>
                    <p className="text-sm text-muted-foreground">{option.description}</p>
                  </div>
                </label>
              );
            })}
          </div>
        );
    }
  };

  return (
    <section className="space-y-4 rounded-2xl border border-border/80 bg-background/30 p-4">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-wide text-muted-foreground">Automation workflows</p>
          <h3 className="text-xl font-semibold text-foreground">Visual builder</h3>
          <p className="text-sm text-muted-foreground">Chain triggers → conditions → actions for end-to-end automation.</p>
        </div>
        <Button onClick={() => openDrawer()} className="bg-primary/90 text-foreground hover:bg-primary/80">
          Create automation
        </Button>
      </div>
      {loading ? (
        <div className="rounded-2xl border border-dashed border-border/70 p-6 text-sm text-muted-foreground">Loading workflows…</div>
      ) : sortedWorkflows.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border/60 bg-background/30 p-6 text-sm text-muted-foreground">
          <p className="text-base text-foreground">No automations yet</p>
          <p className="text-muted-foreground">Start by creating your first workflow.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {sortedWorkflows.map((workflow) => (
            <Card key={workflow.id} className="border-border/70 bg-background/40 p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <h4 className="text-lg font-semibold text-foreground">{workflow.name}</h4>
                    <Badge variant={workflow.enabled ? "default" : "outline"} className="text-xs">
                      {workflow.enabled ? "Active" : "Paused"}
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">{workflow.description || "Runs automatically on its trigger."}</p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right text-xs text-muted-foreground">
                    <p>Trigger</p>
                    <p className="font-semibold text-foreground">{triggerLabelMap[workflow.trigger]}</p>
                  </div>
                  <Switch checked={workflow.enabled} onCheckedChange={() => handleToggleWorkflow(workflow)} />
                </div>
              </div>
              <div className="mt-4 grid gap-3 md:grid-cols-3">
                <div>
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Conditions</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {workflow.conditions.length === 0 ? (
                      <Badge variant="outline" className="border-dashed border-border text-muted-foreground">
                        None
                      </Badge>
                    ) : (
                      workflow.conditions.map((condition) => (
                        <Badge key={condition} variant="outline" className="border-border bg-background/30 text-xs text-foreground/80">
                          {conditionLabelMap[condition]}
                        </Badge>
                      ))
                    )}
                  </div>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Actions</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {workflow.actions.map((action) => (
                      <Badge key={action} className="bg-emerald-500/10 text-emerald-200">
                        {actionLabelMap[action]}
                      </Badge>
                    ))}
                  </div>
                </div>
                <div className="flex items-end justify-end gap-2">
                  <Button size="sm" variant="secondary" onClick={() => openDrawer(workflow)}>
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-rose-400 hover:text-rose-200"
                    onClick={() => handleDelete(workflow)}
                  >
                    Delete
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
      <Sheet open={drawerOpen} onOpenChange={(open) => (!open ? closeDrawer() : setDrawerOpen(true))}>
        <SheetContent className="flex w-full max-w-3xl flex-col gap-5 overflow-y-auto border-l border-border/70 bg-background">
          <SheetHeader>
            <SheetTitle className="text-2xl text-foreground">{formState.id ? "Edit automation" : "Create automation"}</SheetTitle>
          </SheetHeader>
          <div className="space-y-4">
            <label className="text-xs uppercase tracking-wide text-muted-foreground">
              Name
              <Input
                value={formState.name}
                onChange={(event) => setFormState((prev) => ({ ...prev, name: event.target.value }))}
                className="mt-1 bg-background/40 text-foreground"
                placeholder="Clips → Schedule morning draft"
              />
            </label>
            <label className="text-xs uppercase tracking-wide text-muted-foreground">
              Description
              <Textarea
                value={formState.description}
                onChange={(event) => setFormState((prev) => ({ ...prev, description: event.target.value }))}
                rows={3}
                className="mt-1 bg-background/40 text-foreground"
                placeholder="Optional notes for the team."
              />
            </label>
            <div className="flex items-center justify-between rounded-xl border border-border/70 bg-background/40 p-3">
              <div>
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Status</p>
                <p className="text-sm text-foreground">{formState.enabled ? "Active" : "Paused"}</p>
              </div>
              <Switch checked={formState.enabled} onCheckedChange={(checked) => setFormState((prev) => ({ ...prev, enabled: Boolean(checked) }))} />
            </div>
            <div className="space-y-3 rounded-2xl border border-border/70 bg-background/40 p-4">
              <div className="flex flex-wrap items-center gap-3">
                {stepLabels.map((label, index) => (
                  <div
                    key={label}
                    className={cn(
                      "flex items-center gap-2 text-sm",
                      index === wizardStep ? "text-foreground" : "text-muted-foreground",
                    )}
                  >
                    <span
                      className={cn(
                        "flex h-7 w-7 items-center justify-center rounded-full border text-xs",
                        index <= wizardStep ? "border-primary/60 text-foreground" : "border-border text-muted-foreground",
                      )}
                    >
                      {index + 1}
                    </span>
                    {label}
                  </div>
                ))}
              </div>
              {renderStepContent()}
            </div>
          </div>
          <div className="mt-auto flex items-center justify-between border-t border-border/70 pt-4">
            <Button variant="ghost" onClick={() => (wizardStep === 0 ? closeDrawer() : setWizardStep((prev) => prev - 1))}>
              {wizardStep === 0 ? "Cancel" : "Back"}
            </Button>
            {isFinalStep ? (
              <Button onClick={handleSave} disabled={saving}>
                {saving ? "Saving…" : formState.id ? "Save automation" : "Create automation"}
              </Button>
            ) : (
              <Button onClick={() => setWizardStep((prev) => Math.min(prev + 1, stepLabels.length - 1))} disabled={!canAdvance}>
                Next
              </Button>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </section>
  );
}


function EngagementView({
  items,
  signals,
  onRespond,
  onCompose,
  onAutoReply,
  onBoost,
}: {
  items: EngagementItem[];
  signals: EngagementSignal[];
  onRespond: (item: EngagementItem) => void;
  onCompose: (options?: Partial<ComposerState>) => void;
  onAutoReply: (item: EngagementItem) => void;
  onBoost?: (item: EngagementItem) => void;
}) {
  const [tab, setTab] = useState<EngagementTab>("all");
  const tabFilters: { id: EngagementTab; label: string }[] = [
    { id: "all", label: "All" },
    { id: "comments", label: "Comments" },
    { id: "dms", label: "DMs" },
    { id: "mentions", label: "Mentions" },
    { id: "signals", label: "Signals" },
  ];

  const filteredItems = useMemo(() => {
    switch (tab) {
      case "comments":
        return items.filter((item) => item.type === "comment" || item.type === "reply");
      case "dms":
        return items.filter((item) => item.type === "dm");
      case "mentions":
        return items.filter((item) => item.type === "mention");
      default:
        return items;
    }
  }, [items, tab]);

  const stats = {
    comments: items.filter((item) => item.type === "comment" || item.type === "reply").length,
    dms: items.filter((item) => item.type === "dm").length,
    mentions: items.filter((item) => item.type === "mention").length,
    signals: signals.length,
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold text-foreground">Community engagement</h2>
          <p className="text-sm text-muted-foreground">DM replies, mentions, and comments surfaced by the agent.</p>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        <Card className="border-border/80 bg-background/40 p-4">
          <p className="text-xs uppercase text-muted-foreground">Comments</p>
          <p className="mt-1 text-2xl font-semibold text-foreground">{stats.comments}</p>
        </Card>
        <Card className="border-border/80 bg-background/40 p-4">
          <p className="text-xs uppercase text-muted-foreground">DMs</p>
          <p className="mt-1 text-2xl font-semibold text-foreground">{stats.dms}</p>
        </Card>
        <Card className="border-border/80 bg-background/40 p-4">
          <p className="text-xs uppercase text-muted-foreground">Mentions</p>
          <p className="mt-1 text-2xl font-semibold text-foreground">{stats.mentions}</p>
        </Card>
        <Card className="border-border/80 bg-background/40 p-4">
          <p className="text-xs uppercase text-muted-foreground">Signals</p>
          <p className="mt-1 text-2xl font-semibold text-foreground">{stats.signals}</p>
        </Card>
      </div>

      <Tabs value={tab} onValueChange={(value) => setTab(value as EngagementTab)}>
        <TabsList>
          {tabFilters.map((entry) => (
            <TabsTrigger key={entry.id} value={entry.id}>
              {entry.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div className="space-y-4">
        {filteredItems.length ? (
          filteredItems.map((item) => (
            <Card key={item.id} className="border-border/80 bg-background/40 p-4">
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex-1">
                  <p className="text-sm font-semibold text-foreground">@{item.username}</p>
                  <p className="text-xs text-muted-foreground">{formatRelativeTime(item.createdAt)}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {item.type === "dm" ? (
                    <Button size="sm" onClick={() => onRespond(item)}>
                      Respond
                    </Button>
                  ) : (
                    <Button size="sm" onClick={() => onCompose({})}>
                      Open Composer
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => onAutoReply(item)}>
                    Auto-Reply
                  </Button>
                  {item.postId && onBoost ? (
                    <Button size="sm" variant="outline" onClick={() => onBoost(item)}>
                      Boost
                    </Button>
                  ) : null}
                </div>
              </div>
              <p className="mt-3 text-sm text-foreground/80">{item.message}</p>
              {item.postPreviewUrl ? (
                <img src={item.postPreviewUrl} alt="" className="mt-3 h-32 w-full rounded-xl border border-border/70 object-cover" />
              ) : null}
            </Card>
          ))
        ) : (
          <EmptyState label="No engagement items in this filter." />
        )}
      </div>

      <Card className="border-border/80 bg-background/40 p-4">
        <p className="text-sm font-semibold text-foreground">Signals</p>
        <div className="mt-3 space-y-3">
          {signals.length ? (
            signals.map((signal) => (
              <div key={signal.id} className="rounded-xl border border-border/70 bg-background/30 p-3">
                <p className="text-sm font-semibold text-foreground">{signal.metric ?? "Engagement"}</p>
                <p className="text-xs text-muted-foreground">{signal.message}</p>
                <p className="text-[10px] uppercase text-muted-foreground">{formatRelativeTime(signal.createdAt)}</p>
              </div>
            ))
          ) : (
            <p className="text-xs text-muted-foreground">No signals.</p>
          )}
        </div>
      </Card>
    </div>
  );
}

function AnalyticsView({
  timeseries,
  audience,
  platforms,
  cohorts,
  performance,
  streamingInsights,
  streamingLoading,
}: {
  timeseries: AnalyticsTimeseriesPoint[];
  audience: AudienceAnalytics;
  platforms: PlatformAnalytics;
  cohorts: Array<{ cohort: string; posts: number; avgEngagement: number; conversion: number }>;
  performance: PerformanceAnalytics;
  streamingInsights: StreamingInsightsPayload | null;
  streamingLoading?: boolean;
}) {
  const [tab, setTab] = useState("overview");
  const engagementLineData = useMemo(
    () => ({
      labels: timeseries.map((point) => point.date),
      datasets: [
        {
          label: "Engagement",
          data: timeseries.map((point) => point.engagement),
          borderColor: "#34d399",
          backgroundColor: "rgba(52, 211, 153, 0.2)",
          tension: 0.4,
        },
      ],
    }),
    [timeseries],
  );

  const followerBarData = useMemo(
    () => ({
      labels: timeseries.map((point) => point.date),
      datasets: [
        {
          label: "New followers",
          data: timeseries.map((point) => point.followers),
          backgroundColor: "rgba(248, 250, 252, 0.7)",
        },
      ],
    }),
    [timeseries],
  );

  const platformPieData = useMemo(() => {
    const entries = Object.entries(platforms.distribution);
    return {
      labels: entries.map(([name]) => name),
      datasets: [
        {
          data: entries.map(([, value]) => value),
          backgroundColor: ["#f97316", "#38bdf8", "#a855f7", "#4ade80", "#facc15"],
        },
      ],
    };
  }, [platforms.distribution]);

  const scatterData = useMemo(
    () => ({
      datasets: [
        {
          label: "Posts",
          data: performance.scatter.map((point) => ({ x: point.reach, y: point.engagement })),
          pointBackgroundColor: "#38bdf8",
          pointBorderColor: "#38bdf8",
          showLine: false,
        },
      ],
    }),
    [performance.scatter],
  );

  const demographicsBlocks = [
    { title: "Gender", data: audience.demographics.genders },
    { title: "Age Groups", data: audience.demographics.ages },
    { title: "Regions", data: audience.demographics.regions },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold text-foreground">Analytics Intelligence</h2>
          <p className="text-sm text-muted-foreground">Deep-dive across engagement, audience, and conversion matricies.</p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="engagement">Engagement</TabsTrigger>
          <TabsTrigger value="audience">Audience</TabsTrigger>
          <TabsTrigger value="performance">Content Performance</TabsTrigger>
          <TabsTrigger value="platforms">Platforms</TabsTrigger>
          <TabsTrigger value="cohorts">Cohorts</TabsTrigger>
        </TabsList>
        <TabsContent value="overview">
          <div className="grid gap-4 lg:grid-cols-2">
            <Card className="border-border/80 bg-background/40 p-4">
              <p className="text-sm font-semibold text-foreground">Engagement over time</p>
              <Line data={engagementLineData} options={{ plugins: { legend: { display: false } } }} />
            </Card>
            <Card className="border-border/80 bg-background/40 p-4">
              <p className="text-sm font-semibold text-foreground">New followers per day</p>
              <Bar data={followerBarData} options={{ plugins: { legend: { display: false } } }} />
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="engagement">
          <div className="grid gap-4 lg:grid-cols-2">
            <Card className="border-border/80 bg-background/40 p-4">
              <p className="text-sm font-semibold text-foreground">Best posting times</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {audience.bestTimes.length ? (
                  audience.bestTimes.map((slot) => (
                    <Badge key={slot} className="bg-emerald-400/10 text-emerald-100">
                      {slot}
                    </Badge>
                  ))
                ) : (
                  <p className="text-xs text-muted-foreground">No recommendation data</p>
                )}
              </div>
            </Card>
            <Card className="border-border/80 bg-background/40 p-4">
              <p className="text-sm font-semibold text-foreground">Conversion tracking</p>
              <div className="mt-3 space-y-2 text-sm text-foreground/70">
                {performance.conversions.length ? (
                  performance.conversions.map((row) => (
                    <div key={row.channel} className="flex items-center justify-between">
                      <span>{row.channel}</span>
                      <span className="text-xs text-muted-foreground">{row.clicks} clicks · {row.ctr}% CTR</span>
                    </div>
                  ))
                ) : (
                  <p className="text-xs text-muted-foreground">No conversion data.</p>
                )}
              </div>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="audience">
          <div className="grid gap-4 lg:grid-cols-3">
            {demographicsBlocks.map((block) => (
              <Card key={block.title} className="border-border/80 bg-background/40 p-4">
                <p className="text-sm font-semibold text-foreground">{block.title}</p>
                <div className="mt-3 space-y-1 text-xs text-foreground/70">
                  {Object.keys(block.data).length ? (
                    Object.entries(block.data).map(([key, value]) => (
                      <div key={key} className="flex items-center justify-between">
                        <span>{key}</span>
                        <span>{value}%</span>
                      </div>
                    ))
                  ) : (
                    <p className="text-xs text-muted-foreground">Not enough data.</p>
                  )}
                </div>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="platforms">
          <div className="grid gap-4 lg:grid-cols-2">
            <Card className="border-border/80 bg-background/40 p-4">
              <p className="text-sm font-semibold text-foreground">Platform distribution</p>
              <Pie data={platformPieData} />
            </Card>
            <Card className="border-border/80 bg-background/40 p-4">
              <p className="text-sm font-semibold text-foreground">Platform comparison</p>
              <div className="mt-3 overflow-x-auto">
                <table className="w-full text-left text-xs text-foreground/70">
                  <thead>
                    <tr className="text-muted-foreground">
                      <th className="py-2">Platform</th>
                      <th className="py-2">Engagement</th>
                      <th className="py-2">Reach</th>
                    </tr>
                  </thead>
                  <tbody>
                    {platforms.comparison.map((row) => (
                      <tr key={row.platform} className="border-t border-border/70">
                        <td className="py-2">{row.platform}</td>
                        <td className="py-2">{row.engagement.toLocaleString()}</td>
                        <td className="py-2">{row.reach.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="performance">
          <div className="grid gap-4 lg:grid-cols-2">
            <Card className="border-border/80 bg-background/40 p-4">
              <p className="text-sm font-semibold text-foreground">Post performance (Reach vs Engagement)</p>
              <Line
                data={scatterData}
                options={{
                  scales: { x: { title: { display: true, text: "Reach" } }, y: { title: { display: true, text: "Engagement" } } },
                  plugins: { legend: { display: false } },
                }}
              />
            </Card>
            <Card className="border-border/80 bg-background/40 p-4">
              <p className="text-sm font-semibold text-foreground">Conversion tracking</p>
              <div className="mt-3 overflow-x-auto">
                <table className="w-full text-left text-xs text-foreground/70">
                  <thead>
                    <tr className="text-muted-foreground">
                      <th className="py-2">Channel</th>
                      <th className="py-2">Clicks</th>
                      <th className="py-2">CTR</th>
                    </tr>
                  </thead>
                  <tbody>
                    {performance.conversions.map((row) => (
                      <tr key={row.channel} className="border-t border-border/70">
                        <td className="py-2">{row.channel}</td>
                        <td className="py-2">{row.clicks.toLocaleString()}</td>
                        <td className="py-2">{row.ctr}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="cohorts">
          <Card className="border-border/80 bg-background/40 p-4">
            <p className="text-sm font-semibold text-foreground">Content cohorts</p>
            <div className="mt-3 overflow-x-auto">
              <table className="w-full text-left text-xs text-foreground/70">
                <thead>
                  <tr className="text-muted-foreground">
                    <th className="py-2">Cohort</th>
                    <th className="py-2">Posts</th>
                    <th className="py-2">Avg Engagement</th>
                    <th className="py-2">Conversion</th>
                  </tr>
                </thead>
                <tbody>
                  {cohorts.map((cohort) => (
                    <tr key={cohort.cohort} className="border-t border-border/70">
                      <td className="py-2">{cohort.cohort}</td>
                      <td className="py-2">{cohort.posts}</td>
                      <td className="py-2">{cohort.avgEngagement}</td>
                      <td className="py-2">{cohort.conversion}%</td>
                    </tr>
                  ))}
                  {!cohorts.length ? (
                    <tr>
                      <td colSpan={4} className="py-4 text-center text-muted-foreground">
                        No cohort data available.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      <StreamingInsightsPanel data={streamingInsights} loading={streamingLoading} />
    </div>
  );
}

function ConsistencyView({
  report,
  loading,
  onAnalyze,
  onFix,
}: {
  report: ConsistencyReport | null;
  loading: boolean;
  onAnalyze: () => Promise<void>;
  onFix: (option?: string) => Promise<void>;
}) {
  const scores = report ?? { visualScore: 0, toneScore: 0, platformAlignment: 0, issues: [], suggestions: [] };
  const issueExamples =
    report?.issues ?? ["Recent TikTok posts are more playful than IG.", "Colors deviate from brand palette."];
  const suggestionExamples =
    report?.suggestions ?? [
      "Regenerate captions in mystical tone.",
      "Normalize palette for next 10 posts.",
      "Match CTA style to previous tour campaign.",
    ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-semibold text-foreground">Consistency Report</h2>
          <p className="text-sm text-muted-foreground">Ensure tone, visuals, and messaging stay on brand across platforms.</p>
        </div>
        <Button onClick={() => void onAnalyze()} disabled={loading}>
          {loading ? "Analyzing…" : "Run analysis"}
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        {[
          { label: "Visual consistency", value: scores.visualScore },
          { label: "Tone consistency", value: scores.toneScore },
          { label: "Platform alignment", value: scores.platformAlignment },
        ].map((score) => (
          <Card key={score.label} className="border-border/80 bg-background/40 p-4">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">{score.label}</p>
            <p className="mt-2 text-3xl font-semibold text-foreground">{score.value ? `${score.value}%` : "–"}</p>
          </Card>
        ))}
      </div>

      <Card className="border-border/80 bg-background/40 p-4">
        <p className="text-sm font-semibold text-foreground">Inconsistencies</p>
        <div className="mt-3 space-y-2 text-sm text-foreground/70">
          {issueExamples.map((issue) => (
            <div key={issue} className="rounded-xl border border-border/70 bg-background/40 p-3">
              {issue}
            </div>
          ))}
        </div>
      </Card>

      <Card className="border-border/80 bg-background/40 p-4">
        <div className="flex items-center justify-between">
          <p className="text-sm font-semibold text-foreground">Suggested fixes</p>
          <Button variant="outline" onClick={() => void onFix()} disabled={loading}>
            Apply batch fix
          </Button>
        </div>
        <div className="mt-3 grid gap-3 md:grid-cols-2">
          {suggestionExamples.map((suggestion) => (
            <div key={suggestion} className="rounded-xl border border-border/70 bg-background/40 p-3">
              <p className="text-sm text-foreground">{suggestion}</p>
              <div className="mt-2 flex gap-2">
                <Button size="sm" variant="secondary" onClick={() => void onFix(suggestion)}>
                  Apply
                </Button>
                <Button size="sm" variant="ghost">
                  Customize
                </Button>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

function ExperimentsView({
  experiments,
  onStart,
  onSelect,
  selectedExperiment,
  onApplyWinner,
}: {
  experiments: Experiment[];
  onStart: () => void;
  onSelect: (id: string) => void;
  selectedExperiment: Experiment | null;
  onApplyWinner: (id: string) => Promise<void>;
}) {
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold text-foreground">Experiments</h2>
          <p className="text-sm text-muted-foreground">
            Run A/B or multivariate tests on captions, thumbnails, and posting windows.
          </p>
        </div>
        <Button onClick={onStart}>Start new experiment</Button>
      </div>
      <Card className="border-border/80 bg-background/40 p-4">
        <p className="text-sm font-semibold text-foreground">Active experiments</p>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-left text-sm text-foreground/80">
            <thead className="text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="py-2">Name</th>
                <th className="py-2">Metric</th>
                <th className="py-2">Variants</th>
                <th className="py-2">Status</th>
                <th className="py-2">Winner</th>
              </tr>
            </thead>
            <tbody>
              {experiments.length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-6 text-center text-sm text-muted-foreground">
                    No experiments running.
                  </td>
                </tr>
              ) : (
                experiments.map((experiment) => (
                  <tr
                    key={experiment.id}
                    className="cursor-pointer border-t border-border/70 hover:bg-background/30"
                    onClick={() => onSelect(experiment.id)}
                  >
                    <td className="py-3">{experiment.name}</td>
                    <td className="py-3 capitalize">{experiment.metric}</td>
                    <td className="py-3">{experiment.variants.length}</td>
                    <td className="py-3">
                      <Badge variant={experiment.status === "completed" ? "outline" : "default"}>
                        {experiment.status}
                      </Badge>
                    </td>
                    <td className="py-3">{experiment.winnerId ?? "–"}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>
      {selectedExperiment ? (
        <Card className="border-border/80 bg-background/40 p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-sm uppercase tracking-wide text-muted-foreground">Experiment</p>
              <p className="text-xl font-semibold text-foreground">{selectedExperiment.name}</p>
              <p className="text-xs text-muted-foreground">Metric: {selectedExperiment.metric}</p>
            </div>
            {selectedExperiment.status === "completed" && selectedExperiment.winnerId ? (
              <Button onClick={() => void onApplyWinner(selectedExperiment.id)}>Apply winner</Button>
            ) : null}
          </div>
          <div className="mt-4 grid gap-3 md:grid-cols-2">
            {selectedExperiment.variants.map((variant) => (
              <div key={variant.id} className="rounded-xl border border-border/70 bg-background/40 p-3">
                <p className="text-sm font-semibold text-foreground">Variant {variant.id}</p>
                {variant.caption ? (
                  <p className="mt-2 text-xs text-muted-foreground line-clamp-3">{variant.caption}</p>
                ) : null}
                <p className="mt-2 text-xs text-muted-foreground">
                  Scheduled {formatDate(variant.scheduledFor)} • Score{" "}
                  {typeof variant.performanceScore === "number" ? variant.performanceScore : "n/a"}
                </p>
              </div>
            ))}
          </div>
        </Card>
      ) : null}
    </div>
  );
}

function CompetitiveView({
  artists,
  trackedIds,
  onToggleArtist,
  performance,
  posts,
  strategy,
}: {
  artists: PeerArtist[];
  trackedIds: string[];
  onToggleArtist: (id: string) => void;
  performance: CompetitivePerformance;
  posts: Record<string, string[]>;
  strategy: CompetitiveStrategy[];
}) {
  const trackedArtists = artists.filter((artist) => trackedIds.includes(artist.id));
  const labels = performance.dates;
  const growthDataset = trackedArtists.map((artist, index) => ({
    label: `${artist.name} growth`,
    data: performance.growthSeries[artist.id] ?? [],
    borderColor: ["#34d399", "#60a5fa", "#f472b6", "#fbbf24"][index % 4],
  }));

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-semibold text-foreground">Competitive Intelligence</h2>
          <p className="text-sm text-muted-foreground">See how similar artists are growing and what formats they rely on.</p>
        </div>
      </div>

      <Card className="border-border/80 bg-background/40 p-4">
        <p className="text-sm font-semibold text-foreground">Peer artists</p>
        <div className="mt-3 grid gap-3 md:grid-cols-3">
          {artists.map((artist) => {
            const active = trackedIds.includes(artist.id);
            return (
              <button
                key={artist.id}
                type="button"
                className={cn(
                  "rounded-xl border p-3 text-left",
                  active ? "border-emerald-400/60 bg-emerald-400/10" : "border-border/70 bg-background/40",
                )}
                onClick={() => onToggleArtist(artist.id)}
              >
                <p className="text-sm font-semibold text-foreground">{artist.name}</p>
                <p className="text-xs text-muted-foreground">{artist.genre}</p>
                <p className="mt-1 text-xs text-muted-foreground">{artist.followers.toLocaleString()} followers</p>
                <p className="text-xs text-emerald-400">+{artist.growthRate.toFixed(1)}% growth</p>
              </button>
            );
          })}
        </div>
      </Card>

      <section className="grid gap-4 lg:grid-cols-2">
        <Card className="border-border/80 bg-background/40 p-4">
          <p className="text-sm font-semibold text-foreground">Growth comparison</p>
          <div className="mt-3 h-48">
            {labels.length && growthDataset.length ? (
              <Line
                data={{
                  labels,
                  datasets: growthDataset,
                }}
                options={{ plugins: { legend: { display: true } } }}
              />
            ) : (
              <p className="text-sm text-muted-foreground">Not enough data.</p>
            )}
          </div>
        </Card>
        <Card className="border-border/80 bg-background/40 p-4">
          <p className="text-sm font-semibold text-foreground">Recent posts powering growth</p>
          <div className="mt-3 space-y-3 text-xs text-foreground/70">
            {trackedArtists.map((artist) => (
              <div key={artist.id} className="rounded-xl border border-border/70 bg-background/40 p-3">
                <p className="text-sm font-semibold text-foreground">{artist.name}</p>
                {(posts[artist.id] ?? []).slice(0, 3).map((post) => (
                  <div key={post} className="mt-2 rounded-lg border border-border/50 bg-background/30 p-2">
                    {post}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </Card>
      </section>

      <section className="grid gap-4 lg:grid-cols-2">
        <Card className="border-border/80 bg-background/40 p-4">
          <p className="text-sm font-semibold text-foreground">Strategy snapshot</p>
          <div className="mt-3 space-y-3 text-sm text-foreground/70">
            {strategy.map((entry) => (
              <div key={entry.artistId} className="rounded-xl border border-border/70 bg-background/40 p-3">
                <p className="text-base font-semibold text-foreground">
                  {artists.find((artist) => artist.id === entry.artistId)?.name ?? entry.artistId}
                </p>
                <p className="text-xs text-muted-foreground">Best formats: {entry.bestFormats.join(", ")}</p>
                <p className="text-xs text-muted-foreground">Posting times: {entry.postingTimes.join(", ")}</p>
                <div className="mt-2 space-y-1 text-xs text-foreground/70">
                  {entry.workingNotes.map((note) => (
                    <div key={note} className="rounded-lg border border-border/60 bg-background/40 p-2">
                      {note}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Card>
        <Card className="border-border/80 bg-background/40 p-4">
          <p className="text-sm font-semibold text-foreground">Trend intersections</p>
          <div className="mt-3 space-y-2 text-xs text-foreground/70">
            {strategy.flatMap((entry) => entry.trendIntersection).length === 0 ? (
              <p className="text-muted-foreground">No trend data available.</p>
            ) : (
              strategy.flatMap((entry) => entry.trendIntersection).map((trend) => (
                <div key={trend} className="rounded-xl border border-border/60 bg-background/40 p-2">
                  {trend}
                </div>
              ))
            )}
          </div>
        </Card>
      </section>
    </div>
  );
}

function TourConversionView({
  conversions,
  selectedId,
  detail,
  onSelect,
}: {
  conversions: TourConversion[];
  selectedId: string | null;
  detail: TourConversion | null;
  onSelect: (id: string) => void;
}) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const selected = conversions.find((conversion) => conversion.dateId === selectedId) ?? null;
  const active = detail ?? selected ?? null;

  return (
    <div className="grid gap-6 lg:grid-cols-[320px,1fr]">
      <Card className="border-border/80 bg-background/40 p-4">
        <p className="text-sm font-semibold text-foreground">Tour dates</p>
        <div className="mt-4 space-y-2">
          {conversions.length === 0 ? (
            <p className="text-sm text-muted-foreground">No conversion data yet.</p>
          ) : (
            conversions.map((conversion) => (
              <div
                key={conversion.dateId}
                className={cn(
                  "rounded-xl border p-3 transition",
                  selectedId === conversion.dateId
                    ? "border-emerald-400/60 bg-emerald-400/10 text-foreground"
                    : "border-border/70 bg-background/40 text-foreground/70 hover:border-border",
                )}
              >
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="w-full justify-start text-left"
                  onClick={() => onSelect(conversion.dateId)}
                >
                  <p className="text-sm font-semibold">
                    {conversion.city} • {conversion.venue}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Conversion {conversion.conversionRate.toFixed(1)}%
                  </p>
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="mt-2"
                  onClick={() => {
                    onSelect(conversion.dateId);
                    setDrawerOpen(true);
                  }}
                >
                  Open funnel
                </Button>
              </div>
            ))
          )}
        </div>
      </Card>
      {active ? (
        <div className="space-y-4">
          <Card className="border-border/80 bg-background/40 p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-base font-semibold text-foreground">
                  {active.city} • {active.venue}
                </p>
                <p className="text-xs text-muted-foreground">
                  Awareness {active.awarenessRate.toFixed(1)}% • Conversion {active.conversionRate.toFixed(1)}%
                </p>
              </div>
              <Button variant="outline" onClick={() => setDrawerOpen(true)}>
                View funnel
              </Button>
            </div>
            <div className="mt-4 grid gap-3 md:grid-cols-4">
              {[
                { label: "Impressions", value: active.impressions.toLocaleString() },
                { label: "Engagements", value: active.engagements.toLocaleString() },
                { label: "Clicks", value: active.linkClicks.toLocaleString() },
                { label: "Sales", value: active.ticketSales.toLocaleString() },
              ].map((stat) => (
                <div key={stat.label} className="rounded-xl border border-border/70 bg-background/40 p-3">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">{stat.label}</p>
                  <p className="text-lg font-semibold text-foreground">{stat.value}</p>
                </div>
              ))}
            </div>
          </Card>
          <section className="grid gap-4 lg:grid-cols-2">
            <Card className="border-border/80 bg-background/40 p-4">
              <p className="text-sm font-semibold text-foreground">Top assets</p>
              <div className="mt-3 space-y-2 text-sm text-foreground/70">
                {active.topAssets.length ? (
                  active.topAssets.map((asset) => (
                    <div key={asset} className="rounded-xl border border-border/70 bg-background/40 p-3">
                      {asset}
                    </div>
                  ))
                ) : (
                  <p className="text-xs text-muted-foreground">No assets logged yet.</p>
                )}
              </div>
            </Card>
            <Card className="border-border/80 bg-background/40 p-4">
              <p className="text-sm font-semibold text-foreground">Recommended actions</p>
              <div className="mt-3 space-y-2 text-sm text-foreground/70">
                {active.recommendedActions.length ? (
                  active.recommendedActions.map((action) => (
                    <div key={action} className="rounded-xl border border-border/70 bg-background/40 p-3">
                      {action}
                    </div>
                  ))
                ) : (
                  <p className="text-xs text-muted-foreground">No recommendations at this time.</p>
                )}
              </div>
            </Card>
          </section>
          <Card className="border-border/80 bg-background/40 p-4">
            <p className="text-sm font-semibold text-foreground">Promo cadence</p>
            <div className="mt-3 grid gap-3 md:grid-cols-4">
              {["T-21", "T-14", "T-7", "T-1"].map((label) => (
                <div key={label} className="rounded-xl border border-border/70 bg-background/40 p-3">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
                  <p className="mt-1 text-sm text-foreground">Ensure tasks scheduled (placeholder)</p>
                  <Button size="sm" variant="ghost" className="mt-2">
                    Auto-generate
                  </Button>
                </div>
              ))}
            </div>
          </Card>
        </div>
      ) : (
        <Card className="border-dashed border-border/70 bg-background/30 p-6 text-center text-sm text-muted-foreground">
          Select a tour date to view its conversion funnel.
        </Card>
      )}
      <Sheet open={drawerOpen} onOpenChange={(value) => (!value ? setDrawerOpen(false) : undefined)}>
        <SheetContent className="flex w-full max-w-2xl flex-col gap-4 overflow-y-auto border-l border-border/70 bg-background/95">
          {active ? (
            <>
              <SheetHeader>
                <SheetTitle className="text-2xl text-foreground">
                  {active.city} funnel
                </SheetTitle>
              </SheetHeader>
              <div className="space-y-4 text-sm text-foreground/70">
                {[
                  { label: "Awareness", value: active.impressions, note: "How many locals saw anything" },
                  { label: "Engagement", value: active.engagements, note: "Reactions, saves, replies" },
                  { label: "Clicks", value: active.linkClicks, note: "Link visits" },
                  { label: "Sales", value: active.ticketSales, note: "Verified ticket scans" },
                ].map((step, index) => (
                  <div key={step.label} className="rounded-2xl border border-border/70 bg-background/40 p-4">
                    <div className="flex items-center justify-between">
                      <p className="text-base font-semibold text-foreground">
                        Step {index + 1}: {step.label}
                      </p>
                      <Badge className="bg-muted text-foreground">{step.value.toLocaleString()}</Badge>
                    </div>
                    <p className="mt-2 text-xs text-muted-foreground">{step.note}</p>
                    <p className="mt-2 text-xs text-emerald-300">
                      {active.recommendedActions[index] ?? "Keep cadence steady; monitor drop-off."}
                    </p>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="p-6 text-sm text-muted-foreground">Select a tour date to view funnel.</div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}


function ContentShelfView({
  items,
  loading = false,
  onUseAgain,
  onOpenComposer,
}: {
  items: ContentShelfItem[];
  loading?: boolean;
  onUseAgain: (item: ContentShelfItem) => Promise<void>;
  onOpenComposer: (options?: Partial<ComposerState>) => void;
}) {
  const [filter, setFilter] = useState<"all" | "high" | "evergreen" | "idea" | "clip" | "quote">("all");
  const [pendingId, setPendingId] = useState<string | null>(null);

  const filters = [
    { id: "all", label: "All" },
    { id: "high", label: "High Performers" },
    { id: "evergreen", label: "Evergreen" },
    { id: "idea", label: "Ideas" },
    { id: "clip", label: "Clips" },
    { id: "quote", label: "Quotes" },
  ] as const;

  const filteredItems = useMemo(
    () => items.filter((item) => (filter === "all" ? true : item.type === filter)),
    [items, filter],
  );

  const handleUseAgain = async (item: ContentShelfItem) => {
    setPendingId(item.id);
    try {
      await onUseAgain(item);
    } finally {
      setPendingId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold text-foreground">Content Shelf</h2>
          <p className="text-sm text-muted-foreground">
            Evergreen posts, quotes, and clips ready to redeploy when new opportunities hit.
          </p>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        {filters.map((entry) => (
          <button
            key={entry.id}
            type="button"
            className={cn(
              "rounded-full border px-4 py-1 text-sm transition",
              filter === entry.id
                ? "border-primary/60 bg-primary/15 text-foreground"
                : "border-border text-muted-foreground hover:border-border hover:text-foreground",
            )}
            onClick={() => setFilter(entry.id)}
          >
            {entry.label}
          </button>
        ))}
      </div>
      {loading ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, index) => (
            <div key={index} className="animate-pulse rounded-2xl border border-border/70 bg-background/40 p-4">
              <div className="h-32 rounded-xl bg-background/40" />
              <div className="mt-3 h-4 w-3/4 rounded bg-background/40" />
              <div className="mt-2 h-3 w-1/3 rounded bg-background/40" />
            </div>
          ))}
        </div>
      ) : filteredItems.length === 0 ? (
        <EmptyState label="No shelf items match that filter." />
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredItems.map((item) => (
            <Card key={item.id} className="flex flex-col border-border/80 bg-background/40 p-4">
              <div className="space-y-3">
                {item.thumbnailUrl ? (
                  <div className="relative h-40 overflow-hidden rounded-xl border border-border/70">
                    <img src={item.thumbnailUrl} alt={item.title} className="h-full w-full object-cover" />
                    <Badge className="absolute left-2 top-2 bg-black/60 text-xs capitalize text-foreground">{item.type}</Badge>
                  </div>
                ) : (
                  <div className="rounded-xl border border-dashed border-border/70 bg-background/60 p-4">
                    <p className="text-sm font-semibold text-foreground">{item.title}</p>
                    <p className="text-xs text-muted-foreground line-clamp-3">{item.description ?? "Evergreen idea"}</p>
                  </div>
                )}
                <div>
                  <p className="text-base font-semibold text-foreground">{item.title}</p>
                  {item.lastUsedAt ? (
                    <p className="text-xs text-muted-foreground">Last used {formatRelativeTime(item.lastUsedAt)}</p>
                  ) : null}
                </div>
                {item.metrics ? (
                  <div className="flex flex-wrap gap-3 text-xs text-foreground/70">
                    {typeof item.metrics.impressions === "number" ? (
                      <Badge variant="outline" className="border-border text-foreground/80">
                        {item.metrics.impressions.toLocaleString()} impressions
                      </Badge>
                    ) : null}
                    {typeof item.metrics.engagementRate === "number" ? (
                      <Badge variant="outline" className="border-emerald-500/40 text-emerald-200">
                        {item.metrics.engagementRate.toFixed(1)}% ER
                      </Badge>
                    ) : null}
                    {typeof item.metrics.saves === "number" ? (
                      <Badge variant="outline" className="border-primary/60 text-foreground">
                        {item.metrics.saves} saves
                      </Badge>
                    ) : null}
                  </div>
                ) : null}
                {item.insight ? (
                  <div className="flex items-start gap-2 rounded-xl border border-primary/40 bg-primary/10 p-3 text-xs text-foreground">
                    <Lightbulb className="mt-0.5 h-4 w-4" />
                    <p>{item.insight}</p>
                  </div>
                ) : null}
              </div>
              <div className="mt-4 flex flex-1 flex-col justify-end gap-3">
                <Button onClick={() => void handleUseAgain(item)} disabled={pendingId === item.id}>
                  {pendingId === item.id ? "Loading…" : "Use Again"}
                </Button>
                <Button
                  variant="outline"
                  onClick={() =>
                    onOpenComposer({
                      initialAsset: item.asset ?? undefined,
                      initialDatetime: new Date().toISOString(),
                    })
                  }
                >
                  Open in Composer
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function CollabManagerView({
  creators,
  history,
  loading = false,
  onSendOutreach,
  onLinkHistory,
}: {
  creators: CreatorProfile[];
  history: CollabHistoryItem[];
  loading?: boolean;
  onSendOutreach: (creatorId: string, tone: string, message: string) => Promise<void>;
  onLinkHistory: (historyId: string) => Promise<void>;
}) {
  const { push } = useToast();
  const [filter, setFilter] = useState<"all" | "local" | "genre" | "trending" | "small">("all");
  const [selectedCreator, setSelectedCreator] = useState<CreatorProfile | null>(null);
  const [drawerCreator, setDrawerCreator] = useState<CreatorProfile | null>(null);
  const [tone, setTone] = useState<"professional" | "friendly" | "playful">("professional");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const matchesFilter = useCallback(
    (creator: CreatorProfile) => {
      switch (filter) {
        case "local":
          return creator.affinityScore >= 60 || creator.handle.toLowerCase().includes("nash");
        case "genre":
          return creator.affinityScore >= 75;
        case "trending":
          return creator.engagementRate >= 6;
        case "small":
          return creator.followers <= 50000;
        default:
          return true;
      }
    },
    [filter],
  );

  const filteredCreators = useMemo(() => creators.filter(matchesFilter), [creators, matchesFilter]);

  const handleGenerateTemplate = () => {
    if (!selectedCreator) {
      push({ title: "Select a creator", message: "Choose who you want to reach out to first.", variant: "destructive" });
      return;
    }
    const toneText =
      tone === "professional"
        ? "Appreciate your work and think a collab could help both our audiences grow."
        : tone === "friendly"
          ? "Love the vibe you're creating—let's jam on something together."
          : "Your latest jam is begging for a duet. Want to stitch something fun?";
    setMessage(
      `Hey ${selectedCreator.handle},\n\n${toneText}\n\nThinking we link up on a duet or stitch that highlights both of our fiddle runs. Can I send a concept?`,
    );
  };

  const handleSend = async () => {
    if (!selectedCreator) {
      push({ title: "Select a creator", message: "Choose someone to reach out to.", variant: "destructive" });
      return;
    }
    if (!message.trim()) {
      push({ title: "Message required", message: "Draft a quick DM or email first.", variant: "destructive" });
      return;
    }
    setSending(true);
    await onSendOutreach(selectedCreator.id, tone, message);
    setSending(false);
    setMessage("");
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold text-foreground">Collab Manager</h2>
          <p className="text-sm text-muted-foreground">
            Discover aligned creators, plan outreach, and track how collabs impact growth.
          </p>
        </div>
      </div>

      <section className="space-y-3">
        <div className="flex flex-wrap gap-2">
          {[
            { id: "all", label: "All" },
            { id: "local", label: "Local" },
            { id: "genre", label: "Same Genre" },
            { id: "trending", label: "Trending" },
            { id: "small", label: "Small Creators" },
          ].map((entry) => (
            <button
              key={entry.id}
              type="button"
              className={cn(
                "rounded-full border px-4 py-1 text-sm transition",
                filter === entry.id
                  ? "border-sky-400/70 bg-sky-400/10 text-sky-100"
                  : "border-border text-muted-foreground hover:border-border hover:text-foreground",
              )}
              onClick={() => setFilter(entry.id as typeof filter)}
            >
              {entry.label}
            </button>
          ))}
        </div>
        {loading ? (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, index) => (
              <Card key={index} className="animate-pulse border-border/80 bg-background/40 p-4">
                <div className="h-32 rounded-xl bg-background/40" />
                <div className="mt-3 h-4 w-2/3 rounded bg-background/40" />
              </Card>
            ))}
          </div>
        ) : filteredCreators.length === 0 ? (
          <EmptyState label="No creators match those filters." />
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {filteredCreators.map((creator) => (
              <Card key={creator.id} className="border-border/80 bg-background/40 p-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-background/40 text-lg font-semibold text-foreground/80">
                    {creator.handle.slice(1, 2).toUpperCase()}
                  </div>
                  <div>
                    <p className="text-base font-semibold text-foreground">@{creator.handle.replace(/^@/, "")}</p>
                    <p className="text-xs text-muted-foreground">{creator.platform.toUpperCase()}</p>
                  </div>
                  <Badge className="ml-auto bg-emerald-500/10 text-emerald-200">
                    Affinity {creator.affinityScore}%
                  </Badge>
                </div>
                <div className="mt-4 grid grid-cols-2 gap-3 text-xs text-foreground/70">
                  <div>
                    <p className="uppercase tracking-wide text-muted-foreground">Followers</p>
                    <p className="text-lg text-foreground">{creator.followers.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="uppercase tracking-wide text-muted-foreground">Engagement</p>
                    <p className="text-lg text-foreground">{creator.engagementRate.toFixed(1)}%</p>
                  </div>
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  <Button size="sm" className="flex-1" onClick={() => setSelectedCreator(creator)}>
                    Select
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setDrawerCreator(creator)}>
                    Review Profile
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </section>

      <section className="grid gap-4 lg:grid-cols-2">
        <Card className="border-border/80 bg-background/40 p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-foreground">Outreach composer</p>
              <p className="text-xs text-muted-foreground">
                {selectedCreator
                  ? `Drafting for @${selectedCreator.handle}`
                  : "Select a creator to start drafting."}
              </p>
            </div>
            <Select value={tone} onValueChange={(value) => setTone(value as typeof tone)}>
              <SelectTrigger className="w-40 bg-background/40 text-foreground">
                <SelectValue placeholder="Tone" />
              </SelectTrigger>
              <SelectContent className="bg-background/40 text-foreground">
                <SelectItem value="professional">Professional</SelectItem>
                <SelectItem value="friendly">Friendly</SelectItem>
                <SelectItem value="playful">Playful</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Textarea
            value={message}
            onChange={(event) => setMessage(event.target.value)}
            placeholder={
              selectedCreator
                ? `Hey ${selectedCreator.handle}, love your...`
                : "Select a creator to draft a DM or email."
            }
            rows={6}
            className="mt-3 bg-background/40 text-foreground"
          />
          <div className="mt-3 flex flex-wrap gap-2">
            <Button variant="outline" onClick={handleGenerateTemplate}>
              Generate template
            </Button>
            <Button onClick={() => void handleSend()} disabled={sending}>
              {sending ? "Sending…" : "Send & Track"}
            </Button>
          </div>
        </Card>
        <Card className="border-border/80 bg-background/40 p-4">
          <p className="text-sm font-semibold text-foreground">Collab history</p>
          <div className="mt-3 space-y-3">
            {history.length === 0 ? (
              <p className="text-sm text-muted-foreground">No collaborations logged yet.</p>
            ) : (
              history.map((entry) => {
                const normalizedHandle = entry.creatorHandle.replace(/^@/, "");
                const creatorMatch =
                  creators.find(
                    (candidate) => candidate.handle.replace(/^@/, "").toLowerCase() === normalizedHandle.toLowerCase(),
                  ) ?? null;
                return (
                  <div key={entry.id} className="rounded-xl border border-border/70 bg-background/30 p-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div>
                        <p className="text-base font-semibold text-foreground">@{entry.creatorHandle}</p>
                        <p className="text-xs text-muted-foreground">
                          {entry.platform.toUpperCase()} • {formatDate(entry.executedAt)}
                      </p>
                    </div>
                    <Badge variant="outline" className="border-border text-foreground/80">
                      {entry.status ?? "Completed"}
                    </Badge>
                  </div>
                  {entry.campaign ? (
                    <p className="text-xs text-muted-foreground">Campaign: {entry.campaign}</p>
                  ) : null}
                  {entry.impactSummary ? (
                    <p className="mt-2 text-sm text-foreground/70">{entry.impactSummary}</p>
                  ) : null}
                  <div className="mt-3 flex flex-wrap gap-2 text-xs text-muted-foreground">
                    {typeof entry.reachDelta === "number" ? (
                      <Badge variant="outline" className="border-emerald-500/40 text-emerald-200">
                        +{entry.reachDelta.toLocaleString()} reach
                      </Badge>
                    ) : null}
                    {typeof entry.engagementLift === "number" ? (
                      <Badge variant="outline" className="border-sky-400/40 text-sky-100">
                        +{entry.engagementLift.toFixed(1)}% engagement
                      </Badge>
                    ) : null}
                  </div>
                    <div className="mt-3 flex gap-2">
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() =>
                          creatorMatch
                            ? setSelectedCreator(creatorMatch)
                            : push({
                                title: "Creator not in discovery",
                                message: "Add them to potential collaborations to draft outreach.",
                              })
                        }
                      >
                        Reconnect
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => void onLinkHistory(entry.id)}>
                        Link results
                      </Button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </Card>
      </section>

      <CreatorDrawer creator={drawerCreator} onClose={() => setDrawerCreator(null)} />
    </div>
  );
}

function CreatorDrawer({ creator, onClose }: { creator: CreatorProfile | null; onClose: () => void }) {
  const suggestions = creator
    ? [
        `Duet their latest solo to showcase dobro + fiddle call-and-response.`,
        "Respond to their jam challenge with a behind-the-scenes clip.",
        "Feature them in a carousel frame highlighting joint tour dates.",
      ]
    : [];
  return (
    <Sheet open={Boolean(creator)} onOpenChange={(open) => (!open ? onClose() : undefined)}>
      <SheetContent className="flex w-full max-w-lg flex-col gap-4 overflow-y-auto border-l border-border/70 bg-background/95">
        {creator ? (
          <>
            <SheetHeader>
              <SheetTitle className="text-foreground">@{creator.handle}</SheetTitle>
              <p className="text-sm text-muted-foreground">{creator.platform.toUpperCase()}</p>
            </SheetHeader>
            <div className="rounded-2xl border border-border/70 bg-background/40 p-4">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Stats</p>
              <div className="mt-3 grid grid-cols-2 gap-4 text-sm text-foreground">
                <div>
                  <p className="text-2xl font-semibold">{creator.followers.toLocaleString()}</p>
                  <p className="text-xs text-muted-foreground">Followers</p>
                </div>
                <div>
                  <p className="text-2xl font-semibold">{creator.engagementRate.toFixed(1)}%</p>
                  <p className="text-xs text-muted-foreground">Engagement</p>
                </div>
                <div>
                  <p className="text-2xl font-semibold">{creator.affinityScore}%</p>
                  <p className="text-xs text-muted-foreground">Affinity</p>
                </div>
              </div>
            </div>
            <div className="rounded-2xl border border-border/70 bg-background/40 p-4">
              <p className="text-sm font-semibold text-foreground">Sample videos</p>
              <div className="mt-3 space-y-2 text-sm text-foreground/70">
                {creator.sampleVideos?.length ? (
                  creator.sampleVideos.map((video, index) => (
                    <div
                      key={video}
                      className="rounded-xl border border-border/60 bg-background/40 p-3"
                    >
                      <p className="text-foreground">Video {index + 1}</p>
                      <p className="text-xs text-muted-foreground truncate">{video}</p>
                    </div>
                  ))
                ) : (
                  <p className="text-xs text-muted-foreground">No examples available.</p>
                )}
              </div>
            </div>
            <div className="rounded-2xl border border-emerald-400/50 bg-emerald-500/10 p-4">
              <p className="text-sm font-semibold text-foreground">Suggested ideas</p>
              <div className="mt-3 space-y-2 text-sm text-emerald-100">
                {suggestions.map((idea) => (
                  <div key={idea} className="rounded-lg border border-emerald-400/40 bg-emerald-500/5 p-3">
                    {idea}
                  </div>
                ))}
              </div>
            </div>
          </>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}

function AdManagerView({
  accounts,
  campaigns,
  performance,
  tasks,
  onConnectAccount,
  onOpenCreate,
  onBoostEvent,
}: {
  accounts: AdAccount[];
  campaigns: AdCampaign[];
  performance: AdPerformanceSummary;
  tasks: MarketingTask[];
  onConnectAccount: (platform: AdAccount["platform"]) => void;
  onOpenCreate: (seedTask?: MarketingTask | null) => void;
  onBoostEvent: (task: MarketingTask) => Promise<void>;
}) {
  const roasData = useMemo(
    () => ({
      labels: performance.roasSeries.map((point) => point.date),
      datasets: [
        {
          label: "ROAS",
          data: performance.roasSeries.map((point) => point.value),
          borderColor: "#34d399",
          backgroundColor: "rgba(52,211,153,0.2)",
        },
      ],
    }),
    [performance.roasSeries],
  );
  const tourEvents = tasks.filter((task) => task.type === "event_promo");

  const roasStatus = (value: number) => {
    if (value >= 3) return "bg-emerald-500/20 text-emerald-100";
    if (value >= 1.5) return "bg-primary/15 text-foreground";
    return "bg-rose-500/20 text-rose-100";
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold text-foreground">Ads Manager</h2>
          <p className="text-sm text-muted-foreground">Boost posts, promote tour stops, and watch ROAS in one place.</p>
        </div>
        <Button onClick={() => onOpenCreate()} className="bg-primary/90 text-foreground hover:bg-primary/70">
          Create Ad
        </Button>
      </div>

      <section className="grid gap-4 lg:grid-cols-2">
        <Card className="border-border/80 bg-background/40 p-4">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-foreground">Connected accounts</p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => onConnectAccount("facebook")}>
                Connect Facebook Ads
              </Button>
              <Button variant="outline" size="sm" onClick={() => onConnectAccount("tiktok")}>
                Connect TikTok Ads
              </Button>
            </div>
          </div>
          <div className="mt-4 space-y-3">
            {accounts.length === 0 ? (
              <p className="text-sm text-muted-foreground">No ad accounts connected yet.</p>
            ) : (
              accounts.map((account) => (
                <div
                  key={account.id}
                  className="flex items-center justify-between rounded-xl border border-border/70 bg-background/50 p-3"
                >
                  <div>
                    <p className="text-sm font-semibold text-foreground">{account.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {account.platform.toUpperCase()} • {account.status === "connected" ? "Connected" : "Reconnect needed"}
                    </p>
                  </div>
                  {typeof account.spend30d === "number" ? (
                    <p className="text-xs text-muted-foreground">Spend 30d: ${account.spend30d.toLocaleString()}</p>
                  ) : null}
                </div>
              ))
            )}
          </div>
        </Card>
        <Card className="border-border/80 bg-background/40 p-4">
          <p className="text-sm font-semibold text-foreground">Performance dashboard</p>
          <div className="mt-3 grid grid-cols-3 gap-3 text-xs text-muted-foreground">
            <div>
              <p className="uppercase tracking-wide">CPM</p>
              <p className="text-2xl font-semibold text-foreground">${performance.cpm.toFixed(2)}</p>
            </div>
            <div>
              <p className="uppercase tracking-wide">CPC</p>
              <p className="text-2xl font-semibold text-foreground">${performance.cpc.toFixed(2)}</p>
            </div>
            <div>
              <p className="uppercase tracking-wide">CTR</p>
              <p className="text-2xl font-semibold text-foreground">{performance.ctr.toFixed(2)}%</p>
            </div>
          </div>
          <div className="mt-4 h-40">
            {performance.roasSeries.length ? (
              <Line
                data={roasData}
                options={{
                  responsive: true,
                  plugins: { legend: { display: false } },
                  scales: { x: { ticks: { color: "#94a3b8" } }, y: { ticks: { color: "#94a3b8" } } },
                }}
              />
            ) : (
              <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                No ROAS data yet.
              </div>
            )}
          </div>
        </Card>
      </section>

      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <p className="text-sm font-semibold text-foreground">Active campaigns</p>
          <Button variant="outline" size="sm" onClick={() => onOpenCreate()}>
            New Campaign
          </Button>
        </div>
        {campaigns.length === 0 ? (
          <EmptyState label="No campaigns running yet." />
        ) : (
          <div className="overflow-x-auto rounded-2xl border border-border/80">
            <table className="w-full text-left text-sm text-foreground/80">
              <thead className="bg-background/30 text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Platform</th>
                  <th className="px-4 py-3">Objective</th>
                  <th className="px-4 py-3">Spend</th>
                  <th className="px-4 py-3">Impr.</th>
                  <th className="px-4 py-3">Clicks</th>
                  <th className="px-4 py-3">Conversions</th>
                  <th className="px-4 py-3">ROAS</th>
                </tr>
              </thead>
              <tbody>
                {campaigns.map((campaign) => (
                  <tr key={campaign.id} className="border-t border-border/70">
                    <td className="px-4 py-3">{campaign.name}</td>
                    <td className="px-4 py-3">{campaign.platform.toUpperCase()}</td>
                    <td className="px-4 py-3 capitalize">{campaign.objective.replace("_", " ")}</td>
                    <td className="px-4 py-3">${campaign.spend.toLocaleString()}</td>
                    <td className="px-4 py-3">{campaign.impressions.toLocaleString()}</td>
                    <td className="px-4 py-3">{campaign.clicks.toLocaleString()}</td>
                    <td className="px-4 py-3">{campaign.conversions.toLocaleString()}</td>
                    <td className="px-4 py-3">
                      <Badge className={roasStatus(campaign.roas)}>ROAS {campaign.roas.toFixed(2)}x</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="space-y-3 rounded-2xl border border-border/80 bg-background/40 p-4">
        <div className="flex items-center justify-between">
          <p className="text-sm font-semibold text-foreground">Tour event boosts</p>
          <Button variant="ghost" size="sm" onClick={() => onOpenCreate(tourEvents[0])} disabled={!tourEvents.length}>
            Create from event
          </Button>
        </div>
        {tourEvents.length === 0 ? (
          <p className="text-sm text-muted-foreground">No tour promos on the calendar.</p>
        ) : (
          <div className="grid gap-3 md:grid-cols-2">
            {tourEvents.slice(0, 4).map((event) => (
              <Card key={event.id} className="border-border/70 bg-background/50 p-3">
                <p className="text-base font-semibold text-foreground">{event.title}</p>
                {event.scheduledFor ? (
                  <p className="text-xs text-muted-foreground">{formatDate(event.scheduledFor)}</p>
                ) : null}
                <p className="mt-2 text-sm text-foreground/70">
                  Audience: people within 100 miles of venue • Interests: Americana, jamgrass
                </p>
                <div className="mt-3 flex gap-2">
                  <Button size="sm" onClick={() => void onBoostEvent(event)}>
                    Boost event
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => onOpenCreate(event)}>
                    Customize
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </section>

      <section className="space-y-3 rounded-2xl border border-border/80 bg-background/40 p-4">
        <p className="text-sm font-semibold text-foreground">Top converting posts</p>
        <div className="grid gap-3 md:grid-cols-2">
          {performance.topPosts.length === 0 ? (
            <p className="text-sm text-muted-foreground">No conversions tracked yet.</p>
          ) : (
            performance.topPosts.map((entry) => (
              <div key={entry.postId} className="rounded-xl border border-border/70 bg-background/50 p-3">
                <p className="text-base font-semibold text-foreground">{entry.title}</p>
                <p className="text-xs text-muted-foreground">ROAS {entry.roas.toFixed(2)}x</p>
                <Button size="sm" variant="ghost" className="mt-2" onClick={() => onOpenCreate()}>
                  Boost again
                </Button>
              </div>
            ))
          )}
        </div>
      </section>
    </div>
  );
}

function CreateAdDrawer({
  open,
  onClose,
  onSubmit,
  seedTask,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (payload: CreateAdPayload) => Promise<void>;
  seedTask?: MarketingTask | null;
}) {
  const [objective, setObjective] = useState<AdCampaign["objective"]>("traffic");
  const [platform, setPlatform] = useState<AdCampaign["platform"]>("facebook");
  const [budget, setBudget] = useState(250);
  const [radius, setRadius] = useState(50);
  const [lookalike, setLookalike] = useState(true);
  const [interests, setInterests] = useState<string[]>(["bluegrass", "jamgrass"]);
  const [assetId, setAssetId] = useState("");
  const [venue, setVenue] = useState("");

  useEffect(() => {
    if (seedTask) {
      setObjective("traffic");
      setPlatform("facebook");
      setBudget(200);
      setRadius(100);
      setVenue(seedTask.title);
      setAssetId(seedTask.id);
    }
  }, [seedTask]);

  useEffect(() => {
    if (open && !seedTask) {
      setObjective("traffic");
      setPlatform("facebook");
      setBudget(250);
      setRadius(50);
      setVenue("");
      setAssetId("");
      setLookalike(true);
      setInterests(["bluegrass", "jamgrass"]);
    }
  }, [open, seedTask]);

  const toggleInterest = (value: string) => {
    setInterests((prev) => (prev.includes(value) ? prev.filter((entry) => entry !== value) : [...prev, value]));
  };

  const handleSubmit = () => {
    const payload: CreateAdPayload = {
      objective,
      platform,
      budget,
      assetId: assetId || undefined,
      audience: {
        radiusMiles: radius,
        venue: venue || undefined,
        lookalike,
        interests,
      },
    };
    void onSubmit(payload);
  };

  return (
    <Sheet open={open} onOpenChange={(isOpen) => (!isOpen ? onClose() : undefined)}>
      <SheetContent className="flex w-full max-w-3xl flex-col gap-5 overflow-y-auto border-l border-border/70 bg-background/95">
        <SheetHeader>
          <SheetTitle className="text-2xl text-foreground">{seedTask ? "Boost event" : "Create new ad"}</SheetTitle>
          <p className="text-sm text-muted-foreground">
            {seedTask ? `Pre-configured for ${seedTask.title}` : "Promote posts, tour dates, and drops."}
          </p>
        </SheetHeader>
        <div className="space-y-4">
          <div className="grid gap-3 md:grid-cols-2">
            <label className="text-xs uppercase tracking-wide text-muted-foreground">
              Objective
              <Select value={objective} onValueChange={(value) => setObjective(value as AdCampaign["objective"])}>
                <SelectTrigger className="mt-1 bg-background/40 text-foreground">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-background/40 text-foreground">
                  <SelectItem value="traffic">Traffic</SelectItem>
                  <SelectItem value="awareness">Awareness</SelectItem>
                  <SelectItem value="sales">Sales</SelectItem>
                  <SelectItem value="video_views">Video Views</SelectItem>
                </SelectContent>
              </Select>
            </label>
            <label className="text-xs uppercase tracking-wide text-muted-foreground">
              Platform
              <Select value={platform} onValueChange={(value) => setPlatform(value as AdCampaign["platform"])}>
                <SelectTrigger className="mt-1 bg-background/40 text-foreground">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-background/40 text-foreground">
                  <SelectItem value="facebook">Facebook</SelectItem>
                  <SelectItem value="instagram">Instagram</SelectItem>
                  <SelectItem value="tiktok">TikTok</SelectItem>
                </SelectContent>
              </Select>
            </label>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Budget (${budget}/day)</p>
            <Slider value={[budget]} min={25} max={1000} step={25} onValueChange={([value]) => setBudget(value)} />
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            <label className="text-xs uppercase tracking-wide text-muted-foreground">
              Asset or Post ID
              <Input
                className="mt-1 bg-background/40 text-foreground"
                value={assetId}
                onChange={(event) => setAssetId(event.target.value)}
                placeholder="Paste asset or post id"
              />
            </label>
            <label className="text-xs uppercase tracking-wide text-muted-foreground">
              Venue / Tour stop
              <Input
                className="mt-1 bg-background/40 text-foreground"
                value={venue}
                onChange={(event) => setVenue(event.target.value)}
                placeholder="e.g. Ryman Auditorium"
              />
            </label>
          </div>
          <div className="rounded-2xl border border-border/80 bg-background/40 p-4">
            <p className="text-sm font-semibold text-foreground">Audience targeting</p>
            <div className="mt-3 grid gap-3 md:grid-cols-2">
              <label className="text-xs uppercase tracking-wide text-muted-foreground">
                Radius (miles)
                <Input
                  type="number"
                  className="mt-1 bg-background/40 text-foreground"
                  value={radius}
                  onChange={(event) => setRadius(Number(event.target.value))}
                  min={10}
                  max={250}
                />
              </label>
              <label className="flex items-center gap-2 text-sm text-foreground/70">
                <Checkbox checked={lookalike} onCheckedChange={(value) => setLookalike(Boolean(value))} />
                Lookalike: band-like followers
              </label>
            </div>
            <div className="mt-4 space-y-2">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Interests</p>
              <div className="flex flex-wrap gap-2">
                {["bluegrass", "jamgrass", "americana", "songwriting", "festivals"].map((interest) => (
                  <button
                    key={interest}
                    type="button"
                    className={cn(
                      "rounded-full border px-3 py-2 min-h-[44px] md:min-h-[36px] text-xs",
                      interests.includes(interest)
                        ? "border-emerald-400/70 bg-emerald-400/10 text-emerald-100"
                        : "border-border text-muted-foreground",
                    )}
                    onClick={() => toggleInterest(interest)}
                  >
                    {interest}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 border-t border-border/70 pt-4">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit}>Launch campaign</Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function TourPromoEngineView({
  dates,
  selectedId,
  insights,
  loading,
  onSelectDate,
  onGenerateAssets,
  onScheduleAll,
}: {
  dates: TourDate[];
  selectedId: string | null;
  insights: TourCityInsights | null;
  loading?: boolean;
  onSelectDate: (id: string) => void;
  onGenerateAssets: (id: string) => void;
  onScheduleAll: (id: string) => void;
}) {
  const selectedDate = dates.find((date) => date.id === selectedId) ?? null;

  return (
    <div className="grid gap-6 lg:grid-cols-[320px,1fr]">
      <Card className="border-border/80 bg-background/40 p-4">
        <p className="text-sm font-semibold text-foreground">Tour dates</p>
        {loading ? (
          <div className="mt-4 space-y-3">
            {Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="h-14 animate-pulse rounded-xl bg-background/30" />
            ))}
          </div>
        ) : dates.length === 0 ? (
          <p className="mt-4 text-sm text-muted-foreground">No upcoming tour stops.</p>
        ) : (
          <div className="mt-4 space-y-2">
            {dates.map((date) => (
              <button
                key={date.id}
                type="button"
                className={cn(
                  "w-full rounded-xl border p-3 text-left transition",
                  selectedId === date.id
                    ? "border-emerald-400/60 bg-emerald-400/10 text-foreground"
                    : "border-border/70 bg-background/40 text-foreground/70 hover:border-border",
                )}
                onClick={() => onSelectDate(date.id)}
              >
                <p className="text-sm font-semibold text-foreground">{date.city}</p>
                <p className="text-xs text-muted-foreground">{date.venue}</p>
                <p className="text-xs text-muted-foreground">{formatDate(date.date)}</p>
              </button>
            ))}
          </div>
        )}
      </Card>

      <div className="space-y-4">
        {!selectedDate ? (
          <Card className="border-dashed border-border/70 bg-background/30 p-6 text-center text-sm text-muted-foreground">
            Select a tour stop to prep its promo kit.
          </Card>
        ) : (
          <>
            <Card className="border-border/80 bg-background/40 p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-base font-semibold text-foreground">
                    {selectedDate.city} • {selectedDate.venue}
                  </p>
                  <p className="text-xs text-muted-foreground">{formatDate(selectedDate.date)}</p>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => onGenerateAssets(selectedDate.id)}>
                    Generate all assets
                  </Button>
                  <Button onClick={() => onScheduleAll(selectedDate.id)}>Schedule all</Button>
                </div>
              </div>
              <p className="mt-3 text-sm text-foreground/70">
                {insights?.venueSummary ?? "Loading venue insights…"}
              </p>
            </Card>

            <div className="grid gap-4 lg:grid-cols-2">
              <Card className="border-border/80 bg-background/40 p-4">
                <p className="text-sm font-semibold text-foreground">Local insights</p>
                <div className="mt-3 space-y-2 text-sm text-foreground/70">
                  <p>{insights?.previousPerformance ?? "Previous performance data coming soon."}</p>
                  <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                    {(insights?.audienceBehaviors ?? ["Late-night streamers"]).map((behavior) => (
                      <Badge key={behavior} variant="outline" className="border-border text-foreground/80">
                        {behavior}
                      </Badge>
                    ))}
                  </div>
                </div>
              </Card>
              <Card className="border-border/80 bg-background/40 p-4">
                <p className="text-sm font-semibold text-foreground">Trending formats</p>
                <div className="mt-3 space-y-2 text-sm text-foreground/70">
                  {(insights?.trendingFormats ?? ["Duet local fiddle solos", "POV arrival clips"]).map((format) => (
                    <div key={format} className="rounded-xl border border-border/60 bg-background/40 p-3">
                      {format}
                    </div>
                  ))}
                </div>
              </Card>
            </div>

            <div className="grid gap-4 lg:grid-cols-2">
              <Card className="border-border/80 bg-background/40 p-4">
                <p className="text-sm font-semibold text-foreground">Promo cadence</p>
                <div className="mt-3 space-y-2 text-sm text-foreground/70">
                  {(insights?.promoCadence ?? [
                    { label: "T-21", description: "Announce lineup + tease travel clip" },
                    { label: "T-7", description: "Local food collab + CTA" },
                  ]).map((entry) => (
                    <div key={entry.label} className="rounded-xl border border-border/60 bg-background/40 p-3">
                      <p className="text-xs uppercase tracking-wide text-muted-foreground">{entry.label}</p>
                      <p className="text-sm text-foreground">{entry.description}</p>
                    </div>
                  ))}
                </div>
              </Card>
              <Card className="border-border/80 bg-background/40 p-4">
                <p className="text-sm font-semibold text-foreground">Local hashtags & best time</p>
                <div className="mt-3 flex flex-wrap gap-2 text-xs text-muted-foreground">
                  {(insights?.localHashtags ?? ["#DenverJamgrass", "#FiddleNight"]).map((tag) => (
                    <Badge key={tag} variant="outline" className="border-border text-foreground/80">
                      {tag}
                    </Badge>
                  ))}
                </div>
                <div className="mt-3 flex flex-wrap gap-2 text-xs text-foreground/70">
                  {(insights?.bestTimes ?? ["6 PM MT", "11 AM MT"]).map((time) => (
                    <Badge key={time} className="bg-emerald-500/10 text-emerald-100">
                      {time}
                    </Badge>
                  ))}
                </div>
              </Card>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function AudienceSegmentationView({
  segments,
  personas,
  onUpdatePersona,
}: {
  segments: AudienceSegment[];
  personas: AudiencePersona[];
  onUpdatePersona: (personaId: string, payload: { note?: string }) => Promise<void>;
}) {
  const [filter, setFilter] = useState<"all" | "high_value" | "high_growth" | "at_risk" | "dormant">("all");
  const [drawerPersona, setDrawerPersona] = useState<AudiencePersona | null>(null);
  const [updating, setUpdating] = useState(false);

  const filteredPersonas = useMemo(
    () => personas.filter((persona) => (filter === "all" ? true : persona.tier === filter)),
    [personas, filter],
  );

  const tierBadgeClass = (tier?: string) => {
    switch (tier) {
      case "high_value":
        return "bg-emerald-500/20 text-emerald-100";
      case "high_growth":
        return "bg-sky-500/20 text-sky-100";
      case "at_risk":
        return "bg-primary/15 text-foreground";
      case "dormant":
        return "bg-muted text-foreground/80";
      default:
        return "bg-muted text-foreground/80";
    }
  };

  const handlePrioritize = async () => {
    if (!drawerPersona) return;
    setUpdating(true);
    await onUpdatePersona(drawerPersona.id, { note: "prioritize" });
    setUpdating(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold text-foreground">Audience personas</h2>
          <p className="text-sm text-muted-foreground">
            Explore clusters like “Jamgrass Lifers” or “Festival Campers” and tailor content for each.
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {[
          { id: "all", label: "All" },
          { id: "high_value", label: "High Value" },
          { id: "high_growth", label: "High Growth" },
          { id: "at_risk", label: "At Risk" },
          { id: "dormant", label: "Dormant" },
        ].map((entry) => (
          <button
            key={entry.id}
            type="button"
            className={cn(
              "rounded-full border px-4 py-1 text-sm",
              filter === entry.id
                ? "border-violet-400/70 bg-violet-400/10 text-violet-100"
                : "border-border text-muted-foreground hover:border-border hover:text-foreground",
            )}
            onClick={() => setFilter(entry.id as typeof filter)}
          >
            {entry.label}
          </button>
        ))}
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {filteredPersonas.length === 0 ? (
          <EmptyState label="No personas match that filter yet." />
        ) : (
          filteredPersonas.map((persona) => (
            <Card key={persona.id} className="border-border/80 bg-background/40 p-4">
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-violet-500/40 to-blue-500/40 text-lg font-semibold text-foreground">
                  {persona.label.slice(0, 2)}
                </div>
                <div>
                  <p className="text-base font-semibold text-foreground">{persona.label}</p>
                  <p className="text-xs text-muted-foreground">{persona.tagline}</p>
                </div>
                {persona.tier ? (
                  <Badge className={cn("ml-auto text-xs capitalize", tierBadgeClass(persona.tier))}>
                    {persona.tier.replace("_", " ")}
                  </Badge>
                ) : null}
              </div>
              <div className="mt-4 flex flex-wrap gap-2 text-xs text-muted-foreground">
                {(persona.preferredFormats ?? []).slice(0, 3).map((format) => (
                  <Badge key={format} variant="outline" className="border-border text-foreground/80">
                    {format}
                  </Badge>
                ))}
              </div>
              <div className="mt-4 flex items-center justify-between text-xs text-muted-foreground">
                <span>Engagement: {persona.engagementLevel.toUpperCase()}</span>
                <Button size="sm" variant="outline" onClick={() => setDrawerPersona(persona)}>
                  View insights
                </Button>
              </div>
            </Card>
          ))
        )}
      </div>

      <section className="rounded-2xl border border-border/80 bg-background/40 p-4">
        <p className="text-sm font-semibold text-foreground">Segments</p>
        <div className="mt-3 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {segments.map((segment) => (
            <div key={segment.id} className="rounded-xl border border-border/70 bg-background/40 p-3">
              <div className="flex items-center justify-between">
                <p className="text-base font-semibold text-foreground">{segment.label}</p>
                <Badge className="bg-emerald-500/10 text-emerald-100">
                  {segment.engagementScore}% engagement
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground">Size: {segment.size.toLocaleString()} fans</p>
              <div className="mt-2 flex flex-wrap gap-1 text-[11px] text-muted-foreground">
                {segment.behaviorTags.map((tag) => (
                  <Badge key={tag} variant="outline" className="border-border text-foreground/80">
                    {tag}
                  </Badge>
                ))}
              </div>
              <p className="mt-2 text-xs text-muted-foreground">
                Prefers {segment.preferredPlatforms.join(", ")}
              </p>
              <div className="mt-2 space-y-1 text-xs text-foreground/70">
                {segment.topContentExamples.slice(0, 2).map((example) => (
                  <div key={example} className="rounded-lg border border-border/60 bg-background/40 p-2">
                    {example}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      <Sheet open={Boolean(drawerPersona)} onOpenChange={(isOpen) => (!isOpen ? setDrawerPersona(null) : undefined)}>
        <SheetContent className="flex w-full max-w-xl flex-col gap-4 overflow-y-auto border-l border-border/70 bg-background/95">
          {drawerPersona ? (
            <>
              <SheetHeader>
                <SheetTitle className="text-2xl text-foreground">{drawerPersona.label}</SheetTitle>
                <p className="text-sm text-muted-foreground">{drawerPersona.tagline}</p>
              </SheetHeader>
              <div className="rounded-2xl border border-border/80 bg-background/40 p-4 text-sm text-foreground/70">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Demographic snapshot</p>
                <p className="mt-2">
                  {drawerPersona.demographicSnapshot ?? "Mix of millennial jamgrass diehards and festival campers."}
                </p>
                {drawerPersona.bestPostingTimes && (
                  <p className="mt-2 text-xs text-muted-foreground">
                    Best posting times: {drawerPersona.bestPostingTimes.join(", ")}
                  </p>
                )}
              </div>
              <div className="rounded-2xl border border-border/80 bg-background/40 p-4">
                <p className="text-sm font-semibold text-foreground">Preferred formats</p>
                <div className="mt-2 flex flex-wrap gap-2 text-xs text-foreground/70">
                  {(drawerPersona.preferredFormats ?? ["Live reels", "Story AMAs"]).map((format) => (
                    <Badge key={format} variant="outline" className="border-border text-foreground/80">
                      {format}
                    </Badge>
                  ))}
                </div>
              </div>
              <div className="space-y-2 rounded-2xl border border-emerald-400/40 bg-emerald-500/10 p-4 text-sm text-emerald-100">
                <p className="text-sm font-semibold text-foreground">AI suggestions</p>
                {(drawerPersona.aiSuggestions ?? [
                  "Create a mandolin loop duet challenge for this audience today.",
                  "Mash up a tour diary clip with fan voicemails.",
                ]).map((suggestion) => (
                  <div key={suggestion} className="rounded-lg border border-emerald-400/30 bg-emerald-500/5 p-3">
                    {suggestion}
                  </div>
                ))}
              </div>
              <div className="flex justify-end gap-2 border-t border-border/70 pt-4">
                <Button variant="ghost" onClick={() => setDrawerPersona(null)}>
                  Close
                </Button>
                <Button onClick={() => void handlePrioritize()} disabled={updating}>
                  {updating ? "Saving…" : "Mark priority"}
                </Button>
              </div>
            </>
          ) : null}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function ContentInboxView({
  items,
  mutateInbox,
  onComposer,
}: {
  items: InboxItem[];
  mutateInbox?: KeyedMutator<InboxItem[] | undefined>;
  onComposer: (options?: Partial<ComposerState>) => void;
}) {
  const { push } = useToast();
  const [filter, setFilter] = useState<"all" | "idea" | "clip" | "caption" | "trend">("all");
  const filters = [
    { id: "all", label: "All" },
    { id: "idea", label: "Ideas" },
    { id: "clip", label: "Clips" },
    { id: "caption", label: "Captions" },
    { id: "trend", label: "Trends" },
  ];
  const iconMap: Record<InboxItem["type"], LucideIcon> = {
    idea: Lightbulb,
    clip: Clapperboard,
    caption: PenSquare,
    comment: MessageSquare,
    trend: TrendingUp,
  };

  const filtered = items.filter((item) => (filter === "all" ? true : item.type === filter));

  const promoteItem = async (item: InboxItem) => {
    try {
      await post(`/marketing/inbox/items/${item.id}/promote-to-post`, {});
      push({ title: "Added to composer", message: "Draft loaded into the composer drawer." });
      await mutateInbox?.();
      onComposer({
        initialDatetime: new Date().toISOString(),
        initialAsset: item.assetUrl
          ? { id: item.id, type: item.type === "clip" ? "clip" : "image", title: item.type, url: item.assetUrl }
          : undefined,
      });
    } catch (error) {
      push({
        title: "Unable to promote",
        message: error instanceof Error ? error.message : "Try again later.",
        variant: "destructive",
      });
    }
  };

  const deleteItem = async (item: InboxItem) => {
    try {
      await api(`/marketing/inbox/items/${item.id}`, { method: "DELETE" });
      push({ title: "Item deleted", message: "Removed from inbox." });
      await mutateInbox?.();
    } catch (error) {
      push({
        title: "Unable to delete",
        message: error instanceof Error ? error.message : "Try again later.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold text-foreground">Content Inbox</h2>
          <p className="text-sm text-muted-foreground">AI suggestions, clips, and fan comments ready to promote.</p>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        {filters.map((entry) => (
          <button
            key={entry.id}
            type="button"
            className={cn(
              "rounded-full border px-4 py-1 text-sm",
              filter === entry.id
                ? "border-emerald-400/70 bg-emerald-400/10 text-emerald-100"
                : "border-border text-muted-foreground hover:border-border hover:text-foreground",
            )}
            onClick={() => setFilter(entry.id as typeof filter)}
          >
            {entry.label}
          </button>
        ))}
      </div>
      <div className="space-y-4">
        {filtered.length ? (
          filtered.map((item) => {
            const Icon = iconMap[item.type];
            return (
              <Card key={item.id} className="border-border/80 bg-background/40 p-4">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="rounded-xl border border-border/70 bg-background/40 p-2 text-foreground/80">
                    <Icon className="h-4 w-4" />
                  </span>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-foreground uppercase">{item.type}</p>
                    <p className="text-xs text-muted-foreground">{formatRelativeTime(item.createdAt)}</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" onClick={() => void promoteItem(item)}>
                      Use in Post
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => void deleteItem(item)}>
                      Delete
                    </Button>
                  </div>
                </div>
                <p className="mt-3 text-sm text-foreground/80">{item.content}</p>
                {item.type === "clip" && item.assetUrl ? (
                  <video
                    src={item.assetUrl}
                    controls
                    className="mt-3 h-48 w-full rounded-xl border border-border/70 object-cover"
                  />
                ) : null}
                {item.type === "trend" ? (
                  <Badge className="mt-3 w-fit bg-primary/15 text-foreground">
                    {item.trendScore ? `${item.trendScore.toUpperCase()} trend` : "Trend signal"}
                  </Badge>
                ) : null}
              </Card>
            );
          })
        ) : (
          <EmptyState label="No inbox items for this filter." />
        )}
      </div>
    </div>
  );
}

function TrendMinerView({
  trends,
  onUseInShortform,
  onUseInComposer,
}: {
  trends: {
    sounds: TrendItem[];
    formats: TrendItem[];
    memes: TrendItem[];
    genre: TrendItem[];
  };
  onUseInShortform: () => void;
  onUseInComposer: (trend: TrendItem) => void;
}) {
  const [tab, setTab] = useState<"all" | "sound" | "format" | "meme" | "genre">("all");
  const [selected, setSelected] = useState<TrendItem | null>(null);
  const combined = useMemo(
    () => [...trends.sounds, ...trends.formats, ...trends.memes, ...trends.genre],
    [trends],
  );
  const filtered =
    tab === "all"
      ? combined
      : combined.filter((item) => {
          if (tab === "sound") return item.type === "sound";
          if (tab === "format") return item.type === "format";
          if (tab === "meme") return item.type === "meme";
          if (tab === "genre") return item.type === "genre";
          return true;
        });

  const heatLabel = (score: number) => {
    if (score >= 75) return { label: "Hot", className: "bg-rose-500/20 text-rose-200" };
    if (score >= 55) return { label: "Warm", className: "bg-primary/15 text-foreground" };
    return { label: "Rising", className: "bg-sky-400/20 text-sky-100" };
  };

  const heatBuckets = [
    { label: "Sounds", score: trends.sounds[0]?.score ?? 0 },
    { label: "Formats", score: trends.formats[0]?.score ?? 0 },
    { label: "Memes", score: trends.memes[0]?.score ?? 0 },
    { label: "Genre", score: trends.genre[0]?.score ?? 0 },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold text-foreground">Trend Miner</h2>
          <p className="text-sm text-muted-foreground">Latest sounds, formats, and memes ready for remixing.</p>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-4">
        {heatBuckets.map((bucket) => {
          const heat = heatLabel(bucket.score);
          return (
            <Card key={bucket.label} className="border-border/80 bg-background/40 p-4">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-foreground">{bucket.label}</p>
                <Badge className={heat.className}>{heat.label}</Badge>
              </div>
              <p className="mt-2 text-3xl font-bold text-foreground">{bucket.score}</p>
              <p className="text-xs text-muted-foreground">Heat score</p>
            </Card>
          );
        })}
      </div>

      <Tabs value={tab} onValueChange={(value) => setTab(value as typeof tab)}>
        <TabsList>
          <TabsTrigger value="all">All</TabsTrigger>
          <TabsTrigger value="sound">Sounds</TabsTrigger>
          <TabsTrigger value="format">Formats</TabsTrigger>
          <TabsTrigger value="meme">Memes</TabsTrigger>
          <TabsTrigger value="genre">Genre Trends</TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="space-y-4">
        {filtered.length ? (
          filtered.map((trend) => {
            const heat = heatLabel(trend.score);
            return (
              <Card key={trend.id} className="border-border/80 bg-background/40 p-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-foreground">{trend.label}</p>
                    <p className="text-xs uppercase text-muted-foreground">{trend.platform}</p>
                  </div>
                  <Badge className={heat.className}>{heat.label}</Badge>
                </div>
                <p className="mt-2 text-sm text-foreground/70">{trend.recommendedUse ?? "Use this trend to boost reach."}</p>
                {trend.previewUrl ? (
                  trend.type === "sound" ? (
                    <audio controls src={trend.previewUrl} className="mt-3 w-full" />
                  ) : (
                    <img
                      src={trend.previewUrl}
                      alt={trend.label}
                      className="mt-3 h-40 w-full rounded-xl border border-border/70 object-cover"
                    />
                  )
                ) : null}
                <div className="mt-4 flex flex-wrap gap-2">
                  <Button size="sm" variant="secondary" onClick={onUseInShortform}>
                    Use in Short-Form Builder
                  </Button>
                  <Button size="sm" onClick={() => onUseInComposer(trend)}>
                    Create Post Using Trend
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setSelected(trend)}>
                    View insights
                  </Button>
                </div>
              </Card>
            );
          })
        ) : (
          <EmptyState label="No trend data available." />
        )}
      </div>

      <Sheet open={Boolean(selected)} onOpenChange={(open) => (!open ? setSelected(null) : undefined)}>
        <SheetContent side="right" className="w-full max-w-lg border-l border-border/70 bg-background">
          {selected ? (
            <>
              <SheetHeader>
                <SheetTitle>{selected.label}</SheetTitle>
              </SheetHeader>
              <div className="space-y-4 py-4">
                <p className="text-sm text-foreground/70">
                  {selected.description ??
                    "Examples show looping hooks, face-camera intros, and quick cuts aligned to the sound beats."}
                </p>
                <div className="rounded-xl border border-border/70 bg-background/30 p-3 text-sm text-foreground/70">
                  <p className="text-xs uppercase text-muted-foreground">Recommended structure</p>
                  <p>Intro hook → payoff clip → CTA overlay within 12 seconds.</p>
                </div>
                <div className="rounded-xl border border-border/70 bg-background/30 p-3 text-sm text-foreground/70">
                  <p className="text-xs uppercase text-muted-foreground">Suggested captions</p>
                  <ul className="list-disc pl-5 text-xs">
                    <li>Turn the trend into a backstage moment.</li>
                    <li>Drop a fan-favorite lyric as overlay text.</li>
                  </ul>
                </div>
                <div className="rounded-xl border border-border/70 bg-background/30 p-3 text-sm text-foreground/70">
                  <p className="text-xs uppercase text-muted-foreground">Hot window</p>
                  <p>{selected.durationEstimate ?? "Expected to stay hot for ~6 days"}</p>
                </div>
              </div>
            </>
          ) : null}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function GrowthPlannerView({
  kpis,
  goals,
  recommendations,
  mutateGoals,
  mutateRecommendations,
}: {
  kpis: MarketingKpis;
  goals: GrowthGoal[];
  recommendations: GrowthRecommendation[];
  mutateGoals?: KeyedMutator<GrowthGoal[] | undefined>;
  mutateRecommendations?: KeyedMutator<GrowthRecommendation[] | undefined>;
}) {
  const { push } = useToast();
  const [showForm, setShowForm] = useState(false);
  const [metric, setMetric] = useState<GrowthGoal["metric"]>("followers");
  const [amount, setAmount] = useState("500");
  const [timeframe, setTimeframe] = useState("30");
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);

  const handleCreateGoal = async () => {
    setSaving(true);
    try {
      await post("/marketing/growth/goals", {
        metric,
        amount: Number(amount),
        timeframe_days: Number(timeframe),
      });
      push({ title: "Growth goal added", message: "We'll monitor and adjust plans automatically." });
      await mutateGoals?.();
      setShowForm(false);
    } catch (error) {
      push({
        title: "Unable to add goal",
        message: error instanceof Error ? error.message : "Try again later.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleGeneratePlan = async () => {
    setGenerating(true);
    try {
      await post("/marketing/growth/recommendations", {});
      push({ title: "Weekly plan generated", message: "Tasks added to the marketing queue." });
      await mutateRecommendations?.();
    } catch (error) {
      push({
        title: "Unable to generate plan",
        message: error instanceof Error ? error.message : "Try again later.",
        variant: "destructive",
      });
    } finally {
      setGenerating(false);
    }
  };

  const activeRecommendation = recommendations[0];

  const kpiTiles = [
    { label: "Upcoming Posts", value: kpis.upcomingPosts },
    { label: "Engagement 24h", value: `${kpis.engagement24h}%` },
    { label: "New Followers", value: kpis.newFollowers24h },
    { label: "Pending Clips", value: kpis.pendingClips },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold text-foreground">Growth planner</h2>
          <p className="text-sm text-muted-foreground">Set measurable goals and let the agent plan cadence + content mix.</p>
        </div>
        <Button onClick={() => setShowForm(true)}>Set new goal</Button>
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        {kpiTiles.map((tile) => (
          <Card key={tile.label} className="border-border/80 bg-background/40 p-4">
            <p className="text-xs uppercase text-muted-foreground">{tile.label}</p>
            <p className="mt-1 text-2xl font-semibold text-foreground">{tile.value}</p>
          </Card>
        ))}
      </div>

      {showForm ? (
        <Card className="border-border/80 bg-background/40 p-4">
          <div className="grid gap-3 md:grid-cols-3">
            <div>
              <p className="text-xs uppercase text-muted-foreground">Metric</p>
              <Select value={metric} onValueChange={(value) => setMetric(value as GrowthGoal["metric"])}>
                <SelectTrigger className="mt-1 bg-background/40 text-foreground">
                  <SelectValue placeholder="Metric" />
                </SelectTrigger>
                <SelectContent className="bg-background/40 text-foreground">
                  <SelectItem value="followers">Followers</SelectItem>
                  <SelectItem value="engagement">Engagement</SelectItem>
                  <SelectItem value="views">Views</SelectItem>
                  <SelectItem value="clicks">Clicks</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <p className="text-xs uppercase text-muted-foreground">Amount</p>
              <Input
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
                className="mt-1 bg-background/40 text-foreground"
              />
            </div>
            <div>
              <p className="text-xs uppercase text-muted-foreground">Timeframe (days)</p>
              <Input
                value={timeframe}
                onChange={(event) => setTimeframe(event.target.value)}
                className="mt-1 bg-background/40 text-foreground"
              />
            </div>
          </div>
          <div className="mt-4 flex gap-2">
            <Button onClick={() => void handleCreateGoal()} disabled={saving}>
              {saving ? "Saving…" : "Save goal"}
            </Button>
            <Button variant="ghost" onClick={() => setShowForm(false)}>
              Cancel
            </Button>
          </div>
        </Card>
      ) : null}

      <div className="grid gap-4 md:grid-cols-2">
        {goals.map((goal) => {
          const totalTarget = goal.current + goal.amount;
          const progress = Math.min(goal.current / totalTarget, 1);
          return (
            <Card key={goal.id} className="border-border/80 bg-background/40 p-4">
              <p className="text-xs uppercase text-muted-foreground">{goal.metric}</p>
              <p className="mt-1 text-xl font-semibold text-foreground">
                {goal.current} → {goal.projected} ({goal.amount} target)
              </p>
              <div className="mt-3 h-2 rounded-full bg-muted">
                <div className="h-full rounded-full bg-emerald-400/80" style={{ width: `${progress * 100}%` }} />
              </div>
              <p className="mt-2 text-xs text-muted-foreground">{goal.timeframe_days}-day plan</p>
            </Card>
          );
        })}
      </div>

      <Card className="border-border/80 bg-background/40 p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-foreground">Weekly plan</p>
            <p className="text-xs text-muted-foreground">Posting cadence + formats tuned for goals.</p>
          </div>
          <Button onClick={() => void handleGeneratePlan()} disabled={generating}>
            {generating ? "Generating…" : "Generate Weekly Plan"}
          </Button>
        </div>
        {activeRecommendation ? (
          <div className="mt-4 grid gap-4 md:grid-cols-2">
            <div className="rounded-xl border border-border/70 bg-background/30 p-4">
              <p className="text-xs uppercase text-muted-foreground">Cadence</p>
              <p className="text-sm text-foreground">{activeRecommendation.postingFrequency}</p>
              <p className="mt-3 text-xs uppercase text-muted-foreground">Suggested formats</p>
              <div className="mt-2 flex flex-wrap gap-2">
                {activeRecommendation.suggestedFormats.map((format) => (
                  <Badge key={format} className="bg-muted text-foreground/80">
                    {format}
                  </Badge>
                ))}
              </div>
            </div>
            <div className="rounded-xl border border-border/70 bg-background/30 p-4">
              <p className="text-xs uppercase text-muted-foreground">Tasks</p>
              <ul className="mt-2 list-disc pl-5 text-sm text-foreground/70">
                {activeRecommendation.tasks.map((task) => (
                  <li key={task}>{task}</li>
                ))}
              </ul>
            </div>
          </div>
        ) : (
          <p className="mt-3 text-sm text-muted-foreground">Generate a plan to see suggested tasks.</p>
        )}
      </Card>
    </div>
  );
}

function BrandKitView({
  brandKit,
  mutateBrandKit,
  canEditAssets,
  onRequestUpgrade,
}: {
  brandKit: MarketingBrandKit;
  mutateBrandKit?: KeyedMutator<MarketingBrandKit | undefined>;
  canEditAssets: boolean;
  onRequestUpgrade: (message: string) => void;
}) {
  const { push } = useToast();
  const refresh = mutateBrandKit ?? (() => Promise.resolve(brandKit));
  const lightUploadRef = useRef<HTMLInputElement>(null);
  const darkUploadRef = useRef<HTMLInputElement>(null);
  type ExtendedTypography = MarketingBrandKit["typography"] & { headingSize?: number; bodySize?: number };
  const [colorForm, setColorForm] = useState({ hex: "#EAB308", label: "Accent" });
  const [addingColor, setAddingColor] = useState(false);
  const [savingTypography, setSavingTypography] = useState(false);
  const [typographySettings, setTypographySettings] = useState(brandKit.typography);
  const [headingSize, setHeadingSize] = useState<number>((brandKit.typography as ExtendedTypography).headingSize ?? 48);
  const [bodySize, setBodySize] = useState<number>((brandKit.typography as ExtendedTypography).bodySize ?? 18);
  const [elements, setElements] = useState<BrandReusableElement[]>(
    (brandKit as MarketingBrandKit & { reusableElements?: BrandReusableElement[] }).reusableElements ?? DEFAULT_REUSABLE_ELEMENTS,
  );
  const [elementForm, setElementForm] = useState({ name: "", usage: "" });

  useEffect(() => {
    const typography = brandKit.typography as ExtendedTypography;
    setTypographySettings(brandKit.typography);
    setHeadingSize(typography.headingSize ?? 48);
    setBodySize(typography.bodySize ?? 18);
    setElements(
      (brandKit as MarketingBrandKit & { reusableElements?: BrandReusableElement[] }).reusableElements ?? DEFAULT_REUSABLE_ELEMENTS,
    );
  }, [brandKit]);

  const FONT_OPTIONS = ["Space Grotesk", "Instrument Sans", "Clash Display", "Sora", "Inter"];
  const BODY_FONT_OPTIONS = ["Instrument Sans", "Inter", "General Sans", "Space Grotesk", "Work Sans"];
  const WEIGHT_OPTIONS = ["300", "400", "500", "600", "700"];

  const ensureEditable = () => {
    if (canEditAssets) return true;
    onRequestUpgrade("Brand kit edits require creative permissions.");
    return false;
  };

  const handleLogoUpload = async (variant: "light" | "dark", event: React.ChangeEvent<HTMLInputElement>) => {
    if (!ensureEditable()) {
      event.target.value = "";
      return;
    }
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const formData = new FormData();
      formData.append("variant", variant);
      formData.append("file", file);
      await api("/marketing/brand/logos", { method: "POST", body: formData });
      push({ title: "Logo uploaded", message: `${variant === "light" ? "Light" : "Dark"} mark refreshed.` });
      await refresh();
    } catch (error) {
      push({
        title: "Upload failed",
        message: error instanceof Error ? error.message : "Logo endpoint rejected the upload.",
        variant: "destructive",
      });
    } finally {
      event.target.value = "";
    }
  };

  const handleExportLogo = (variant: "light" | "dark", format: "png" | "svg") => {
    push({
      title: "Preparing download",
      message: `${variant === "light" ? "Light" : "Dark"} logo export (${format.toUpperCase()}) queued.`,
    });
  };

  const handleDownloadKit = () => {
    push({ title: "Brand kit queued", message: "Brand kit PDF export will be ready shortly." });
  };

  const handleAddColor = async () => {
    if (!ensureEditable()) return;
    const normalizedHex = colorForm.hex.startsWith("#") ? colorForm.hex : `#${colorForm.hex}`;
    if (!/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(normalizedHex)) {
      push({ title: "Invalid color", message: "Use a valid HEX value (e.g. #F97316).", variant: "destructive" });
      return;
    }
    setAddingColor(true);
    try {
      await post("/marketing/brand/colors", { hex: normalizedHex.toUpperCase(), label: colorForm.label || "Accent" });
      push({ title: "Color added", message: "Palette updated." });
      setColorForm({ hex: "#EAB308", label: "Accent" });
      await refresh();
    } catch (error) {
      push({
        title: "Unable to add color",
        message: error instanceof Error ? error.message : "Palette endpoint returned an error.",
        variant: "destructive",
      });
    } finally {
      setAddingColor(false);
    }
  };

  const handleDeleteColor = async (colorId: string) => {
    if (!ensureEditable()) return;
    try {
      await api(`/marketing/brand/colors/${colorId}`, { method: "DELETE" });
      push({ title: "Color removed", message: "Palette updated." });
      await refresh();
    } catch (error) {
      push({
        title: "Unable to remove color",
        message: error instanceof Error ? error.message : "Delete request failed.",
        variant: "destructive",
      });
    }
  };

  const handleTypographyChange = (key: keyof MarketingBrandKit["typography"], value: string) => {
    setTypographySettings((prev) => ({ ...prev, [key]: value }));
  };

  const handleSaveTypography = async () => {
    if (!ensureEditable()) return;
    setSavingTypography(true);
    try {
      await api("/marketing/brand/typography", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...typographySettings, headingSize, bodySize }),
      });
      push({ title: "Typography synced", message: "Defaults saved to the kit." });
      await refresh();
    } catch (error) {
      push({
        title: "Unable to save typography",
        message: error instanceof Error ? error.message : "Typography endpoint rejected the update.",
        variant: "destructive",
      });
    } finally {
      setSavingTypography(false);
    }
  };

  const persistElements = async (next: BrandReusableElement[]) => {
    setElements(next);
    if (mutateBrandKit) {
      await mutateBrandKit(
        (current) => ({
          ...(current ?? DEFAULT_BRAND_KIT),
          reusableElements: next,
        }),
        false,
      );
    }
  };

  const handleAddElement = async () => {
    if (!ensureEditable()) return;
    if (!elementForm.name.trim()) {
      push({ title: "Name required", message: "Give the reusable element a short name.", variant: "destructive" });
      return;
    }
    const newElement: BrandReusableElement = {
      id: `kit-${Date.now()}`,
      name: elementForm.name.trim(),
      usage: elementForm.usage.trim() || "Reusable component",
      lastUsed: new Date().toISOString(),
    };
    await persistElements([newElement, ...elements]);
    setElementForm({ name: "", usage: "" });
    push({ title: "Element added", message: "Reusable building block saved." });
  };

  const handleRemoveElement = async (elementId: string) => {
    if (!ensureEditable()) return;
    await persistElements(elements.filter((element) => element.id !== elementId));
    push({ title: "Element removed", message: "Component removed from the kit." });
  };

  const renderLogoVariant = (variant: "light" | "dark") => {
    const stored = brandKit.logos.find((logo) => logo.variant === variant);
    const inputRef = variant === "light" ? lightUploadRef : darkUploadRef;
    return (
      <div key={variant} className="space-y-3 rounded-2xl border border-border/70 bg-background/40 p-4">
        <div
          className={cn(
            "flex h-40 items-center justify-center rounded-xl border text-sm",
            variant === "light" ? "border-border bg-background text-muted-foreground/70" : "border-border bg-background/40 text-foreground/70",
          )}
        >
          {stored ? (
            <img src={stored.url} alt={stored.label ?? `${variant} logo`} className="max-h-28 max-w-full object-contain" />
          ) : (
            <p>No {variant} logo yet.</p>
          )}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <p className="text-sm font-semibold text-foreground capitalize">{variant} variant</p>
            <p className="text-xs text-muted-foreground">PNG + SVG supported</p>
          </div>
          {canEditAssets ? (
            <Button
              size="sm"
              onClick={() => {
                if (!ensureEditable()) return;
                inputRef.current?.click();
              }}
            >
              Upload
            </Button>
          ) : (
            <Badge variant="outline" className="border-border/70 text-foreground/70">
              Read only
            </Badge>
          )}
        </div>
        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/svg+xml"
          className="hidden"
          onChange={(event) => void handleLogoUpload(variant, event)}
        />
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={() => handleExportLogo(variant, "png")}>
            Export PNG
          </Button>
          <Button size="sm" variant="outline" onClick={() => handleExportLogo(variant, "svg")}>
            Export SVG
          </Button>
        </div>
      </div>
    );
  };

  const lastUpdated = brandKit.updatedAt ? formatDate(brandKit.updatedAt, true) : "Not updated yet";

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold text-foreground">Brand kit</h2>
          <p className="text-sm text-muted-foreground">Keep logos, palettes, typography, and reusable assets aligned.</p>
          <p className="text-xs text-muted-foreground">Last updated {lastUpdated}</p>
        </div>
        <Button variant="outline" onClick={handleDownloadKit}>
          Download Brand Kit PDF
        </Button>
      </div>

      <Card className="border-border/80 bg-background/40 p-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Logo management</p>
            <p className="text-sm text-foreground/70">Light + dark marks for fast exports.</p>
          </div>
          <Badge className="bg-primary/15 text-foreground">Export ready</Badge>
        </div>
        <div className="mt-4 grid gap-4 md:grid-cols-2">{["light", "dark"].map((variant) => renderLogoVariant(variant as "light" | "dark"))}</div>
      </Card>

      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2 border-border/80 bg-background/40 p-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Color palette</p>
              <p className="text-sm text-foreground/70">Accessible swatches for posts, merch, and decks.</p>
            </div>
            <p className="text-xs text-muted-foreground">POST / DELETE API backed</p>
          </div>
          {brandKit.colors.length ? (
            <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {brandKit.colors.map((color) => (
                <div key={color.id} className="rounded-2xl border border-border/70 bg-background/60 p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="h-10 w-10 rounded-lg border border-border" style={{ backgroundColor: color.hex }} />
                      <div>
                        <p className="text-sm font-semibold text-foreground">{color.label ?? "Swatch"}</p>
                        <p className="font-mono text-xs text-muted-foreground">{color.hex.toUpperCase()}</p>
                      </div>
                    </div>
                    {canEditAssets ? (
                      <Button size="icon" variant="ghost" onClick={() => void handleDeleteColor(color.id)}>
                        <X className="h-4 w-4" />
                      </Button>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="mt-4">
              <EmptyState label="No palette colors yet." />
            </div>
          )}
          <div className="mt-4 flex flex-wrap items-center gap-3 rounded-2xl border border-dashed border-border/70 p-4">
            <input
              type="color"
              value={colorForm.hex}
              onChange={(event) => setColorForm((prev) => ({ ...prev, hex: event.target.value }))}
              className="h-12 w-12 cursor-pointer rounded-lg border border-border bg-background/40"
              disabled={!canEditAssets}
            />
            <Input
              value={colorForm.label}
              onChange={(event) => setColorForm((prev) => ({ ...prev, label: event.target.value }))}
              placeholder="Swatch label"
              className="flex-1 bg-background/40 text-foreground"
              disabled={!canEditAssets}
            />
            <Button onClick={() => void handleAddColor()} disabled={!canEditAssets || addingColor}>
              {addingColor ? "Saving…" : "Add color"}
            </Button>
          </div>
        </Card>

        <Card className="border-border/80 bg-background/40 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Reusable elements</p>
              <p className="text-sm text-foreground/70">CTA lockups, lower thirds, reels overlays.</p>
            </div>
            <Badge variant="outline" className="border-border/70 text-foreground/80">
              {elements.length} saved
            </Badge>
          </div>
          <div className="mt-4 space-y-3">
            {elements.map((element) => (
              <div key={element.id} className="rounded-xl border border-border/70 bg-background/30 p-3 text-sm text-foreground/80">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="font-semibold text-foreground">{element.name}</p>
                    <p className="text-xs text-muted-foreground">{element.usage}</p>
                    {element.lastUsed ? <p className="text-xs text-muted-foreground/70">Used {formatRelativeTime(element.lastUsed)}</p> : null}
                  </div>
                  {canEditAssets ? (
                    <Button size="icon" variant="ghost" onClick={() => void handleRemoveElement(element.id)}>
                      <X className="h-4 w-4" />
                    </Button>
                  ) : null}
                </div>
              </div>
            ))}
            {elements.length === 0 ? <EmptyState label="No reusable components yet." /> : null}
          </div>
          <div className="mt-4 space-y-2">
            <Input
              value={elementForm.name}
              onChange={(event) => setElementForm((prev) => ({ ...prev, name: event.target.value }))}
              placeholder="Element name"
              className="bg-background/40 text-foreground"
              disabled={!canEditAssets}
            />
            <Input
              value={elementForm.usage}
              onChange={(event) => setElementForm((prev) => ({ ...prev, usage: event.target.value }))}
              placeholder="Usage notes"
              className="bg-background/40 text-foreground"
              disabled={!canEditAssets}
            />
            <Button className="w-full" disabled={!canEditAssets} onClick={() => void handleAddElement()}>
              Save reusable element
            </Button>
          </div>
        </Card>
      </div>

      <Card className="border-border/80 bg-background/40 p-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Typography presets</p>
            <p className="text-sm text-foreground/70">Lock in heading + body defaults for templates.</p>
          </div>
          <Button onClick={() => void handleSaveTypography()} disabled={!canEditAssets || savingTypography}>
            {savingTypography ? "Saving…" : "Save defaults"}
          </Button>
        </div>
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <div className="space-y-3 rounded-2xl border border-border/60 bg-background/30 p-4">
            <p className="text-sm font-semibold text-foreground">Heading styles</p>
            <Select value={typographySettings.headingFont} onValueChange={(value) => handleTypographyChange("headingFont", value)} disabled={!canEditAssets}>
              <SelectTrigger className="bg-background/40 text-foreground">
                <SelectValue placeholder="Heading font" />
              </SelectTrigger>
              <SelectContent>
                {FONT_OPTIONS.map((font) => (
                  <SelectItem key={font} value={font}>
                    {font}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={typographySettings.headingWeight} onValueChange={(value) => handleTypographyChange("headingWeight", value)} disabled={!canEditAssets}>
              <SelectTrigger className="bg-background/40 text-foreground">
                <SelectValue placeholder="Heading weight" />
              </SelectTrigger>
              <SelectContent>
                {WEIGHT_OPTIONS.map((weight) => (
                  <SelectItem key={weight} value={weight}>
                    {weight}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div>
              <p className="text-xs text-muted-foreground">Default size ({headingSize}px)</p>
              <Slider value={[headingSize]} min={24} max={96} step={2} onValueChange={([value]) => setHeadingSize(value)} disabled={!canEditAssets} />
              <p
                className="mt-3 font-semibold text-foreground"
                style={{ fontFamily: typographySettings.headingFont, fontWeight: Number(typographySettings.headingWeight), fontSize: `${headingSize}px` }}
              >
                Preview headline
              </p>
            </div>
          </div>
          <div className="space-y-3 rounded-2xl border border-border/60 bg-background/30 p-4">
            <p className="text-sm font-semibold text-foreground">Body styles</p>
            <Select value={typographySettings.bodyFont} onValueChange={(value) => handleTypographyChange("bodyFont", value)} disabled={!canEditAssets}>
              <SelectTrigger className="bg-background/40 text-foreground">
                <SelectValue placeholder="Body font" />
              </SelectTrigger>
              <SelectContent>
                {BODY_FONT_OPTIONS.map((font) => (
                  <SelectItem key={font} value={font}>
                    {font}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={typographySettings.bodyWeight} onValueChange={(value) => handleTypographyChange("bodyWeight", value)} disabled={!canEditAssets}>
              <SelectTrigger className="bg-background/40 text-foreground">
                <SelectValue placeholder="Body weight" />
              </SelectTrigger>
              <SelectContent>
                {WEIGHT_OPTIONS.map((weight) => (
                  <SelectItem key={weight} value={weight}>
                    {weight}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div>
              <p className="text-xs text-muted-foreground">Default size ({bodySize}px)</p>
              <Slider value={[bodySize]} min={12} max={32} step={1} onValueChange={([value]) => setBodySize(value)} disabled={!canEditAssets} />
              <p
                className="mt-3 text-foreground/80"
                style={{ fontFamily: typographySettings.bodyFont, fontWeight: Number(typographySettings.bodyWeight), fontSize: `${bodySize}px`, lineHeight: 1.45 }}
              >
                Body copy preview for captions, briefs, and decks.
              </p>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}

function AssetsView({
  tasks,
  onOpenComposer,
  canEditAssets,
  onRequestUpgrade,
}: {
  tasks: MarketingTask[];
  onOpenComposer: (options?: Partial<ComposerState>) => void;
  canEditAssets: boolean;
  onRequestUpgrade: (message: string) => void;
}) {
  const { push } = useToast();
  const { data: assets, isLoading, mutate: mutateAssets } = useSWR<MarketingAsset[]>("/marketing/assets", fetcher);
  const { data: folders, mutate: mutateFolders } = useSWR<MarketingAssetFolder[]>("/marketing/assets/folders", fetcher);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({
    neededSoon: false,
    usedRecently: false,
    hasCampaign: false,
  });
  const [typeFilters, setTypeFilters] = useState<Set<MarketingAsset["type"]>>(new Set());
  const [selectedFolderId, setSelectedFolderId] = useState<string>("all");
  const [newFolderName, setNewFolderName] = useState("");
  const [creatingFolder, setCreatingFolder] = useState(false);
  const folderSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  const upcomingCampaigns = useMemo(() => {
    const upcoming = new Set<string>();
    const now = new Date();
    const weekAhead = new Date();
    weekAhead.setDate(now.getDate() + 7);
    tasks.forEach((task) => {
      if (!task.campaignLabel) return;
      const date = new Date(task.scheduledFor ?? task.dueAt ?? "");
      if (date >= now && date <= weekAhead) {
        upcoming.add(task.campaignLabel);
      }
    });
    return upcoming;
  }, [tasks]);

  const filteredAssets = useMemo(() => {
    const list = assets ?? [];
    return list.filter((asset) => {
      if (selectedFolderId !== "all") {
        const folderMatch = selectedFolderId === "uncategorized" ? !asset.folderId : asset.folderId === selectedFolderId;
        if (!folderMatch) return false;
      }
      if (search) {
        const query = search.toLowerCase();
        const matches =
          asset.tags.some((tag) => tag.toLowerCase().includes(query)) ||
          asset.type.toLowerCase().includes(query);
        if (!matches) return false;
      }
      if (filters.neededSoon) {
        const related = asset.relatedCampaigns ?? [];
        if (!related.some((campaign) => upcomingCampaigns.has(campaign))) return false;
      }
      if (filters.usedRecently) {
        const lastUsed = asset.lastUsedAt ? new Date(asset.lastUsedAt) : null;
        const threshold = new Date();
        threshold.setDate(threshold.getDate() - 7);
        if (!lastUsed || lastUsed < threshold) return false;
      }
      if (filters.hasCampaign && !(asset.relatedCampaigns?.length)) return false;
      if (typeFilters.size > 0 && !typeFilters.has(asset.type)) return false;
      return true;
    });
  }, [assets, filters, upcomingCampaigns, typeFilters, search, selectedFolderId]);

  const toggleFilter = (key: keyof typeof filters) => {
    setFilters((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleType = (type: MarketingAsset["type"]) => {
    setTypeFilters((prev) => {
      const next = new Set(prev);
      if (next.has(type)) {
        next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
  };

  const handleReset = () => {
    setSearch("");
    setFilters({ neededSoon: false, usedRecently: false, hasCampaign: false });
    setTypeFilters(new Set());
    setSelectedFolderId("all");
  };

  const handleAutoTag = async (asset: MarketingAsset) => {
    try {
      await post(`/marketing/assets/${asset.id}/tag`, {});
      push({ title: "Auto-tagged", message: "Tags refreshed for this asset." });
      await mutateAssets();
    } catch (error) {
      push({
        title: "Tagging failed",
        message: error instanceof Error ? error.message : "Unable to auto-tag asset.",
        variant: "destructive",
      });
    }
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    if (!canEditAssets) {
      onRequestUpgrade("Creating folders requires creative permissions.");
      return;
    }
    setCreatingFolder(true);
    try {
      await post("/marketing/assets/folders", { name: newFolderName.trim() });
      push({ title: "Folder created", message: "Asset library updated." });
      setNewFolderName("");
      await mutateFolders();
    } catch (error) {
      push({
        title: "Unable to create folder",
        message: error instanceof Error ? error.message : "Try again later.",
        variant: "destructive",
      });
    } finally {
      setCreatingFolder(false);
    }
  };

  const handleAssetDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || !String(over.id).startsWith("folder-")) return;
    if (!canEditAssets) {
      onRequestUpgrade("Moving assets requires creative permissions.");
      return;
    }
    const folderId = String(over.id).replace("folder-", "");
    if (folderId === "all") return;
    const assetId = String(active.id);
    try {
      await api("/marketing/assets/move", {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ assetId, folderId: folderId === "uncategorized" ? null : folderId }),
      });
      push({ title: "Asset moved", message: "Folder assignment updated." });
      await mutateAssets();
      await mutateFolders();
    } catch (error) {
      push({
        title: "Unable to move asset",
        message: error instanceof Error ? error.message : "Try again later.",
        variant: "destructive",
      });
    }
  };

  const safeFolders = folders ?? DEFAULT_ASSET_FOLDERS;
  const folderAssetCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    (assets ?? []).forEach((asset) => {
      const key = asset.folderId ?? "uncategorized";
      counts[key] = (counts[key] ?? 0) + 1;
    });
    return counts;
  }, [assets]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold text-foreground">Asset library</h2>
          <p className="text-sm text-muted-foreground">Search clips, posters, and templates, then drag cards into folders to prep upcoming drops.</p>
        </div>
        <Button variant="secondary" onClick={() => onOpenComposer({})}>
          Use in new post
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-border/70 bg-background/40 p-4">
        <Input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search tags, folders, or types…"
          className="flex-1 bg-background/40 text-foreground"
        />
        <div className="flex flex-wrap gap-2">
          {[
            { key: "neededSoon", label: "Needed Soon" },
            { key: "usedRecently", label: "Used Recently" },
            { key: "hasCampaign", label: "Campaign" },
          ].map((filter) => (
            <Button
              key={filter.key}
              size="sm"
              variant={filters[filter.key as keyof typeof filters] ? "secondary" : "outline"}
              onClick={() => toggleFilter(filter.key as keyof typeof filters)}
            >
              {filter.label}
            </Button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2">
          {[
            { id: "image", label: "Images" },
            { id: "poster", label: "Posters" },
            { id: "template", label: "Templates" },
          ].map((type) => (
            <Button
              key={type.id}
              size="sm"
              variant={typeFilters.has(type.id as MarketingAsset["type"]) ? "secondary" : "outline"}
              onClick={() => toggleType(type.id as MarketingAsset["type"])}
            >
              {type.label}
            </Button>
          ))}
        </div>
        <Button variant="ghost" onClick={handleReset}>
          Reset filters
        </Button>
      </div>

      <DndContext sensors={folderSensors} collisionDetection={closestCenter} onDragEnd={handleAssetDragEnd}>
        <div className="grid gap-4 lg:grid-cols-[220px,1fr]">
        <Card className="border-border/80 bg-background/40 p-4">
          <p className="text-sm font-semibold text-foreground">Folders</p>
          <div className="mt-3 space-y-2">
            {[
              { id: "all", name: "All Assets" },
              ...safeFolders,
              { id: "uncategorized", name: "Uncategorized" },
            ].map((folder) => (
              <FolderDropZone key={folder.id} folderId={folder.id}>
                <button
                  type="button"
                  onClick={() => setSelectedFolderId(folder.id)}
                  className={cn(
                    "flex w-full items-center justify-between rounded-lg border px-3 py-2 text-left text-sm",
                    selectedFolderId === folder.id
                      ? "border-emerald-400/70 bg-emerald-400/10 text-foreground"
                      : "border-border bg-background/30 text-foreground/70 hover:border-border",
                  )}
                >
                  <span>{folder.name}</span>
                  <span className="text-xs text-muted-foreground">
                    {folder.id === "all"
                      ? (assets ?? []).length
                      : folder.id === "uncategorized"
                        ? folderAssetCounts["uncategorized"] ?? 0
                        : folder.itemCount ?? folderAssetCounts[folder.id] ?? 0}
                  </span>
                </button>
              </FolderDropZone>
            ))}
          </div>
          <div className="mt-4 space-y-2">
            <Input
              value={newFolderName}
              onChange={(event) => setNewFolderName(event.target.value)}
              placeholder="New folder name"
              className="bg-background/40 text-foreground"
            />
            <Button onClick={handleCreateFolder} disabled={!newFolderName.trim() || !canEditAssets || creatingFolder}>
              {creatingFolder ? "Creating…" : "Create Folder"}
            </Button>
          </div>
        </Card>

        <div>
          {isLoading ? (
            <Card className="border-border/80 bg-background/40 p-6 text-center text-sm text-muted-foreground">
              Loading assets…
            </Card>
          ) : filteredAssets.length ? (
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {filteredAssets.map((asset) => (
                <AssetDraggable key={asset.id} assetId={asset.id}>
                  <Card className="border-border/80 bg-background/40 p-4">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-semibold text-foreground">{asset.type.toUpperCase()}</p>
                      <span className="text-xs text-muted-foreground">{formatRelativeTime(asset.createdAt)}</span>
                    </div>
                    <div className="mt-3 h-40 w-full overflow-hidden rounded-xl border border-border/70 bg-background/30">
                      <img src={asset.url} alt="" className="h-full w-full object-cover" />
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2 text-xs text-foreground/70">
                      {asset.tags.map((tag) => (
                        <Badge key={tag} variant="outline" className="border-border/70 text-foreground/80">
                          #{tag}
                        </Badge>
                      ))}
                    </div>
                    <div className="mt-4 flex flex-wrap gap-2 text-xs text-muted-foreground">
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() =>
                          onOpenComposer({
                            initialAsset: {
                              id: asset.id,
                              type: asset.type === "thumbnail" ? "clip" : "image",
                              title: asset.tags[0] ?? asset.type,
                              thumbnailUrl: asset.url,
                              url: asset.url,
                            },
                          })
                        }
                      >
                        Use in Post
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => void handleAutoTag(asset)}>
                        Auto-Tag
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Created {formatDate(asset.createdAt)} · {asset.lastUsedAt ? `Used ${formatRelativeTime(asset.lastUsedAt)}` : "Not used yet"}
                    </p>
                  </Card>
                </AssetDraggable>
              ))}
            </div>
          ) : (
            <EmptyState label="No assets match your filters." />
          )}
        </div>
        </div>
      </DndContext>
    </div>
  );
}
function CaptionsView() {
  return (
    <PlaceholderPanel title="Captions view placeholder" description="Draft copy variants and localization pipelines." />
  );
}

function NotificationsDrawer({
  open,
  onOpenChange,
  notifications,
  loading = false,
  onOpenLifecycle,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  notifications: NotificationItem[];
  loading?: boolean;
  onOpenLifecycle?: (taskId: string) => void;
}) {
  const [sourceFilter, setSourceFilter] = useState<NotificationSource | "all">("all");
  const sorted = useMemo(
    () => [...notifications].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()),
    [notifications],
  );
  const filtered = useMemo(() => {
    if (sourceFilter === "all") {
      return sorted;
    }
    return sorted.filter((item) => (item.source ?? "marketing") === sourceFilter);
  }, [sorted, sourceFilter]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full max-w-md border-l border-border/70 bg-background/95">
        <SheetHeader>
          <SheetTitle>Notifications</SheetTitle>
        </SheetHeader>
        <div className="mt-4 space-y-3">
          <div className="flex flex-wrap gap-2">
            {NOTIFICATION_SOURCE_FILTERS.map((option) => (
              <Button
                key={option.value}
                size="sm"
                variant={option.value === sourceFilter ? "default" : "outline"}
                className={option.value === sourceFilter ? "bg-primary/70 text-foreground" : "text-foreground/80"}
                onClick={() => setSourceFilter(option.value)}
              >
                {option.label}
              </Button>
            ))}
          </div>
        </div>
        <div className="mt-4 space-y-3 overflow-y-auto pr-2">
          {loading ? (
            <NotificationsSkeleton />
          ) : filtered.length ? (
            filtered.map((notification) => (
              <div key={notification.id} className="rounded-lg border border-border/80 bg-background/40 p-4">
                <div className="flex items-center justify-between gap-2 text-sm">
                  <p className="font-medium text-foreground">{notification.title}</p>
                  <Badge className={cn("capitalize", SEVERITY_BADGE_STYLES[notification.severity])}>
                    {notification.severity}
                  </Badge>
                </div>
                  <p className="mt-1 text-xs text-muted-foreground">{formatRelativeTime(notification.createdAt)}</p>
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <Badge variant="outline" className="border-border/70 text-foreground/80">
                      {NOTIFICATION_TYPE_META[notification.type].label}
                    </Badge>
                    <Badge variant="outline" className="border-border/70 text-foreground/80">
                      {NOTIFICATION_SOURCE_LABELS[notification.source ?? "marketing"]}
                    </Badge>
                    {notification.relatedCampaignLabel ? (
                      <Badge variant="outline" className="border-primary/60 text-foreground">
                        {notification.relatedCampaignLabel}
                      </Badge>
                    ) : null}
                </div>
                <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
                  <p className="flex-1 text-left">{notification.message}</p>
                  <div className="flex gap-2">
                    {notification.relatedTaskId ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 px-3 text-xs"
                        onClick={() => onOpenLifecycle?.(notification.relatedTaskId!)}
                      >
                        Lifecycle
                      </Button>
                    ) : null}
                    <Button size="sm" variant="secondary" className="h-7 px-3 text-xs">
                      Open
                    </Button>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <EmptyState label="No notifications yet." />
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function DMResponseDrawer({ item, onClose }: { item: EngagementItem | null; onClose: () => void }) {
  const { push } = useToast();
  const [reply, setReply] = useState("");

  useEffect(() => {
    if (item) {
      setReply(`Hey ${item.username}, appreciate you reaching out!`);
    } else {
      setReply("");
    }
  }, [item]);

  const handleSend = () => {
    if (!item) return;
    push({ title: "Reply sent", message: `Queued DM response to @${item.username}.` });
    setReply("");
    onClose();
  };

  const handleAddToCRM = () => {
    if (!item) return;
    push({ title: "Contact synced", message: `@${item.username} noted for CRM follow-ups.` });
  };

  return (
    <Sheet open={Boolean(item)} onOpenChange={(open) => (!open ? onClose() : undefined)}>
      <SheetContent side="right" className="flex w-full max-w-md flex-col gap-4 border-l border-border/70 bg-background/95">
        <SheetHeader>
          <SheetTitle>{item ? `Reply to @${item.username}` : "DM response"}</SheetTitle>
          <p className="text-sm text-muted-foreground">Keep the convo moving without leaving Marketing.</p>
        </SheetHeader>
        {item ? (
          <div className="space-y-4 overflow-y-auto pr-2">
            <div className="rounded-2xl border border-border/80 bg-background/40 p-4 text-sm text-foreground/70">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Incoming message</p>
              <p className="mt-2 text-foreground">{item.message}</p>
              <p className="mt-1 text-xs text-muted-foreground">{formatRelativeTime(item.createdAt)}</p>
            </div>
            {item.postPreviewUrl ? (
              <div className="rounded-2xl border border-border/80 bg-background/40 p-3">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Context post</p>
                <img src={item.postPreviewUrl} alt="" className="mt-2 w-full rounded-xl object-cover" />
              </div>
            ) : null}
            <div className="space-y-2">
              <label className="text-xs uppercase tracking-wide text-muted-foreground">Reply</label>
              <Textarea
                value={reply}
                onChange={(event) => setReply(event.target.value)}
                rows={4}
                className="bg-background/40 text-foreground"
                placeholder="Draft your response…"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              <Button onClick={handleSend} disabled={!reply.trim()}>
                Send reply
              </Button>
              <Button variant="outline" onClick={handleAddToCRM}>
                Add to CRM
              </Button>
              <Button variant="ghost" onClick={onClose}>
                Cancel
              </Button>
            </div>
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}

function ReplyAssistantDrawer({
  item,
  onClose,
  onRespond,
}: {
  item: EngagementItem | null;
  onClose: () => void;
  onRespond: () => void;
}) {
  return (
    <Sheet open={Boolean(item)} onOpenChange={(open) => (!open ? onClose() : undefined)}>
      <SheetContent side="right" className="flex w-full max-w-md flex-col gap-4 border-l border-border/70 bg-background/95">
        <SheetHeader>
          <SheetTitle>{item ? `Reply to @${item.username}` : "Comment responder"}</SheetTitle>
          <p className="text-sm text-muted-foreground">Queue AI-assisted responses with optional approvals.</p>
        </SheetHeader>
        {item ? (
          <div className="overflow-y-auto pr-2">
            <CommentResponder
              platform={item.platform}
              postId={item.postId ?? item.id}
              commentId={item.id}
              commentText={item.message}
              commenter={item.username}
              createdAt={item.createdAt}
              previewUrl={item.postPreviewUrl}
              onSuccess={() => {
                onRespond();
                onClose();
              }}
            />
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}

type ReplayGeneratorDrawerProps = {
  open: boolean;
  source: ReplaySourcePost | null;
  onClose: () => void;
  onSendToComposer: (options?: Partial<ComposerState>) => void;
  refreshSources: KeyedMutator<ReplaySourcePost[]>;
  permissions: MarketingPermissions;
  onRequestUpgrade: (message: string) => void;
};

function ReplayGeneratorDrawer({
  open,
  source,
  onClose,
  onSendToComposer,
  refreshSources,
  permissions,
  onRequestUpgrade,
}: ReplayGeneratorDrawerProps) {
  const { push } = useToast();
  const [generation, setGeneration] = useState<ReplayGeneration>(() => ({
    caption: "",
    tone: "hype",
    cta: CTA_PRESETS[0]?.label ?? "Watch now",
    recommendedTime: new Date().toISOString(),
    templateId: "",
  }));

  useEffect(() => {
    if (!open) return;
    setGeneration((prev) => ({
      ...prev,
      caption: source?.caption ?? "",
      recommendedTime: new Date().toISOString(),
    }));
  }, [open, source]);

  const handleSend = () => {
    if (!source) return;
    if (!permissions.canSchedule) {
      onRequestUpgrade("Scheduling posts requires an upgraded plan.");
      return;
    }
    onSendToComposer({
      initialAsset: {
        id: `replay-${source.id}`,
        type: "clip",
        title: source.title,
        thumbnailUrl: source.thumbnailUrl,
        url: source.thumbnailUrl,
        tags: [source.platform],
      },
      initialDatetime: generation.recommendedTime,
    });
    push({ title: "Replay ready", message: `${source.title} copied into the composer.` });
    void refreshSources();
    onClose();
  };

  const platformLabel = source
    ? platformLabels[source.platform as PlatformKey] ?? source.platform
    : "";

  return (
    <Sheet open={open} onOpenChange={(value) => (!value ? onClose() : undefined)}>
      <SheetContent side="right" className="flex w-full max-w-md flex-col gap-4 border-l border-border/70 bg-background/95">
        <SheetHeader>
          <SheetTitle>Replay generator</SheetTitle>
          <p className="text-sm text-muted-foreground">Spin a top performer into new composer-ready assets.</p>
        </SheetHeader>
        <div className="space-y-4 overflow-y-auto pr-2">
          {source ? (
            <div className="space-y-4">
              <div className="space-y-3">
                <div className="rounded-2xl border border-border/70 bg-background/30">
                  {source.thumbnailUrl ? (
                    <img
                      src={source.thumbnailUrl}
                      alt={source.title}
                      className="h-44 w-full rounded-2xl object-cover"
                    />
                  ) : (
                    <div className="flex h-44 items-center justify-center rounded-2xl text-sm text-muted-foreground">
                      Preview unavailable
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-xs uppercase tracking-wide text-muted-foreground">Original post</p>
                      <p className="text-lg font-semibold text-foreground">{source.title}</p>
                      <p className="text-xs text-muted-foreground">
                        {source.postedAt ? formatRelativeTime(source.postedAt) : "Published date unavailable"}
                      </p>
                    </div>
                    <Badge variant="outline" className="text-xs uppercase tracking-wide text-foreground">
                      {platformLabel}
                    </Badge>
                  </div>
                  {source.caption ? <p className="text-sm text-foreground/70 line-clamp-3">{source.caption}</p> : null}
                  <div className="flex flex-wrap gap-2 text-xs text-foreground/70">
                    <Badge variant="outline" className="border-border text-foreground/80">
                      Engagement {Math.round((source.engagementScore ?? 0) * 100)}%
                    </Badge>
                    {typeof source.reach === "number" ? (
                      <Badge variant="outline" className="border-border text-foreground/80">
                        Reach {source.reach.toLocaleString()}
                      </Badge>
                    ) : null}
                    {typeof source.saves === "number" ? (
                      <Badge variant="outline" className="border-border text-foreground/80">
                        Saves {source.saves}
                      </Badge>
                    ) : null}
                    {typeof source.comments === "number" ? (
                      <Badge variant="outline" className="border-border text-foreground/80">
                        Comments {source.comments}
                      </Badge>
                    ) : null}
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <div className="space-y-2">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Caption</p>
                  <Textarea
                    rows={3}
                    className="bg-background/80 text-foreground"
                    value={generation.caption}
                    onChange={(event) => setGeneration((prev) => ({ ...prev, caption: event.target.value }))}
                    placeholder="Customize the caption for this replay."
                  />
                </div>
                <div className="space-y-2">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">CTA</p>
                  <Input
                    className="bg-background/80 text-foreground"
                    value={generation.cta}
                    onChange={(event) => setGeneration((prev) => ({ ...prev, cta: event.target.value }))}
                    placeholder="CTA text"
                  />
                  <div className="flex flex-wrap gap-2">
                    {CTA_PRESETS.map((preset) => (
                      <Button
                        key={preset.id}
                        size="sm"
                        variant="outline"
                        className={cn(
                          "rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-wide",
                          preset.label === generation.cta
                            ? "border-primary/70 bg-primary/15 text-foreground"
                            : "border-border text-foreground/70 hover:border-border",
                        )}
                        onClick={() => setGeneration((prev) => ({ ...prev, cta: preset.label }))}
                      >
                        {preset.label}
                      </Button>
                    ))}
                  </div>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-2">
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">Tone</p>
                    <Select
                      value={generation.tone}
                      onValueChange={(value) => setGeneration((prev) => ({ ...prev, tone: value as TonePreset }))}
                    >
                      <SelectTrigger className="bg-background/80 text-foreground">
                        <SelectValue placeholder="Choose a tone" />
                      </SelectTrigger>
                      <SelectContent className="bg-background text-foreground">
                        {TONE_PRESETS.map((tone) => (
                          <SelectItem key={tone} value={tone}>
                            {toneLabels[tone]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">Schedule time</p>
                    <Input
                      type="datetime-local"
                      className="bg-background/80 text-foreground"
                      value={formatDateTimeInput(generation.recommendedTime)}
                      onChange={(event) => {
                        const candidate = new Date(event.target.value);
                        if (!Number.isNaN(candidate.getTime())) {
                          setGeneration((prev) => ({ ...prev, recommendedTime: candidate.toISOString() }));
                        }
                      }}
                    />
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 pt-3">
                <Button className="flex-1" onClick={handleSend}>
                  Send to composer
                </Button>
                <Button variant="outline" className="text-foreground/80" onClick={() => void refreshSources()}>
                  Refresh sources
                </Button>
                <Button variant="ghost" className="text-foreground/70" onClick={onClose}>
                  Close
                </Button>
              </div>
            </div>
          ) : (
            <EmptyState label="Select a replay source from the Replay view to start generating fresh assets." />
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

type PostLifecycleStage = "early" | "mid" | "declining" | "evergreen";

type PostLifecycleMetrics = {
  awareness: {
    impressions: number;
    reach: number;
    watchTime: number;
    retention: Array<{ label: string; rate: number }>;
  };
  engagement: {
    likes: number;
    comments: number;
    saves: number;
    shares: number;
    engagementRate: number;
    velocity: number;
  };
  conversions: {
    linkClicks: number;
    ticketSales: number;
    merchClicks: number;
    spotifyOpens: number;
  };
  audience: {
    segments: Array<{ label: string; value: number }>;
    regions: Array<{ label: string; value: number }>;
    devices: Array<{ label: string; value: number }>;
  };
  stage: PostLifecycleStage;
};

const DEFAULT_RETENTION_POINTS: Array<{ label: string; rate: number }> = [
  { label: "0s", rate: 100 },
  { label: "3s", rate: 82 },
  { label: "6s", rate: 58 },
  { label: "9s", rate: 42 },
  { label: "12s", rate: 31 },
  { label: "15s", rate: 22 },
];

const DEFAULT_POST_LIFECYCLE: PostLifecycleMetrics = {
  awareness: {
    impressions: 0,
    reach: 0,
    watchTime: 0,
    retention: [],
  },
  engagement: {
    likes: 0,
    comments: 0,
    saves: 0,
    shares: 0,
    engagementRate: 0,
    velocity: 0,
  },
  conversions: {
    linkClicks: 0,
    ticketSales: 0,
    merchClicks: 0,
    spotifyOpens: 0,
  },
  audience: {
    segments: [],
    regions: [],
    devices: [],
  },
  stage: "early",
};

const LIFECYCLE_STAGE_META: Record<
  PostLifecycleStage,
  { label: string; description: string; dotClass: string; badgeClass: string }
> = {
  early: {
    label: "Early-stage",
    description: "Still collecting first-hour data",
    dotClass: "bg-sky-400",
    badgeClass: "bg-sky-500/20 text-sky-100 border border-sky-400/60",
  },
  mid: {
    label: "Mid-engagement",
    description: "Momentum building, watch for spikes",
    dotClass: "bg-emerald-400",
    badgeClass: "bg-emerald-500/20 text-emerald-100 border border-emerald-400/60",
  },
  declining: {
    label: "Declining",
    description: "Momentum tapering off",
    dotClass: "bg-primary/60",
    badgeClass: "bg-primary/15 text-foreground border border-primary/60",
  },
  evergreen: {
    label: "Evergreen candidate",
    description: "Consistent performer, consider repost strategy",
    dotClass: "bg-purple-400",
    badgeClass: "bg-purple-500/20 text-purple-100 border border-purple-400/60",
  },
};

const LIFECYCLE_STAGE_ORDER: PostLifecycleStage[] = ["early", "mid", "declining", "evergreen"];

function PostLifecycleDrawer({ task, open, onClose }: { task: MarketingTask | null; open: boolean; onClose: () => void }) {
  const [activeTab, setActiveTab] = useState("overview");
  useEffect(() => {
    setActiveTab("overview");
  }, [task?.id]);
  const { data, isLoading, error } = useSWR<PostLifecycleMetrics>(
    task ? `/marketing/posts/${task.id}/lifecycle` : null,
    fetcher,
  );
  const metrics = data ?? DEFAULT_POST_LIFECYCLE;
  const audienceSegments = metrics.audience?.segments ?? [];
  const audienceRegions = metrics.audience?.regions ?? [];
  const audienceDevices = metrics.audience?.devices ?? [];
  const stageMeta = LIFECYCLE_STAGE_META[metrics.stage];
  const retentionPoints = metrics.awareness.retention.length
    ? metrics.awareness.retention
    : DEFAULT_RETENTION_POINTS;
  const hasServerData = Boolean(data);

  return (
    <Sheet open={open} onOpenChange={(value) => (!value ? onClose() : undefined)}>
      <SheetContent className="flex w-full max-w-4xl flex-col gap-4 overflow-y-auto border-l border-border/70 bg-background/95">
        <SheetHeader>
          <SheetTitle className="text-2xl text-foreground">Post lifecycle</SheetTitle>
          <p className="text-sm text-muted-foreground">
            {task ? task.title : "Select a scheduled or published post to inspect lifecycle metrics."}
          </p>
        </SheetHeader>
        {!task ? (
          <div className="rounded-2xl border border-dashed border-border/70 p-6 text-center text-sm text-muted-foreground">
            Choose a scheduled or published post to view awareness, engagement, and conversion curves.
          </div>
        ) : isLoading && !data ? (
          <div className="flex flex-1 items-center justify-center py-16">
            <Spinner className="h-10 w-10 border-primary/60" />
          </div>
        ) : (
          <>
            {error ? (
              <div className="rounded-xl border border-rose-500/50 bg-rose-500/10 p-3 text-sm text-rose-200">
                Unable to load lifecycle metrics. Please try again shortly.
              </div>
            ) : null}
            {!hasServerData ? (
              <div className="rounded-xl border border-primary/40 bg-primary/10 p-3 text-xs text-foreground">
                Live analytics still warming up for this post. Showing placeholder insights until the API responds.
              </div>
            ) : null}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="audience">Audience</TabsTrigger>
                <TabsTrigger value="engagement">Engagement</TabsTrigger>
                <TabsTrigger value="conversions">Conversions</TabsTrigger>
              </TabsList>
              <TabsContent value="overview" className="space-y-4">
                <div className="grid gap-3 md:grid-cols-3">
                  <LifecycleMetric label="Impressions" value={metrics.awareness.impressions.toLocaleString()} />
                  <LifecycleMetric label="Reach" value={metrics.awareness.reach.toLocaleString()} />
                  <LifecycleMetric
                    label="Watch time"
                    value={`${(metrics.awareness.watchTime / 60).toFixed(1)} min`}
                    sublabel="Across all platforms"
                  />
                </div>
                <div className="grid gap-4 lg:grid-cols-[2fr_1fr]">
                  <div className="rounded-2xl border border-border/80 bg-background/40 p-4">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className="text-sm font-semibold text-foreground">Retention curve</p>
                      <Badge className="text-xs uppercase tracking-wide text-foreground">
                        {(task?.platforms ?? []).join(" • ") || "All"}
                      </Badge>
                    </div>
                    <div className="mt-4 flex h-36 items-end gap-2">
                      {retentionPoints.map((point) => (
                        <div key={point.label} className="flex-1 text-center">
                          <div
                            className="mx-auto w-full rounded-t-xl bg-emerald-400/70 transition"
                            style={{ height: `${Math.max(point.rate, 4)}%` }}
                          />
                          <p className="mt-1 text-[10px] text-muted-foreground">{point.label}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="rounded-2xl border border-border/80 bg-background/40 p-4">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-semibold text-foreground">Lifecycle stage</p>
                      <Badge className={cn("text-xs", stageMeta.badgeClass)}>{stageMeta.label}</Badge>
                    </div>
                    <p className="mt-2 text-sm text-muted-foreground">{stageMeta.description}</p>
                    <div className="mt-4 space-y-2 text-sm">
                      {LIFECYCLE_STAGE_ORDER.map((stage) => {
                        const meta = LIFECYCLE_STAGE_META[stage];
                        const active = stage === metrics.stage;
                        return (
                          <div
                            key={stage}
                            className={cn(
                              "flex items-center justify-between rounded-xl border px-3 py-2",
                              active ? "border-border/50 bg-background/5 text-foreground" : "border-border/80 bg-background/30 text-muted-foreground",
                            )}
                          >
                            <div className="flex items-center gap-2">
                              <span
                                className={cn(
                                  "h-2.5 w-2.5 rounded-full",
                                  active ? meta.dotClass : "bg-muted",
                                )}
                              />
                              {meta.label}
                            </div>
                            {active ? <span className="text-xs text-foreground/80">{meta.description}</span> : null}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </TabsContent>
              <TabsContent value="audience" className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="rounded-2xl border border-border/80 bg-background/40 p-4">
                    <p className="text-sm font-semibold text-foreground">Segments responding</p>
                    <div className="mt-3 space-y-2">
                      {audienceSegments.length ? (
                        audienceSegments.map((segment) => (
                          <div key={segment.label} className="flex items-center justify-between text-sm text-foreground/80">
                            <span>{segment.label}</span>
                            <span className="text-muted-foreground">{segment.value}%</span>
                          </div>
                        ))
                      ) : (
                        <p className="text-sm text-muted-foreground">No audience segments yet.</p>
                      )}
                    </div>
                  </div>
                  <div className="rounded-2xl border border-border/80 bg-background/40 p-4">
                    <p className="text-sm font-semibold text-foreground">Regions</p>
                    <div className="mt-3 space-y-2">
                      {audienceRegions.length ? (
                        audienceRegions.map((region) => (
                          <div key={region.label} className="flex items-center justify-between text-sm text-foreground/80">
                            <span>{region.label}</span>
                            <span className="text-muted-foreground">{region.value}%</span>
                          </div>
                        ))
                      ) : (
                        <p className="text-sm text-muted-foreground">No geo data yet.</p>
                      )}
                    </div>
                  </div>
                </div>
                <div className="rounded-2xl border border-border/80 bg-background/40 p-4">
                  <p className="text-sm font-semibold text-foreground">Devices</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {audienceDevices.length ? (
                      audienceDevices.map((device) => (
                        <Badge key={device.label} variant="outline" className="border-border text-foreground/80">
                          {device.label}: {device.value}%
                        </Badge>
                      ))
                    ) : (
                      <p className="text-sm text-muted-foreground">No device splits yet.</p>
                    )}
                  </div>
                </div>
              </TabsContent>
              <TabsContent value="engagement" className="space-y-4">
                <div className="grid gap-3 md:grid-cols-3">
                  <LifecycleMetric label="Likes" value={metrics.engagement.likes.toLocaleString()} />
                  <LifecycleMetric label="Comments" value={metrics.engagement.comments.toLocaleString()} />
                  <LifecycleMetric label="Saves" value={metrics.engagement.saves.toLocaleString()} />
                  <LifecycleMetric label="Shares" value={metrics.engagement.shares.toLocaleString()} />
                  <LifecycleMetric
                    label="Engagement rate"
                    value={`${metrics.engagement.engagementRate.toFixed(2)}%`}
                    sublabel="vs reach"
                  />
                  <LifecycleMetric
                    label="Velocity"
                    value={`${metrics.engagement.velocity.toFixed(1)}`}
                    sublabel="engagement/hr"
                  />
                </div>
              </TabsContent>
              <TabsContent value="conversions">
                <div className="grid gap-3 md:grid-cols-2">
                  <LifecycleMetric label="Link clicks" value={metrics.conversions.linkClicks.toLocaleString()} />
                  <LifecycleMetric label="Ticket sales" value={metrics.conversions.ticketSales.toLocaleString()} />
                  <LifecycleMetric label="Merch clicks" value={metrics.conversions.merchClicks.toLocaleString()} />
                  <LifecycleMetric label="Spotify opens" value={metrics.conversions.spotifyOpens.toLocaleString()} />
                </div>
              </TabsContent>
            </Tabs>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

function LifecycleMetric({ label, value, sublabel }: { label: string; value: string; sublabel?: string }) {
  return (
    <div className="rounded-2xl border border-border/80 bg-background/40 p-4">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-1 text-2xl font-semibold text-foreground">{value}</p>
      {sublabel ? <p className="text-xs text-muted-foreground">{sublabel}</p> : null}
    </div>
  );
}

function ActivitySkeleton({ count = 4 }: { count?: number }) {
  return (
    <div className="space-y-4">
      {Array.from({ length: count }).map((_, index) => (
        <div key={index} className="flex items-start gap-4">
          <div className="h-3 w-12 animate-pulse rounded bg-muted" />
          <div className="flex-1 space-y-2">
            <div className="h-4 w-1/2 animate-pulse rounded bg-muted" />
            <div className="h-3 w-3/4 animate-pulse rounded bg-background/40" />
          </div>
        </div>
      ))}
    </div>
  );
}

function NudgeList({ nudges, onApply }: { nudges: MarketingNudge[]; onApply: (nudge: MarketingNudge) => Promise<void> }) {
  return (
    <div className="space-y-3">
      {nudges.map((nudge) => {
        const meta = NUDGE_TYPE_META[nudge.type];
        const Icon = meta.icon;
        return (
          <div key={nudge.id} className="rounded-2xl border border-border/80 bg-background/40 p-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-1 items-center gap-3">
                <span className="rounded-full bg-background/40 p-2 text-foreground">
                  <Icon className="h-4 w-4" />
                </span>
                <div>
                  <p className="text-sm font-medium text-foreground">{meta.label}</p>
                  <p className="text-sm text-foreground/70">{nudge.message}</p>
                </div>
              </div>
              <Badge className={cn("capitalize", NUDGE_SEVERITY_STYLES[nudge.severity])}>{nudge.severity}</Badge>
            </div>
            {nudge.recommendedAction ? (
              <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-sm text-muted-foreground">
                <span>{nudge.recommendedAction}</span>
                <Button size="sm" onClick={() => void onApply(nudge)}>
                  Apply suggestion
                </Button>
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

const STYLE_PRESETS = ["clean", "posterized", "warm_winds", "psychedelic_fall"] as const;

function ClipEditorDrawer({
  clip,
  onClose,
  loadingCaption,
  loadingOverlay,
  loadingApprove,
  onApprove,
  onSaveOverlay,
  onGenerateCaption,
  onOpenComposer,
}: {
  clip: MarketingTask | null;
  onClose: () => void;
  loadingCaption: boolean;
  loadingOverlay: boolean;
  loadingApprove: boolean;
  onApprove: (clip: MarketingTask) => Promise<void>;
  onSaveOverlay: (clip: MarketingTask, overlay: ClipOverlayPayload) => Promise<void>;
  onGenerateCaption: (clip: MarketingTask) => Promise<void>;
  onOpenComposer: (options?: Partial<ComposerState>) => void;
}) {
  const [overlayState, setOverlayState] = useState<ClipOverlayPayload>({
    titleOverlay: "",
    ctaOverlay: "",
    vignette: false,
    timestamp: false,
    stylePreset: "clean",
  });

  useEffect(() => {
    if (clip) {
      setOverlayState({
        titleOverlay: clip.overlay?.titleOverlay ?? "",
        ctaOverlay: clip.overlay?.ctaOverlay ?? "",
        vignette: Boolean(clip.overlay?.vignette),
        timestamp: Boolean(clip.overlay?.timestamp),
        stylePreset: clip.overlay?.stylePreset ?? "clean",
      });
    }
  }, [clip]);

  const handleOverlaySave = async () => {
    if (!clip) return;
    await onSaveOverlay(clip, overlayState);
  };

  const handleApprove = async () => {
    if (!clip) return;
    await onApprove(clip);
  };

  const handleGenerateCaption = async () => {
    if (!clip) return;
    await onGenerateCaption(clip);
  };

  return (
    <>
      <Sheet open={Boolean(clip)} onOpenChange={(open) => (!open ? onClose() : undefined)}>
        <SheetContent className="flex w-full max-w-3xl flex-col gap-4 overflow-y-auto border-l border-border/70 bg-background/95">
          {clip ? (
            <>
              <SheetHeader>
                <SheetTitle>{clip.title ?? "Clip review"}</SheetTitle>
              </SheetHeader>
              <div className="space-y-6">
                <div className="space-y-2">
                  {clip.mediaUrl ? (
                    <video src={clip.mediaUrl} controls className="w-full rounded-2xl border border-border/70" />
                  ) : (
                    <div className="rounded-xl border border-dashed border-border/80 p-6 text-center text-sm text-muted-foreground">
                      Clip source unavailable.
                    </div>
                  )}
                  <div className="grid gap-3 rounded-xl border border-border/80 bg-background/20 p-4 md:grid-cols-3">
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">
                      Duration
                      <span className="mt-1 block text-base text-foreground">{formatDuration(clip.durationSeconds)}</span>
                    </p>
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">
                      Source
                      <span className="mt-1 block truncate text-base text-foreground">{clip.sourceFile ?? "N/A"}</span>
                    </p>
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">
                      Generated
                      <span className="mt-1 block text-base text-foreground">
                        {clip.createdAt ? formatDate(clip.createdAt, true) : "Unknown"}
                      </span>
                    </p>
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-foreground">Overlay tools</h3>
                  <div className="grid gap-4 md:grid-cols-2">
                    <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      Title overlay
                      <Input
                        value={overlayState.titleOverlay}
                        onChange={(event) =>
                          setOverlayState((prev) => ({ ...prev, titleOverlay: event.target.value }))
                        }
                        className="mt-1 bg-background/40 text-foreground"
                      />
                    </label>
                    <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      Lower-third CTA
                      <Input
                        value={overlayState.ctaOverlay}
                        onChange={(event) => setOverlayState((prev) => ({ ...prev, ctaOverlay: event.target.value }))}
                        className="mt-1 bg-background/40 text-foreground"
                      />
                    </label>
                  </div>
                  <div className="flex flex-wrap gap-4 text-sm text-foreground/70">
                    <label className="flex items-center gap-2">
                      <Checkbox
                        checked={overlayState.vignette}
                        onCheckedChange={(checked) =>
                          setOverlayState((prev) => ({ ...prev, vignette: Boolean(checked) }))
                        }
                      />
                      Add vignette effect
                    </label>
                    <label className="flex items-center gap-2">
                      <Checkbox
                        checked={overlayState.timestamp}
                        onCheckedChange={(checked) =>
                          setOverlayState((prev) => ({ ...prev, timestamp: Boolean(checked) }))
                        }
                      />
                      Add timestamp overlay
                    </label>
                  </div>
                  <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Style preset
                    <Select
                      value={overlayState.stylePreset}
                      onValueChange={(value) => setOverlayState((prev) => ({ ...prev, stylePreset: value }))}
                    >
                      <SelectTrigger className="mt-1 bg-background/40 text-foreground">
                        <SelectValue placeholder="Select preset" />
                      </SelectTrigger>
                      <SelectContent className="bg-background/40 text-foreground">
                        {STYLE_PRESETS.map((preset) => (
                          <SelectItem key={preset} value={preset}>
                            {preset.replace(/_/g, " ")}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </label>
                  <div className="flex flex-wrap gap-2">
                    <Button variant="outline" disabled={loadingOverlay} onClick={handleOverlaySave}>
                      {loadingOverlay ? "Saving…" : "Save Overlay"}
                    </Button>
                    <Button variant="secondary" disabled={loadingCaption} onClick={handleGenerateCaption}>
                      {loadingCaption ? "Generating…" : "Generate Caption"}
                    </Button>
                  </div>
                </div>

                <div className="space-y-3 rounded-2xl border border-border/80 bg-background/30 p-4">
                  <h3 className="text-lg font-semibold text-foreground">Actions</h3>
                  <div className="flex flex-wrap gap-2">
                    <Button onClick={handleApprove} disabled={loadingApprove}>
                      {loadingApprove ? "Approving…" : "Approve Clip"}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        if (!clip) return;
                        onOpenComposer({
                          initialAsset: {
                            id: clip.id,
                            type: "clip",
                            title: clip.title,
                            thumbnailUrl: clip.thumbnailUrl,
                            url: clip.mediaUrl,
                          },
                          initialDatetime: clip.scheduledFor ?? clip.dueAt ?? null,
                        });
                        onClose();
                      }}
                    >
                      Schedule as Post
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => clip.mediaUrl && window.open(clip.mediaUrl, "_blank", "noopener,noreferrer")}
                    >
                      Download Clip
                    </Button>
                  </div>
                </div>
              </div>
            </>
          ) : null}
        </SheetContent>
      </Sheet>
    </>
  );
}

type PostComposerDrawerProps = {
  open: boolean;
  onClose: () => void;
  initialTask?: MarketingTask | null;
  initialAsset?: ComposerAsset | null;
  initialDatetime?: string | null;
  defaultPlatforms: PlatformKey[];
  automationWindows: PostingWindow[];
  permissions: MarketingPermissions;
  onRequestUpgrade: (message: string) => void;
  onOpenLifecycle?: (task: MarketingTask) => void;
  abTestPosts: Array<{ id: string; title: string }>;
  onBoostWithMeta?: (payload: { caption: string; mediaUrl?: string | null }) => Promise<void>;
};

function AnalyticsTile({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-border/80 bg-background/40 p-4">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-2 text-lg font-semibold text-foreground">{value}</p>
    </div>
  );
}

function TodaySection({ title, count, children }: { title: string; count?: number; children: ReactNode }) {
  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-base font-semibold text-foreground">{title}</p>
          <p className="text-xs uppercase tracking-wide text-muted-foreground">Live updates</p>
        </div>
        {typeof count === "number" ? (
          <Badge variant="secondary" className="bg-background/40 text-foreground/80">
            {count}
          </Badge>
        ) : null}
      </div>
      {children}
    </section>
  );
}

function TaskCard({ task, actions }: { task: MarketingTask; actions?: ReactNode }) {
  const typeLabel = TASK_TYPE_LABELS[task.type] ?? task.type;
  const priorityClass = PRIORITY_STYLES[task.priority];

  return (
    <Card className="border-border/80 bg-background/30">
      <div className="space-y-3 p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            <Badge variant="secondary" className="bg-muted text-foreground/80">
              {typeLabel}
            </Badge>
            {task.platforms?.map((platform) => (
              <Badge key={platform} variant="outline" className="border-border/80 text-foreground/70">
                {PLATFORM_LABELS[platform]}
              </Badge>
            ))}
            {task.campaignLabel ? (
              <Badge variant="outline" className="border-primary/60 text-foreground">
                {task.campaignLabel}
              </Badge>
            ) : null}
            {task.eventLabel ? (
              <Badge variant="outline" className="border-sky-400/60 text-sky-200">
                {task.eventLabel}
              </Badge>
            ) : null}
          </div>
          <span className={cn("rounded-full px-3 py-1 text-xs font-semibold capitalize", priorityClass)}>{task.priority}</span>
        </div>

        <div className="space-y-1">
          <p className="text-base font-semibold text-foreground">{task.title}</p>
          {task.description ? <p className="text-sm text-muted-foreground">{task.description}</p> : null}
        </div>

        <div className="text-xs text-muted-foreground">
          {task.dueAt ? <p>Due {formatDate(task.dueAt)}</p> : null}
          {task.scheduledFor ? <p>Scheduled {formatDate(task.scheduledFor, true)}</p> : null}
          <p className="capitalize">Status: {task.status.replace(/_/g, " ")}</p>
        </div>

        {actions ? <div className="flex flex-wrap gap-2">{actions}</div> : null}
      </div>
    </Card>
  );
}

function LegendDot({ className, label }: { className?: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={cn("h-2.5 w-2.5 rounded-full", className)} />
      {label}
    </span>
  );
}

const TASK_TYPE_LABELS: Record<MarketingTask["type"], string> = {
  event_promo: "Event Promo",
  post: "Post",
  clip: "Clip",
  caption: "Caption",
  campaign: "Campaign",
  system: "System",
};

const PLATFORM_LABELS = {
  instagram: "Instagram",
  tiktok: "TikTok",
  facebook: "Facebook",
  youtube: "YouTube",
  youtube_shorts: "YouTube Shorts",
} as const;

const PLATFORM_ABBREVIATIONS: Record<NonNullable<MarketingTask["platforms"]>[number], string> = {
  instagram: "IG",
  tiktok: "TT",
  facebook: "FB",
  youtube: "YT",
  youtube_shorts: "YS",
};

const PRIORITY_STYLES: Record<MarketingTask["priority"], string> = {
  low: "bg-muted text-foreground/80",
  medium: "bg-blue-900/60 text-blue-100",
  high: "border border-primary/60 bg-primary/15 text-foreground",
  critical: "border border-rose-400/80 bg-rose-500/20 text-rose-200",
};

const STATUS_PILL_STYLES: Record<MarketingTaskStatus, string> = {
  todo: "bg-muted text-foreground/80",
  in_progress: "bg-sky-500/20 text-sky-100",
  ready: "bg-indigo-500/20 text-indigo-100",
  scheduled: "bg-emerald-500/20 text-emerald-100",
  done: "bg-muted text-foreground/80",
  error: "bg-rose-500/20 text-rose-200",
};

const NUDGE_TYPE_META: Record<MarketingNudge["type"], { icon: LucideIcon; label: string }> = {
  engagement: { icon: BarChart3, label: "Engagement" },
  timing: { icon: Target, label: "Timing" },
  platform: { icon: Server, label: "Platform" },
  content_gap: { icon: Lightbulb, label: "Content gap" },
  campaign: { icon: Megaphone, label: "Campaign" },
};

const NUDGE_SEVERITY_STYLES: Record<MarketingNudge["severity"], string> = {
  low: "bg-muted text-foreground/80",
  medium: "bg-primary/15 text-foreground border border-primary/50",
  high: "bg-rose-500/20 text-rose-200 border border-rose-400/70",
};

function resetToStartOfDay(date: Date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

function getSlotFromDate(date: Date): "morning" | "afternoon" | "evening" {
  const hour = date.getHours();
  if (hour < 12) return "morning";
  if (hour < 17) return "afternoon";
  return "evening";
}

function formatDate(iso: string, includeTime = false) {
  const date = new Date(iso);
  if (includeTime) {
    return date.toLocaleString(undefined, {
      weekday: "short",
      hour: "numeric",
      minute: "2-digit",
    });
  }

  return date.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

function formatTime(iso: string) {
  return new Date(iso).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

function getStartOfWeek(date: Date) {
  const start = new Date(date);
  start.setHours(0, 0, 0, 0);
  const day = start.getDay();
  start.setDate(start.getDate() - day);
  return start;
}

function isWithinRange(date: Date, start: Date, end: Date) {
  return date >= start && date < end;
}

function formatDayKey(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function getTimelineRange(tasks: MarketingTask[]) {
  const defaultStart = resetToStartOfDay(new Date());
  const defaultEnd = new Date(defaultStart);
  defaultEnd.setDate(defaultEnd.getDate() + 7);

  const dates = tasks
    .map((task) => task.scheduledFor ?? task.dueAt)
    .filter(Boolean)
    .map((iso) => new Date(iso as string));

  if (!dates.length) {
    return { start: defaultStart, end: defaultEnd };
  }

  const start = new Date(Math.min(...dates.map((date) => date.getTime())));
  const end = new Date(Math.max(...dates.map((date) => date.getTime())));
  end.setDate(end.getDate() + 1);

  return { start, end };
}

function getRelativePosition(task: MarketingTask, start: Date, end: Date) {
  const referenceDate = task.scheduledFor ?? task.dueAt;
  if (!referenceDate) return 5;
  const total = end.getTime() - start.getTime();
  if (total <= 0) return 5;
  const distance = new Date(referenceDate).getTime() - start.getTime();
  return Math.min(95, Math.max(5, (distance / total) * 100));
}

function formatDuration(seconds?: number) {
  if (!seconds) return "0:00";
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60)
    .toString()
    .padStart(2, "0");
  return `${mins}:${secs}`;
}

function formatShortDate(iso: string) {
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function CampaignDetailDrawer({
  campaignId,
  onClose,
  mutateCampaigns,
  pushToast,
  nudges,
  onApplyNudge,
  onOpenComposer,
  permissions,
  onRequestUpgrade,
}: {
  campaignId: string | null;
  onClose: () => void;
  mutateCampaigns: KeyedMutator<MarketingCampaign[]>;
  pushToast: ReturnType<typeof useToast>["push"];
  nudges: MarketingNudge[];
  onApplyNudge: (nudge: MarketingNudge) => Promise<void>;
  onOpenComposer: (options?: Partial<ComposerState>) => void;
  permissions: MarketingPermissions;
  onRequestUpgrade: (message: string) => void;
}) {
  const { data: campaign, mutate: mutateCampaign } = useSWR<MarketingCampaign>(
    campaignId ? `/marketing/campaigns/${campaignId}` : null,
    fetcher,
  );
  const { data: timeline } = useSWR<MarketingTask[]>(campaignId ? `/marketing/campaigns/${campaignId}/timeline` : null, fetcher);
  const [goals, setGoals] = useState({
    awareness: "",
    ticketSales: "",
    presaves: "",
    merch: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (campaign) {
      setGoals({ ...campaign.goals });
    }
  }, [campaign]);

  const handleSaveGoals = async () => {
    if (!campaignId) return;
    if (!permissions.canManageCampaigns) {
      onRequestUpgrade("Campaign management requires manager permissions.");
      return;
    }
    setSaving(true);
    try {
      await post(`/marketing/campaigns/${campaignId}/update`, { goals });
      await mutateCampaign();
      await mutateCampaigns();
      pushToast({ title: "Campaign updated", message: "Goals saved successfully." });
    } catch (error) {
      pushToast({
        title: "Unable to save goals",
        message: error instanceof Error ? error.message : "Failed to update campaign goals.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const sortedTimeline = useMemo(() => {
    if (!timeline) return [];
    return [...timeline].sort((a, b) => {
      const aDate = new Date(a.scheduledFor ?? a.dueAt ?? "");
      const bDate = new Date(b.scheduledFor ?? b.dueAt ?? "");
      return aDate.getTime() - bDate.getTime();
    });
  }, [timeline]);

  const campaignNudges = useMemo(
    () => (campaign ? nudges.filter((nudge) => nudge.campaignLabel === campaign.name) : []),
    [campaign, nudges],
  );

  return (
    <Sheet open={Boolean(campaignId)} onOpenChange={(open) => (!open ? onClose() : undefined)}>
      <SheetContent className="flex w-full max-w-4xl flex-col gap-6 overflow-y-auto border-l border-border/70 bg-background/95">
        {!campaign ? (
          <div className="p-6 text-sm text-muted-foreground">Loading campaign…</div>
        ) : (
          <>
            <SheetHeader>
              <SheetTitle className="text-2xl text-foreground">{campaign.name}</SheetTitle>
              <p className="text-sm text-muted-foreground">
                {formatShortDate(campaign.startDate)} – {formatShortDate(campaign.endDate)}
              </p>
              <Badge className="w-fit capitalize">{campaign.status}</Badge>
            </SheetHeader>

            <section className="space-y-4 rounded-2xl border border-border/80 bg-background/30 p-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-foreground">Goals</h3>
                <Button size="sm" onClick={handleSaveGoals} disabled={saving || !permissions.canManageCampaigns} aria-disabled={!permissions.canManageCampaigns}>
                  {saving ? "Saving…" : permissions.canManageCampaigns ? "Save goals" : "Requires manager access"}
                </Button>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                {(["awareness", "ticketSales", "presaves", "merch"] as const).map((field) => (
                  <label key={field} className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {field.replace(/([A-Z])/g, " $1")}
                    <Input
                      className="mt-1 bg-background/40 text-foreground"
                      value={goals[field]}
                      onChange={(event) => setGoals((prev) => ({ ...prev, [field]: event.target.value }))}
                    />
                  </label>
                ))}
              </div>
            </section>

            <section className="space-y-3 rounded-2xl border border-border/80 bg-background/30 p-4">
              <h3 className="text-lg font-semibold text-foreground">Campaign timeline</h3>
              <div className="space-y-3">
                {sortedTimeline.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No scheduled items found.</p>
                ) : (
                  sortedTimeline.map((item) => (
                    <div key={item.id} className="rounded-xl border border-border/70 bg-background/40 p-3">
                      <div className="flex flex-wrap items-center justify-between">
                        <div>
                          <p className="text-sm font-semibold text-foreground">{item.title}</p>
                          <p className="text-xs text-muted-foreground">
                            {item.scheduledFor
                              ? formatDate(item.scheduledFor, true)
                              : item.dueAt
                                ? formatDate(item.dueAt, true)
                                : "No date"}
                          </p>
                        </div>
                        <Badge className="capitalize">{item.status.replace(/_/g, " ")}</Badge>
                      </div>
                      <div className="mt-2 flex flex-wrap gap-2 text-xs text-muted-foreground">
                        {(item.platforms ?? []).map((platform) => (
                          <Badge key={platform} variant="outline" className="border-border text-foreground/80">
                            {platformLabels[platform as PlatformKey]}
                          </Badge>
                        ))}
                      </div>
                      <div className="mt-2 flex flex-wrap gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() =>
                            onOpenComposer({
                              initialTask: item,
                              initialDatetime: item.scheduledFor ?? item.dueAt ?? undefined,
                            })
                          }
                        >
                          Open in composer
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </section>

            <section className="space-y-3 rounded-2xl border border-border/80 bg-background/30 p-4">
              <h3 className="text-lg font-semibold text-foreground">Content plan</h3>
              <div className="space-y-3">
                {campaign.contentPlan.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No posts planned yet.</p>
                ) : (
                  campaign.contentPlan.map((plan) => (
                    <div key={plan.id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border/70 bg-background/40 p-3">
                      <div>
                        <p className="text-sm font-semibold text-foreground">{plan.title}</p>
                        <div className="mt-1 flex flex-wrap gap-2 text-xs text-muted-foreground">
                          {plan.platforms.map((platform) => (
                            <Badge key={platform} variant="outline" className="border-border text-foreground/80">
                              {platformLabels[platform]}
                            </Badge>
                          ))}
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge className="capitalize">{plan.status}</Badge>
                        <Button size="sm" variant="secondary" onClick={() => onOpenComposer({ initialDatetime: campaign.startDate })}>
                          Edit
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => onOpenComposer({ initialDatetime: campaign.startDate })}>
                          Generate
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => onOpenComposer({ initialDatetime: campaign.startDate })}>
                          Schedule
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </section>

            <section className="space-y-3 rounded-2xl border border-border/80 bg-background/30 p-4">
              <h3 className="text-lg font-semibold text-foreground">Analytics summary</h3>
              <div className="grid gap-4 md:grid-cols-4">
                <AnalyticsTile label="Reach" value={(campaign.analytics?.reach ?? 0).toLocaleString()} />
                <AnalyticsTile label="Engagement" value={(campaign.analytics?.engagement ?? 0).toLocaleString()} />
                <AnalyticsTile label="CTR" value={`${campaign.analytics?.ctr ?? 0}%`} />
                <AnalyticsTile label="Top post" value={campaign.analytics?.bestPost ?? "TBD"} />
              </div>
            </section>

            <section className="space-y-3 rounded-2xl border border-border/80 bg-background/30 p-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-foreground">Campaign insights</h3>
                <Badge variant="outline" className="border-border text-foreground/80">
                  {campaignNudges.length}
                </Badge>
              </div>
              {campaignNudges.length === 0 ? (
                <p className="text-sm text-muted-foreground">No nudges for this campaign.</p>
              ) : (
                <NudgeList nudges={campaignNudges} onApply={onApplyNudge} />
              )}
            </section>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
function ExperimentCreatorDrawer({
  open,
  onClose,
  onCreate,
}: {
  open: boolean;
  onClose: () => void;
  onCreate: (payload: { name: string; metric: Experiment["metric"]; variants: ExperimentDraftVariant[] }) => Promise<void>;
}) {
  const [name, setName] = useState("");
  const [metric, setMetric] = useState<Experiment["metric"]>("engagement");
  const [variants, setVariants] = useState<ExperimentDraftVariant[]>([
    { caption: "", scheduledFor: new Date().toISOString() },
    { caption: "", scheduledFor: new Date().toISOString() },
  ]);
  const [submitting, setSubmitting] = useState(false);

  const handleVariantChange = (index: number, key: keyof ExperimentDraftVariant, value: string) => {
    setVariants((prev) =>
      prev.map((variant, idx) => (idx === index ? { ...variant, [key]: value } : variant)),
    );
  };

  const handleAddVariant = () => {
    if (variants.length >= 4) return;
    setVariants((prev) => [...prev, { caption: "", scheduledFor: new Date().toISOString() }]);
  };

  const handleRemoveVariant = (index: number) => {
    setVariants((prev) => prev.filter((_, idx) => idx !== index));
  };

  const handleSubmit = async () => {
    if (!name.trim()) return;
    setSubmitting(true);
    await onCreate({ name, metric, variants });
    setSubmitting(false);
    setName("");
    setVariants([
      { caption: "", scheduledFor: new Date().toISOString() },
      { caption: "", scheduledFor: new Date().toISOString() },
    ]);
  };

  return (
    <Sheet open={open} onOpenChange={(isOpen) => (!isOpen ? onClose() : undefined)}>
      <SheetContent className="flex w-full max-w-3xl flex-col gap-5 overflow-y-auto border-l border-border/70 bg-background/95">
        <SheetHeader>
          <SheetTitle className="text-2xl text-foreground">Start new experiment</SheetTitle>
          <p className="text-sm text-muted-foreground">Test captions, thumbnails, or timing variants.</p>
        </SheetHeader>
        <div className="space-y-4">
          <label className="text-xs uppercase tracking-wide text-muted-foreground">
            Name
            <Input className="mt-1 bg-background/40 text-foreground" value={name} onChange={(event) => setName(event.target.value)} />
          </label>
          <label className="text-xs uppercase tracking-wide text-muted-foreground">
            Metric
            <Select value={metric} onValueChange={(value) => setMetric(value as Experiment["metric"])}>
              <SelectTrigger className="mt-1 bg-background/40 text-foreground">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-background/40 text-foreground">
                <SelectItem value="engagement">Engagement</SelectItem>
                <SelectItem value="views">Views</SelectItem>
                <SelectItem value="clicks">Clicks</SelectItem>
                <SelectItem value="retention">Retention</SelectItem>
              </SelectContent>
            </Select>
          </label>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-foreground">Variants</p>
              <Button variant="outline" size="sm" onClick={handleAddVariant} disabled={variants.length >= 4}>
                Add variant
              </Button>
            </div>
            {variants.map((variant, index) => (
              <div key={index} className="rounded-xl border border-border/70 bg-background/40 p-3 space-y-2">
                <div className="flex justify-between">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Variant {index + 1}</p>
                  {variants.length > 2 ? (
                    <Button size="sm" variant="ghost" onClick={() => handleRemoveVariant(index)}>
                      Remove
                    </Button>
                  ) : null}
                </div>
                <Textarea
                  rows={3}
                  className="bg-background/40 text-foreground"
                  placeholder="Caption or notes"
                  value={variant.caption}
                  onChange={(event) => handleVariantChange(index, "caption", event.target.value)}
                />
                <label className="text-xs uppercase tracking-wide text-muted-foreground">
                  Scheduled for
                  <Input
                    type="datetime-local"
                    className="mt-1 bg-background/40 text-foreground"
                    value={formatDateTimeInput(variant.scheduledFor)}
                    onChange={(event) => handleVariantChange(index, "scheduledFor", event.target.value)}
                  />
                </label>
              </div>
            ))}
          </div>
        </div>
        <div className="flex justify-end gap-2 border-t border-border/70 pt-4">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => void handleSubmit()} disabled={submitting || !name.trim()}>
            {submitting ? "Launching…" : "Launch"}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
const notificationsFetcher = (url: string) =>
  fetch(url, { credentials: "include" }).then((response) => response.json());

const normalizeNotification = (item: UnifiedNotification): NotificationItem => {
  const payload = (item.payload ?? {}) as Record<string, unknown>;
  const createdAt = item.created_at ?? item.createdAt ?? new Date().toISOString();
  return {
    id: String(item.id),
    createdAt,
    type: (item.type as NotificationType) ?? "system",
    severity: (item.severity as NotificationSeverity) ?? "info",
    title: item.title ?? "Notification",
    message: item.message ?? "",
    relatedTaskId: (payload.task_id ?? payload.taskId ?? undefined) as string | undefined,
    relatedCampaignLabel: (payload.campaign_label ?? payload.campaignLabel ?? undefined) as string | undefined,
    source: (item.source as NotificationSource) ?? "marketing",
    payload,
    read: Boolean(item.read),
  };
};
